// help/helpContent.js — the Help catalog + rules engine.
//
// ONE catalog models everything: dynamic "Did you know?" tips, static guides, and
// "What's new" announcements. A catalog entry becomes a *suggestion* only when its
// `signal.when(signals)` predicate is true AND the admin holds the required
// permissions AND it hasn't been dismissed/snoozed. Entries with no `signal` are
// always-browsable static guides. This is the unifying model that lets dynamic
// discovery and the static help library live side by side — adding a tip is one
// object appended here.
//
// The per-tenant `signals` come from useHelpSignals(); the per-admin dismiss/snooze
// `state` comes from useHelpState(). Both are injected so this module stays pure and
// unit-testable (no Firestore, no React).

import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import FireTruckIcon from '@mui/icons-material/FireTruck';
import ConstructionIcon from '@mui/icons-material/Construction';
import ChecklistIcon from '@mui/icons-material/Checklist';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import SchoolIcon from '@mui/icons-material/School';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ForestIcon from '@mui/icons-material/Forest';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ApartmentIcon from '@mui/icons-material/Apartment';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import FolderIcon from '@mui/icons-material/Folder';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SettingsIcon from '@mui/icons-material/Settings';
import GroupsIcon from '@mui/icons-material/Groups';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

// Per-entry icons. A catalog entry may override its module's icon when the module
// icon is too generic for the task — "Getting Started" is a rocket, which says
// nothing about adding a person.
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';

// The Help page and global search publish the same sections.
export const VISIBLE_MODULES = ['apparatus', 'gear', 'incidents', 'training', 'qualifications', 'lessonplans', 'members'];

// Section groups. The "All guides" filter would otherwise be a wall of one chip
// per module, so modules roll up into the same handful of groups the sidebar
// uses — a reader who knows where a feature lives in the nav looks in the same
// place here. `start` has no sidebar equivalent; it's the onboarding path.
export const MODULE_GROUPS = [
    {id: 'start', label: 'Getting Started'},
    {id: 'operations', label: 'Operations'},
    {id: 'training', label: 'Training'},
    {id: 'scheduling', label: 'Scheduling'},
    {id: 'resources', label: 'Resources'},
    {id: 'administration', label: 'Administration'}
];

// Modules = top-level categories, mirroring the product nav (icons match useMenuItems).
export const MODULES = {
    'getting-started': {
        label: 'Getting Started',
        group: 'start',
        icon: RocketLaunchIcon,
        description: 'Set a department up from an empty system: users, permissions, apparatus and the checks your crews run.'
    },
    apparatus: {
        label: 'Apparatus',
        group: 'operations',
        icon: FireTruckIcon,
        description: 'Keep a record of every vehicle in your fleet, so checks, gear assignments and fuel logs all attach to the right unit.'
    },
    gear: {
        label: 'Gear',
        group: 'resources',
        icon: ConstructionIcon,
        description: 'Track whatever your department carries — PPE, SCBA, ladders, hose, consumables — on its own terms, wherever it lives.'
    },
    members: {
        label: 'Users',
        group: 'administration',
        icon: GroupsIcon,
        description: 'Manage user profiles, keep service history and supporting records, and review each member’s incidents, training and deployments.'
    },
    incidents: {label: 'Incidents', group: 'operations', icon: WhatshotIcon, description: 'Log calls with times, attendance and outcomes, then lock the report.'},
    tasks: {label: 'Tasks', group: 'scheduling', icon: ChecklistIcon, description: 'Work assigned to a user, whether you created it or a check, schedule or expiry did.'},
    fuel: {label: 'Fuel', group: 'resources', icon: LocalGasStationIcon, description: 'On-site fuel: tanks, fill-ups, deliveries and levels.'},
    training: {label: 'Training Records', group: 'training', icon: SchoolIcon, description: 'Add sessions, record attendance and competencies, then verify the training in the Training Matrix and explore the linked JPRs.'},
    ohs: {label: 'OH&S Meetings', group: 'training', icon: HealthAndSafetyIcon, description: 'Record each month’s health and safety meeting — attendance, notes, hazards, action items and minutes — and get reminded until it’s done.'},
    qualifications: {
        label: 'Qualification Matrix',
        group: 'training',
        icon: VerifiedIcon,
        description: 'Record member qualifications, attach certificates, track expiry dates and review progress across the department.'
    },
    lessonplans: {
        label: 'Lesson Plans',
        group: 'training',
        icon: MenuBookIcon,
        description: 'Prepare reusable lessons, import existing plans, and carry their competencies and JPRs into training records.'
    },
    // Requirements and Assignments are the two ends of one feature, split because
    // they belong to different people: an officer builds and hands out a
    // requirement, and every member works through the assignment it creates. The
    // permissions say the same thing — a firefighter holds no requirements.*
    // claim by default, yet still has a My Assignments page.
    requirements: {
        label: 'Requirements',
        group: 'training',
        icon: LocalLibraryIcon,
        description: 'Build something members have to complete — an SOP to sign, a video to watch, a quiz to pass — then hand it out and track who has.'
    },
    assignments: {
        label: 'Assignments',
        group: 'training',
        icon: AssignmentTurnedInIcon,
        description: 'The member’s side of a requirement: what you’ve been given, what’s due, and what happens when you finish it.'
    },
    reports: {
        label: 'Reports',
        group: 'operations',
        icon: AssessmentIcon,
        description: 'Standard reports built from what you already record — for officers, councils and regional boards.'
    },
    deployments: {
        label: 'Deployments',
        group: 'operations',
        icon: ForestIcon,
        description: 'Wildland and mutual-aid deployments: crews, rotations, the daily paperwork, and the package you send for reimbursement.'
    },
    calendar: {
        label: 'Calendar',
        group: 'scheduling',
        icon: CalendarMonthIcon,
        description: 'Everything scheduled in one month view — training, calls, duty shifts and department events.'
    },
    dutyshifts: {
        label: 'Duty Shifts',
        group: 'scheduling',
        icon: EventAvailableIcon,
        description: 'Who is on duty, on standby, or unavailable — and the hours that follow from it.'
    },
    preplans: {
        label: 'Preplans',
        group: 'resources',
        icon: ApartmentIcon,
        description: 'What a crew needs to know about a building before they arrive at it.'
    },
    adminhours: {
        label: 'Admin Hours',
        group: 'scheduling',
        icon: MoreTimeIcon,
        description: 'Time spent on department work that isn’t a call, a practice or a shift — logged by the member, approved by an officer.'
    },
    files: {
        label: 'Files',
        group: 'administration',
        icon: FolderIcon,
        description: 'The department’s document library: SOPs, forms and anything else the crew needs to find.'
    },
    payroll: {
        label: 'Payroll',
        group: 'scheduling',
        icon: AccountBalanceIcon,
        description: 'Roll a period’s hours up from what was already recorded, review them, and hand the numbers on for payment.'
    },
    // Per-feature configuration is documented in that feature's own section —
    // fuel alerting under Fuel, shift presets under Duty Shifts, and so on — so
    // somebody reading about a feature finds how to set it up in the same place.
    // This section is only for the settings no single feature owns.
    account: {
        label: 'Your Account',
        group: 'administration',
        icon: AccountCircleIcon,
        description: 'Your own profile: the details you keep current, how the app talks to you, and the record of what you’ve done.'
    },
    settings: {
        label: 'Settings',
        group: 'administration',
        icon: SettingsIcon,
        description: 'The department-wide setup: identity and branding, stations, who can sign in, and which parts of the app are switched on.'
    }
};

// The catalog. Relevance predicate + content + CTA all live together, so there's a
// single place to reason about a suggestion.
export const CATALOG = [
    /* ===== Dynamic discovery tips (have a `signal`) ===== */
    {
        id: 'gear-into-apparatus-check',
        kind: 'tip',
        hidden: true,
        category: 'apparatus',
        docType: 'how-to',
        title: 'Check a truck and its gear in one walkthrough',
        summary: 'Gear assigned to an apparatus can be joined into that truck’s check — so SCBA bottles, hose, and tools appear inline while a crew runs the weekly check. One pass instead of two.',
        how: [
            'Assign a gear category’s items to an apparatus (e.g. SCBA bottles → E51).',
            'Open the apparatus check and add the gear category as a section.',
            'Each assigned item’s own checks now appear as subsections of the truck check.',
            'Running the weekly check covers the truck and its gear together.'
        ],
        cta: {label: 'Open Apparatus', to: '/apparatus'},
        signal: {
            requires: ['editChecks.write', 'gear.write'],
            when: s => s.flags.gear && s.counts.gearItemsAssignedToApparatus > 0 && s.counts.templatesJoiningGear === 0,
            priority: 95
        }
    },
    {
        id: 'check-item-to-task',
        kind: 'tip',
        category: 'apparatus',
        docType: 'how-to',
        alsoIn: ['tasks'],
        title: 'Failed check items',
        summary: 'A problem is flagged by adding a note to the item during the check. The item then carries its own thread — what was found, any replies, and the ability to open a task against it. The note appears on every check of that apparatus until it’s resolved.',
        sections: [
            {
                title: 'Flagging an item',
                body: 'Adding a note is what flags an item. Anyone running the check can leave one, with photos, and the item is marked with a warning triangle from then on until it is resolved.'
            },
            {
                title: 'Who can reply and assign',
                body: 'Replying to a flagged item and opening a task from it are review actions. They require permission to approve apparatus checks (or gear checks, for a gear item) and appear only while the check is awaiting review.',
                images: [
                    {
                        src: '/help/check-item-reply.webp',
                        alt: 'A flagged item under review: a warning triangle beside the item name, the crew member’s note and who reported it, a Reply box, and an Add Task button.',
                        caption: 'Under review, the flagged item shows who reported it and what they found, with a **Reply** box and **Add Task** beside it. **Show only items with notes** narrows the check to just the flagged ones.'
                    }
                ]
            }
        ],
        images: [
            {
                src: '/help/check-add-task.webp',
                alt: 'A failed check item with the crew’s note and an Add Task button on the right of the row.'
            }
        ],
        cta: {label: 'View Apparatus', to: '/apparatus'},
        signal: {
            requires: ['tasks.write'],
            when: s => s.flags.tasks && s.counts.checksRun > 0 && s.counts.tasksFromChecks === 0,
            priority: 82
        }
    },
    {
        id: 'sealable-sections',
        kind: 'tip',
        category: 'apparatus',
        docType: 'how-to',
        // Follows "Creating a new check": making a section sealable is part of
        // setting the check up, so it reads next.
        orderIn: {apparatus: -2},
        title: 'Creating a sealable section',
        summary: 'Some compartments stay sealed until they’re used — a first aid kit with a numbered tag, a wildland forestry bag, etc. A sealed section reflects that in the check: the section stays locked for the seal duration, so the check confirms the seal rather than every item behind it.',
        sections: [
            {
                title: 'Setting it up on the check',
                body: 'Editing the check, mark the section **Sealable**. The seal itself is applied when a crew runs it.',
                images: [
                    {
                        src: '/help/seal-template.webp',
                        alt: 'A section in the check editor with its items listed and the Sealable checkbox ticked below them.'
                    }
                ]
            },
            {
                title: 'Sealing a section',
                steps: [
                    'Complete every item in the section.',
                    'Enter a **Seal Expiry** date and any **Notes**.',
                    'Select **Sealed**.'
                ],
                images: [
                    {
                        src: '/help/seal-apply.webp',
                        alt: 'A section during a check with every item completed and struck through, a Seal Expiry date field, a Notes field, and a Sealed checkbox.'
                    }
                ]
            },
            {
                title: 'Checking a sealed section in future checks',
                body: 'A sealed section shows who sealed it and when the seal expires, with its items collapsed under **Show items**.',
                bullets: [
                    'Select **Mark seal intact** if the seal is unbroken.',
                    'Select **Break seal** if the compartment has been opened or used. The items reopen, and the section is sealed again with a new **Seal Expiry** date.'
                ],
                images: [
                    {
                        src: '/help/seal-check.webp',
                        alt: 'A sealed section collapsed to one row: who sealed it and when it expires, a Show items toggle, and Break seal and Mark seal intact buttons.'
                    }
                ],
                footer: 'A seal also expires on its date, and the section reopens for full inspection at the next check.'
            }
        ],
        cta: {label: 'Edit a check', to: '/apparatus'},
        signal: {
            requires: ['editChecks.write'],
            when: s => s.counts.checkTemplates > 0 && s.counts.sealableSections === 0,
            priority: 74
        }
    },
    {
        id: 'recurring-gear-checks',
        kind: 'tip',
        category: 'gear',
        docType: 'how-to',
        title: 'Let gear checks schedule themselves',
        summary: 'Give a gear category check frequencies — inspect monthly, hydro-test yearly — and we generate the tasks when they come due. No spreadsheet, no missed certifications.',
        how: [
            'Give each of a category’s check types a frequency, on **Edit category**.',
            'Tasks generate themselves as checks come due, and overdue items surface on the dashboard and in Tasks.'
        ],
        cta: {label: 'Open Gear', to: '/gear'},
        signal: {
            requires: ['gear.write'],
            when: s => s.flags.gear && s.counts.gearItems > 0 && s.counts.gearCategoriesWithFrequencies === 0,
            priority: 68
        }
    },
    {
        id: 'fuel-setup',
        kind: 'tip',
        category: 'fuel',
        docType: 'how-to',
        title: 'Track your on-site fuel before the tank runs low',
        summary: 'Fuel tracking is switched on for your department but no tanks are set up yet. Add a tank to log fill-ups, record deliveries, and watch levels — so a low tank is never a surprise.',
        how: [
            'Add a fuel tank with its capacity and location.',
            'Log fill-ups as apparatus draw from it.',
            'Record deliveries to top the tank back up.',
            'Level gauges show remaining fuel at a glance.'
        ],
        cta: {label: 'Set up a fuel tank', to: '/fuel'},
        signal: {
            requires: ['fuel.write'],
            when: s => s.flags.fuel && s.counts.fuelTanks === 0,
            priority: 60
        }
    },

    /* ===== "What's new" announcement (kind: announcement) =====
       Announcements target tenants who haven't adopted a new feature yet, so this
       one only surfaces while Fuel is still switched off — and its CTA sends the
       admin to Settings to turn it on, not to a route that doesn't exist yet. */
    {
        id: 'announce-fuel',
        kind: 'announcement',
        category: 'fuel',
        title: 'New: Fuel tank tracking',
        summary: 'Track on-site fuel inventory — tanks, fill-ups, deliveries, and level gauges. It’s switched off by default; enable it under Settings › Features to get started.',
        cta: {label: 'Enable in Settings', to: '/settings/features'},
        when: s => !s.flags.fuel
    },

    /* ===== Static guides (no signal — always in the library) ===== */
    {
        id: 'guide-weekly-check',
        kind: 'guide',
        hidden: true,
        category: 'apparatus',
        docType: 'how-to',
        title: 'Run a weekly apparatus check',
        summary: 'Start a check, work through the sections, add notes and photos, and complete it for the record.',
        how: [
            'Open an apparatus and choose Start Check.',
            'Pick the check type (Weekly, Pre-Trip, Full).',
            'Work through each section; flag anything failed.',
            'Add notes and complete — it’s saved to History.'
        ],
        cta: {label: 'Go to Apparatus', to: '/apparatus'}
    },
    {
        id: 'guide-build-template',
        kind: 'guide',
        category: 'apparatus',
        docType: 'how-to',
        // Straight after "Adding apparatus": a new truck has no checks until it has
        // a check set up, so this is the next thing to do rather than the sixth.
        orderIn: {apparatus: -3},
        title: 'Creating a new check',
        summary: 'Define the sections and items once; crews work through the same list every time they check that apparatus.',
        taskLabel: 'To create a check',
        how: [
            'In the sidebar, go to **Apparatus** and open the apparatus.',
            'On an apparatus with no checks set up yet, select **Add Check**. Once it has one, the same button reads **Configure checks** — select it, then **Add New Check**.',
            'Pick a check type from the list, or select **Other** and name your own.',
            'Add sections, then items within each section. Select the **+** on a row, or press **Enter** while typing in it, to add the next item below.',
            'Optionally mark a section **Sealable**. Sealable sections are signed off as a whole, with inspector, date, next expiry and notes recorded once for every item in the section.',
            'Optionally add a Gear Check. **Add Gear Check** inside a section nests the gear under it, or you can select it below to give it a section of its own.',
            'Select **Save Changes**.'
        ],
        note: 'Only gear already assigned to this apparatus is offered in the **Add Gear Check** picker, and the gear category needs a check of its own set up via the gear page.',
        // The first two shots are the same spot on the apparatus record in its two
        // shapes: a plain button on a new apparatus, a menu once it has checks.
        // Ordered to match the steps — the none-set-up case is where a reader who
        // just added an apparatus actually is. The third is the editor they land in.
        images: [
            {
                src: '/help/add-check-none.webp',
                alt: 'An apparatus record with no checks set up: a Continue Pre-Trip button and an Add Check button below it.',
                caption: 'With none set up yet, the button reads **Add Check** and opens the check-type prompt directly.'
            },
            {
                src: '/help/add-check-existing.webp',
                alt: 'An apparatus record with the Configure checks menu open, listing Edit Weekly Check, Edit Full Check and Add New Check.',
                caption: 'Once the apparatus has checks, the same button reads **Configure checks** — it lists them for editing, with **Add New Check** below.'
            },
            {
                src: '/help/check-template-items.webp',
                alt: 'The check editor: a section with a title field and two item rows, each with a quantity, a name, an Add row button and a delete button.',
                caption: 'Each row is one item, with a quantity beside it. The **+** adds a new row below, and you can also hit **Enter** on your keyboard to create a new row item.'
            }
        ],
        cta: {label: 'Go to Apparatus', to: '/apparatus'}
    },
    {
        id: 'guide-run-check',
        kind: 'guide',
        category: 'apparatus',
        docType: 'how-to',
        // Set the check up, make a section sealable, then crews run it.
        orderIn: {apparatus: -1},
        title: 'Running your apparatus checks',
        summary: 'Crews work through the check a section at a time, marking each item complete or skipped, with notes and photos on anything that needs them.',
        taskLabel: 'To run a check',
        how: [
            'Open the apparatus. The buttons on the right start a check — a check already part-way through reads **Continue** instead of **Start**.',
            'Work down each section, marking every item **Complete** or **Skip**.',
            'Add a note beside anything that failed or needs following up, and a photo if it helps.',
            'On a sealable section, confirm the seal is intact rather than checking every item behind it.',
            'Add the users who ran the check.',
            'Select **Finish Check**.'
        ],
        note: 'A check is saved as you go, so a crew can stop and come back to it — the apparatus card shows it as in progress until it’s finished. It can’t be finished without members on it, and once finished it goes to the apparatus **Records**, awaiting review if your department signs checks off.',
        sections: [
            {
                title: 'Station login or personal login',
                body: 'A check can be run from either. Signed in as yourself, you work on your own account; signed in as a station, a device at the hall or on the truck stays on a shared account with a shared password, so nobody has to sign in and out to run the weekly check. Station sign-in is turned on in **Settings** > **Login**.',
                footer: 'A station is a shared account rather than a person, which is why the check asks who ran it — the users you add are the record of who did the work, whichever login was used.'
            }
        ],
        cta: {label: 'Go to Apparatus', to: '/apparatus'}
    },
    {
        id: 'guide-resolve-check',
        kind: 'guide',
        category: 'apparatus',
        docType: 'how-to',
        title: 'Resolving a check with issues',
        summary: 'Only checks with notes on them wait for review. Someone with approval permission answers the notes and marks them resolved, which closes the item and signs off its history.',
        images: [
            {
                src: '/help/resolve-check.webp',
                alt: 'A check awaiting review: two flagged items with the crew’s notes and who reported them, a Reply box and an Add Task button on each, and a Mark as Resolved button at the top right.',
                caption: 'The review view: every flagged item with its note, a **Reply** box and **Add Task** on each, and **Mark as Resolved** at the top right once they’re dealt with.'
            }
        ],
        sections: [
            {
                title: 'To resolve a check',
                body: 'A check awaiting resolution can be opened from its row in **Recent History** on the apparatus record, from **Records**, or from the **Assigned to me** section of the dashboard.',
                steps: [
                    'Open the check.',
                    'Reply to the flagged item, and optionally open a task from anything that needs following up.',
                    'Once the problem has been dealt with, select **Mark as Resolved**.',
                    'On the **Resolve Check** dialog, add a note describing what was done, attach any files, and confirm.'
                ],
                footer: 'An unresolved item continues to appear on every check of that apparatus, carrying its notes, until it’s resolved.'
            },
            {
                title: 'To assign a task from a failed item',
                steps: [
                    'Open the finished check and find the flagged item, marked with a warning triangle.',
                    'Select **Add Task** on that item, beside its reply box.',
                    'Give the task an assignee and a due date, then save.'
                ],
                note: 'The task carries the item, the apparatus and the crew’s note with it, so the assignee sees what was actually found. It also lands in the item’s thread as “assigned a task”, which keeps the inspection and the repair on one record.'
            }
        ],
        cta: {label: 'Go to Apparatus', to: '/apparatus'}
    },
    {
        id: 'guide-tasks',
        kind: 'guide',
        category: 'tasks',
        docType: 'how-to',
        title: 'Creating and assigning tasks',
        summary: 'Create a task for a user yourself. Most tasks arrive on their own from checks, gear schedules and expiries, but anything else you need done is added here.',
        taskLabel: 'To create a task',
        how: [
            'In the sidebar, go to **Tasks** and select **Add New Task**.',
            'Give it a **Name**, an **Assignee** and a **Due Date**.',
            'Add **Notes** if there’s anything the assignee needs to know.',
            'Select **Add task**.'
        ],
        cta: {label: 'Go to Tasks', to: '/tasks'}
    },
    {
        id: 'guide-add-member',
        orderIn: {members: -30},
        kind: 'guide',
        category: 'getting-started',
        alsoIn: ['members'],
        docType: 'how-to',
        title: 'Adding a user',
        summary: 'Users are added from the Users page. Email address is the only required field; all other profile details can be completed on creation or later by an admin or by the user.',
        icon: PersonAddAlt1Icon,
        taskLabel: 'To add a user',
        how: [
            'In the sidebar, go to **Users** and select **Add User**.',
            'Enter the user’s email address and details.',
            'Leave **Notify user via email** selected. We send the user a link to set their own password. If this option is cleared, an administrator must send a password reset from the user’s profile before the user can sign in.',
            'Select **Create User**.'
        ],
        // The bullets below are the plain-English reading of DefaultPermissions in
        // @embertracking/common (23 claims default to true). Keep them in step with
        // that object rather than with the role labels, which can diverge.
        sections: [
            {
                title: 'Default permissions',
                body: 'All new users receive the same standard permission set:',
                bullets: [
                    'View the user list, stations, apparatus, gear, preplans, files and NFPA references',
                    'View the calendar and duty shifts',
                    'Complete apparatus and gear checks',
                    'View and log fuel',
                    'Add and update records',
                    'Create tasks',
                    'See incident and training statistics, without access to the incidents or training records themselves'
                ],
                footer: 'Incidents, training records and administrative functions are disabled by default and must be granted individually. To adjust a user’s access, open the user from the **Users** page and edit their access in the **Permissions** tab.'
            }
        ],
        images: [
            {
                src: '/help/add-member.webp',
                alt: 'The Users page, with the Add User button at the top right of the user list.',
                caption: 'Users › Add User opens the new user form.'
            }
        ],
        cta: {label: 'Add a user', to: '/users/add'}
    },
    {
        id: 'guide-import-members',
        orderIn: {members: 70},
        kind: 'guide',
        category: 'getting-started',
        alsoIn: ['members'],
        docType: 'how-to',
        title: 'Importing and updating users in bulk',
        summary: 'Upload a CSV or Excel file to add or update many users at once. We map the columns for you, flag anything questionable, and show exactly what will change before anything is saved.',
        icon: GroupAddIcon,
        taskLabel: 'To import users',
        how: [
            'In the sidebar, go to **Users** and select **Import / Update**.',
            'Upload a **.csv**, **.xlsx** or **.xls** file.',
            'Check the column mapping. We match your headings to user fields automatically; correct anything we got wrong and set columns you don’t want to bring in to **Don’t import**.',
            'Review the rows. Each is marked as a create or an update, with any issues listed beside it.',
            'On the confirm step, decide whether to **Also update existing members matched in the file** and whether to **Email new members a set-password invite**.',
            'Select **Apply import**.'
        ],
        sections: [
            {
                title: 'What the review flags',
                body: 'Every row is checked before anything is saved:',
                bullets: [
                    'Errors: a missing name, a missing or invalid email on a user being created, or the same email twice in one file. These rows are skipped.',
                    'Warnings: an unrecognised rank, station or status, or a phone number that looks too short. These rows still import.',
                    'Notes: a name that differs from the matched user’s record.'
                ]
            }
        ],
        images: [
            {
                src: '/help/import-members.webp',
                alt: 'The Import / Update Users dialog on its first step, showing the four stages and a drop zone accepting .csv, .xlsx and .xls files.',
                caption: 'The import wizard runs in four steps, and only saves on the last one.'
            },
            {
                src: '/help/import-mapping.webp',
                alt: 'The Map columns step, with each file heading matched to a user field and an unwanted column set to Don’t import.',
                caption: 'Headings we recognise are matched automatically and badged with Auto. Anything you don’t want to use you can set to **Don’t import** from the dropdown.'
            }
        ],
        cta: {label: 'Go to Users', to: '/users'}
    },
    {
        id: 'guide-permissions',
        orderIn: {members: 80},
        kind: 'guide',
        category: 'getting-started',
        alsoIn: ['members'],
        docType: 'how-to',
        title: 'Setting a user’s permissions',
        summary: 'Every user starts from a base role. Grant one-off extras on top of it, and only reach for the advanced controls when a single area needs setting precisely.',
        icon: ManageAccountsIcon,
        taskLabel: 'To set a user’s permissions',
        how: [
            'In the sidebar, go to **Users** and select the user.',
            'Open the **Permissions** tab.',
            'Pick a **Base role**. Each card lists what that role can and can’t do.',
            'To grant something the role doesn’t cover, select **Add access** and choose the area. This is the usual way to handle a one-off, such as letting a user approve payroll.',
            'Select **Save changes**.'
        ],
        sections: [
            {
                title: 'The three roles',
                body: 'The base role sets a sensible default for every area at once:',
                bullets: [
                    '**Standard** — everyday access. Completes apparatus, gear and fuel checks, views preplans, logs their own tasks, and sees incident and training summary statistics. Can’t view sensitive incident or training records.',
                    '**Elevated** — creates and views sensitive incidents and training records, edits apparatus and checks, duty shifts and preplans, and approves sign-offs. Can’t delete records, run payroll, or change settings.',
                    '**Administrator** — unrestricted, including payroll, users and system settings.'
                ]
            },
            {
                title: 'Setting a single area precisely',
                body: 'Open **Advanced** to set one area on its own, including reducing it below what the role grants. The app calls this rarely needed, and it is:',
                bullets: [
                    'Each area steps from **Hidden** through **View**, **Edit** and **Approve** up to **Delete**.',
                    'Each level includes the ones below it, so **Edit** also grants **View**.',
                    'Apparatus, gear and stations name their levels **Checks** and **Edit/Approve** instead. **Checks** completes checks and adds records; changing the truck, gear item or hall itself needs **Edit/Approve**.',
                    'Areas are grouped, so Resources, Administration and the rest are set separately.'
                ]
            }
        ],
        note: 'Users see only the areas they’re permitted to, and the sidebar hides the rest. You can’t change your own permissions; ask another administrator. **Administrator** bypasses every area check, so grant it sparingly.',
        images: [
            {
                src: '/help/permissions.webp',
                alt: 'The Permissions tab, showing the three base role cards, the access summary with an Add access button, and the advanced per-area controls.',
                caption: 'Pick a base role, then use **Add access** for anything extra. **Advanced** is there for the rare case that needs one area set on its own.'
            }
        ],
        cta: {label: 'Go to Users', to: '/users'}
    },
    {
        id: 'guide-settings-stations',
        kind: 'guide',
        category: 'getting-started',
        alsoIn: ['settings'],
        docType: 'how-to',
        title: 'Adding a station',
        summary: 'Stations are what apparatus, gear and members get assigned to, and what most filters narrow by.',
        taskLabel: 'To add a station',
        how: [
            'Go to **Settings** > **Stations**.',
            'Add a station and give it a **Station number**, an **Address**, or both.',
            'Save.'
        ],
        note: 'One of the two is required: a station with no number needs an address to identify it. Worth setting up before adding apparatus, since assigning a truck to a station it can’t see means going back through them afterwards.',
        cta: {label: 'Open Settings', to: '/settings/stations'}
    },
    {
        id: 'guide-settings-export',
        kind: 'guide',
        category: 'settings',
        docType: 'how-to',
        title: 'Downloading everything your department holds',
        summary: 'A full copy of every record and every uploaded file — for a backup, an audit, or a freedom-of-information request.',
        taskLabel: 'To export everything',
        how: [
            'Go to **Settings** > **Data Export**.',
            'Start the export.',
            'Download each archive as it becomes ready — they arrive one area of the app at a time rather than all at the end.'
        ],
        note: 'Records come out as CSV and uploaded files as zip archives. It can be run once a day, and the archives are kept for seven days before being deleted, so download them rather than treating the page as somewhere they live. This needs admin: a full export packages every HR record and payroll line in the department, which is a different sensitivity class from anything else in Settings.',
        cta: {label: 'Open Settings', to: '/settings/dataexport'}
    },
    {
        id: 'guide-add-apparatus',
        kind: 'guide',
        category: 'getting-started',
        docType: 'how-to',
        title: 'Adding apparatus',
        summary: 'Apparatus are the vehicles, trailers and other equipment tracked on their own record, with their own checks, gear, fuel logs and service history.',
        icon: FireTruckIcon,
        alsoIn: ['apparatus'],
        // Sorts above the other apparatus how-tos: you add a truck before you can
        // check one. Getting Started keeps its own setup sequence.
        orderIn: {apparatus: -4},
        taskLabel: 'To add an apparatus',
        how: [
            'In the sidebar, select **Apparatus**, then select **Add Apparatus** on the Apparatus page.',
            'Add an apparatus image.',
            'Add the apparatus details.',
            'Select **Save Apparatus**.'
        ],
        sections: [
            {
                title: 'What to do next',
                bullets: [
                    'Assign gear to the apparatus from the **Gear** section, using the apparatus as the gear’s location.',
                    'Set up an apparatus check. Add the types the organization uses, such as weekly, full or maintenance checks. Assigned gear can be added as a section on the check, so each item’s checks are completed within the apparatus check rather than separately.',
                    'Change the status when the apparatus comes off the run — out of service, or deployed.',
                    'If the organization stores fuel on site, log fuel from the **Fuel** section to track consumption for the apparatus.',
                    'If the apparatus is available for BCWS deployment, set its **BCWS Deployment Type** and mark it **Deployment Ready**.'
                ]
            }
        ],
        images: [
            {
                src: '/help/add-apparatus.webp',
                alt: 'The Apparatus page, showing the fleet as cards grouped by status, with the Add Apparatus button at the top right.'
            }
        ],
        cta: {label: 'Add apparatus', to: '/apparatus/add'}
    },
    {
        id: 'about-members',
        kind: 'guide',
        category: 'members',
        docType: 'explanation',
        title: 'About users',
        summary: 'Each user has a profile that connects their contact details, rank and station with their service history, qualifications and activity. Keeping one profile for each person lets you follow their time with the department.',
        sections: [
            {
                title: 'Profile and access',
                body: 'Start in **Users** to add someone or open an existing profile. Record their name, contact details, station and start dates. Their **Rank/Role** describes their position; **Permissions** controls what they can access in Ember Tracking.',
                footer: 'See [[guide-add-member]] and [[guide-edit-member]] for the basics, or [[guide-permissions]] to change access.'
            },
            {
                title: 'Their time with the department',
                body: '**Service History** records rank changes, leave and returns with effective dates. **Records** holds supporting documents and notes such as commendations, service awards and driver’s abstracts.',
                footer: 'Use a status change when someone leaves the department so their past attendance and records remain available. See [[guide-member-status]], [[guide-member-service-history]] and [[guide-member-records]].'
            },
            {
                title: 'Activity in one place',
                body: 'Open **Incidents**, **Training** or **Deployments** within the profile to review the member’s activity and statistics. Other sections include qualifications, gear and tasks.',
                footer: 'The sections available depend on your permissions and the features enabled for your department. Users can also open their own profile; confidential records are hidden from the member’s own view.'
            }
        ],
        cta: {label: 'Go to Users', to: '/users'}
    },
    {
        id: 'guide-member-status',
        kind: 'guide',
        category: 'members',
        docType: 'how-to',
        title: 'Putting a member on leave, or off the roster',
        summary: 'Record what changed and when it took effect, rather than editing someone out of existence.',
        taskLabel: 'To change a member’s status',
        how: [
            'Go to **Users**, open the member and select **Change** beside their status on **Profile**.',
            'Choose the **Status** — **On Leave**, **Medical Leave**, **Resigned** or **Terminated**, or **Active** when they return.',
            'Set the **Effective date** and add a note or supporting files if needed.',
            'Select **Update Status**. The change appears in **Service History**.'
        ],
        note: 'Status changes preserve the member’s history and record when they were available. To find someone who has resigned or been terminated, turn on **Include Inactive** on the Users page. See [[guide-member-service-history]] to review or correct earlier changes.',
        images: [
            {
                src: '/help/member-change-status.webp',
                alt: 'The Active status badge on a user’s profile, with the Change button beside it.',
                caption: 'On the user’s **Profile**, select **Change** beside their current status.'
            },
            {
                src: '/help/member-status-dialog.webp',
                alt: 'The Change Member Status dialog with Status, Effective date, Note, Add Files and Update Status controls.',
                caption: 'Choose the **Status** and **Effective date**, add a note or files if needed, then select **Update Status**.'
            }
        ],
        cta: {label: 'Go to Users', to: '/users'}
    },
    {
        id: 'guide-view-member',
        kind: 'guide',
        category: 'members',
        docType: 'how-to',
        orderIn: {members: -20},
        title: 'Finding and viewing a user',
        summary: 'Open a user’s profile to see their details and move between their service, training and activity sections.',
        taskLabel: 'To view a user',
        how: [
            'Go to **Users** and search for the person you want.',
            'Turn on **Include Inactive** if you need to find a former member.',
            'Select the user to open their **Profile**.',
            'Use the profile’s section navigation to open **Service History**, **Records**, **Incidents**, **Training** or another available section.'
        ],
        note: 'Viewing a profile does not grant access to every section. For example, viewing another member’s Records needs HR access, and incident and training sections need their respective permissions.',
        cta: {label: 'Go to Users', to: '/users'}
    },
    {
        id: 'guide-edit-member',
        kind: 'guide',
        category: 'members',
        docType: 'how-to',
        orderIn: {members: -10},
        title: 'Editing a user’s profile',
        summary: 'Keep contact details, station, emergency contacts and other profile information current.',
        taskLabel: 'To edit a user',
        how: [
            'Go to **Users**, select the user and open **Profile**.',
            'Update the relevant fields, such as their name, phone numbers, station or emergency contacts.',
            'Check **Department Start Date** for when they joined this department, and **Service Start Date** for earlier fire service if applicable.',
            'Select **Update User** in the save bar. When editing your own profile, the button is **Save Profile**.'
        ],
        note: 'Editing other users requires permission to edit users. For an existing firefighter, use **Change** beside **Rank/Role** to open Service History and record a dated rank change. Use **Change** beside their status for leave or a return to active service.',
        cta: {label: 'Go to Users', to: '/users'}
    },
    {
        id: 'guide-delete-member',
        kind: 'guide',
        category: 'members',
        docType: 'how-to',
        orderIn: {members: 10},
        title: 'Deleting an unused user account',
        summary: 'An unused account can be permanently deleted when it has no linked records. For someone who has served with the department, use [[guide-member-status]] to keep their history.',
        taskLabel: 'To delete an unused account',
        how: [
            'Go to **Users**, open the account and select **Profile**.',
            'Select **Delete** in the save bar. This is available only with permission to delete users, for another account that is eligible for deletion.',
            'Read the confirmation and type the name or email shown.',
            'Select **Delete Permanently**.'
        ],
        note: 'Deletion cannot be undone. If the account is referenced by incidents, training or other linked records, deletion is blocked. **Resigned** or **Terminated** keeps those records connected to the member.',
        cta: {label: 'Go to Users', to: '/users'}
    },
    {
        id: 'guide-member-service-history',
        kind: 'guide',
        category: 'members',
        docType: 'how-to',
        orderIn: {members: 20},
        title: 'Recording and reviewing service history',
        summary: 'Service History brings a firefighter’s ranks, leave and returns into one timeline, with their current rank, years of service and start date above it.',
        taskLabel: 'To record a service change',
        how: [
            'Go to **Users**, open the member and select **Service History**.',
            'Select **Record change** above the timeline.',
            'Choose **Rank** or **Status**, then select the new value. For a rank change, choose the **Appointment** where applicable.',
            'Enter the **Effective date** and an optional note. For older rank changes, use **Date precision** if you know only the month or year.',
            'Select **Add Entry**. Review the change on the timeline.'
        ],
        images: [
            {
                src: '/help/member-service-history.webp',
                alt: 'A member’s Service History showing current rank, years of service, joined date and status, above a chart of rank progression and active service.',
                caption: 'The summary shows the member’s current service details. The **Service History** chart shows how their rank and status have changed over time.'
            }
        ],
        sections: [
            {
                title: 'Correcting the history',
                body: 'Use an entry’s **Edit** control to correct its date or details, then select **Save Changes**. Use **Delete** for an entry recorded in error and review the confirmation. Changes to the timeline can change the member’s current rank or status.'
            },
            {
                title: 'Start dates and access',
                body: 'If the setup message asks for start dates, select **Set dates**, or update them on **Profile**. Department Start Date is when they joined this department; Service Start Date can reflect earlier fire service.',
                footer: 'Service History is available for firefighter ranks from Recruit through Chief. Members can view their own history; recording or correcting entries requires permission to edit users.'
            }
        ],
        cta: {label: 'Go to Users', to: '/users'}
    },
    {
        id: 'guide-member-records',
        kind: 'guide',
        category: 'members',
        docType: 'how-to',
        orderIn: {members: 30},
        title: 'Adding and managing a user’s records',
        summary: 'Use Records for personnel notes and supporting documents, such as commendations, service awards, driver’s abstracts and criminal record checks.',
        taskLabel: 'To add a record',
        how: [
            'Go to **Users**, open the member and select **Records**.',
            'Select **Add New Record**.',
            'Choose the **Type**, set the **Date** and enter a **Note**.',
            'Review **Confidential** and attach any supporting files. A **Driver’s Abstract** requires a file.',
            'Select **Add Record**.'
        ],
        sections: [
            {
                title: 'Viewing, editing and deleting',
                body: 'Select a record in the list to read it and view its attachments. To make changes, select **Edit Record**, update the details and select **Update Record**. **Delete Record** is available while editing when you have permission to delete HR records.'
            },
            {
                title: 'Who can see the record',
                body: 'Records must be enabled for your department. Viewing someone else’s records requires HR read access; adding or editing requires HR write access. Permission to edit users alone does not grant HR access.',
                footer: 'Members can see their own non-confidential records. **Discipline**, **Driver’s Abstract**, **Criminal Record Check** and **Note** start as confidential; review that setting before saving.'
            },
            {
                title: 'Recording a promotion',
                body: 'A **Promotion** record can hold the supporting note or document. To change the member’s rank and its effective date, also record the change in **Service History** — see [[guide-member-service-history]].'
            }
        ],
        cta: {label: 'Go to Users', to: '/users'}
    },
    {
        id: 'guide-member-incident-stats',
        kind: 'guide',
        category: 'members',
        docType: 'how-to',
        orderIn: {members: 40},
        title: 'Viewing a user’s incident statistics',
        summary: 'Review how often a member responded, stood by or was cancelled, along with their hours and response trends.',
        taskLabel: 'To review incident activity',
        how: [
            'Go to **Users**, open the member and select **Incidents**.',
            'Choose **This Year**, **Last Year** or **By Month**. For a monthly view, select the month.',
            'Review **Responded**, **Standby**, **Cancelled**, **Missed** and **Total Hours**, plus the response trends and breakdowns by incident type and apparatus.',
            'If you have permission to edit incidents, select an attendance card to view the matching incidents below.',
            'After correcting attendance on an incident, use **Recalculate now** to refresh the statistics.'
        ],
        note: 'The section requires permission to view incident statistics. Attendance uses the member’s eligible service period, accounting for joining dates, leave and deployment absences. Correct attendance on the original incident if a figure is wrong.',
        cta: {label: 'Go to Users', to: '/users'}
    },
    {
        id: 'guide-member-training-stats',
        kind: 'guide',
        category: 'members',
        docType: 'how-to',
        orderIn: {members: 50},
        title: 'Viewing a user’s training statistics',
        summary: 'Review a member’s practice attendance, missed sessions and training by discipline, then open the records behind the figures.',
        taskLabel: 'To review training activity',
        how: [
            'Go to **Users**, open the member and select **Training**.',
            'Set the **Date Range** for the period you want to review.',
            'Select **Practice Attendance** or **Missed Practices** to filter the list. **Extra Practice** and **Instructed** appear when there is activity to show.',
            'Select a card under **By Discipline** to narrow the list, and use **Show Missed** to include missed sessions.',
            'Open a training record to review its details. After correcting attendance, use **Recalculate now** to refresh the figures.'
        ],
        note: 'This section requires access to training records. Attendance accounts for the member’s eligible service period and absences. Competency sign-offs are recorded on the training record and can be verified in the Training Matrix — see [[guide-training-competencies]] and [[guide-training-matrix]].',
        cta: {label: 'Go to Users', to: '/users'}
    },
    {
        id: 'guide-member-deployment-stats',
        kind: 'guide',
        category: 'members',
        docType: 'how-to',
        orderIn: {members: 60},
        title: 'Viewing a user’s deployment statistics',
        summary: 'See a member’s deployment rotations, days away, recorded work hours and travel hours in one place.',
        taskLabel: 'To review deployment activity',
        how: [
            'Go to **Users**, open the member and select **Deployments**.',
            'Review **Deployments**, **Days Deployed**, **Hours** and **Travel**. A seasonal chart appears when there is more than one year of activity.',
            'Search or sort the rotations below to find the deployment, apparatus, role and dates you need.',
            'Open the deployment link when you have permission to view it. Correct staffing dates or recorded days on the deployment if something is missing.'
        ],
        note: 'Rotations appear when the member is staffed on an apparatus for a deployment. Hours come from recorded days. Deployments must be enabled; members can view their own deployment history, while viewing someone else’s requires deployment access.',
        cta: {label: 'Go to Users', to: '/users'}
    },
    {
        id: 'ref-member-ranks',
        kind: 'guide',
        category: 'members',
        docType: 'reference',
        title: 'Ranks and statuses',
        summary: 'The ranks a member can hold and the states they can be in.',
        sections: [
            {
                title: 'Firefighter ranks',
                bullets: [
                    '**Recruit** · **Auxiliary** · **Exterior** · **Interior** · **Full Service**',
                    '**Team Leader** · **Engineer** · **Lieutenant** · **Captain**',
                    '**Assistant Training Officer** · **Training Officer** · **Assistant Chief** · **Deputy Chief** · **Chief**'
                ],
                footer: 'These are the ranks the department is measured on: attendance percentages, the qualification matrix and AWOL calculations all work from this list. Auxiliary does not imply Exterior qualification; training for auxiliary/support members depends on their assigned duties and hazards.'
            },
            {
                title: 'Other account roles',
                bullets: [
                    '**Apparatus** — a shared station login rather than a person.',
                    '**Employee** and **Administrator** — accounts that run the system rather than respond to calls.'
                ],
                footer: 'They’re left out of turnout statistics on purpose, so an admin account never drags a department average down.'
            },
            {
                title: 'Statuses',
                bullets: [
                    '**Active** — on the roster.',
                    '**On Leave** · **Medical Leave** — suspended, still a member.',
                    '**Resigned** · **Terminated** — off the roster.'
                ]
            }
        ],
        cta: {label: 'Go to Members', to: '/users'}
    },
    {
        id: 'ref-driver-clearance-settings',
        kind: 'guide',
        category: 'members',
        docType: 'reference',
        title: 'Driver clearance settings',
        summary: 'What **Settings** > **Driver Clearance** controls: the supervised hours a member logs toward driving each apparatus.',
        sections: [
            {
                title: 'Driving hours',
                bullets: [
                    '**Track driving hours** — members log supervised right-seat hours against an apparatus, and an officer approves them.',
                    '**Hours required** — the target, counted separately per apparatus. Twenty here means twenty on the engine and another twenty on the tender.',
                    '**Applies to** — the apparatus types that need hours at all. Trailers and boats aren’t on the list, because neither is driven.'
                ],
                footer: 'One awkward truck that needs a different figure is handled on the apparatus itself, under **Driving hours to clear** — the setting here is the department standard, not nine separate ones.'
            },
            {
                title: 'Hours aren’t the clearance',
                body: 'Reaching the target doesn’t clear anyone. It marks a member as ready for an officer to make the call, per apparatus — the hours are the evidence behind that decision rather than the decision itself.'
            }
        ],
        cta: {label: 'Open Settings', to: '/settings/driverclearance'}
    },
    {
        id: 'about-apparatus',
        kind: 'guide',
        category: 'apparatus',
        docType: 'explanation',
        title: 'About apparatus',
        summary: 'An apparatus record is the hub for a vehicle. Its checks, the gear it carries, the fuel it draws, its service history and its deployment status all hang off it.',
        sections: [
            {
                title: 'What attaches to an apparatus',
                body: 'Once the record exists, the rest of the fleet features attach to it. You can:',
                bullets: [
                    'Assign gear to it, so the equipment carried on it is checked alongside it',
                    'Set up its checks, so crews can run weekly and pre-trip checks against it',
                    'Log the fuel it draws',
                    'Record service dates and keep its full history',
                    'Set its BCWS deployment type and mark it deployment ready, so it can go out on deployments',
                    'Have crews run its checks from a shared station login'
                ]
            },
            {
                title: 'Apparatus and gear are checked the same way',
                body: 'Checks are one mechanism with two subjects. A check is set up once and run against either an apparatus or a gear item, which is why a gear category can be joined into a truck’s check and covered in the same walkthrough.',
                footer: 'Permissions follow that split: running apparatus checks and running gear checks are granted separately, while editing the checks themselves is its own permission again.'
            }
        ],
        cta: {label: 'Open Apparatus', to: '/apparatus'}
    },
    {
        id: 'guide-apparatus-status',
        kind: 'guide',
        category: 'apparatus',
        docType: 'how-to',
        title: 'Changing the status of an apparatus',
        summary: 'An apparatus is **In Service**, **Reserve**, **Out of Service** or **Decommissioned**. Status determines how the apparatus sorts on the Apparatus page and whether it’s available for use.',
        taskLabel: 'To change an apparatus status',
        how: [
            'In the sidebar, go to **Apparatus** and open the apparatus.',
            'Select **Edit**. Status is changed on the edit form, not from the apparatus page itself.',
            'Next to the status chip, select **Change**.',
            'Pick the new status and confirm.'
        ],
        note: 'Changing status needs permission to edit apparatus; without it the chip shows the current status but there’s no **Change** button.',
        images: [
            {
                src: '/help/apparatus-status.webp',
                alt: 'The Edit Apparatus form, with an In Service chip below the image in the left column and a Change button beside it.',
                caption: 'On the **Edit Apparatus** form, the status chip sits under the image with **Change** beside it.'
            }
        ],
        cta: {label: 'Open Apparatus', to: '/apparatus'}
    },
    {
        id: 'ref-apparatus-types',
        kind: 'guide',
        hidden: true,
        category: 'apparatus',
        docType: 'reference',
        title: 'Apparatus types',
        summary: 'The full list of type options on an apparatus record.',
        sections: [
            {
                title: 'Types',
                bullets: [
                    '**Engine** · **Pumper** · **Tender** · **Ladder**',
                    '**Bush Truck** · **Rescue Truck** · **Utility Truck**',
                    '**Trailer** · **Boat** · **Car**'
                ],
                footer: 'Type is required and defaults to Engine. It’s a label rather than a behaviour switch — nothing in checks, gear or fuel changes based on it.'
            }
        ],
        cta: {label: 'Open Apparatus', to: '/apparatus'}
    },
    {
        id: 'ref-apparatus-statuses',
        kind: 'guide',
        hidden: true,
        category: 'apparatus',
        docType: 'reference',
        title: 'Apparatus statuses',
        summary: 'The four statuses an apparatus can hold, and the one fleet grouping that isn’t a status at all.',
        sections: [
            {
                title: 'Statuses',
                bullets: [
                    '**In Service** — the default. A new apparatus starts here.',
                    '**Reserve** — held back but still available.',
                    '**Out of Service** — not available to respond.',
                    '**Decommissioned** — retired. The record stays for its history, and its form is read only.'
                ]
            },
            {
                title: 'Needs attention',
                body: '**Needs attention** appears on the Apparatus page but is not a status. It’s derived: an apparatus lands there when a check is in progress on it, or when a completed check is waiting to be reviewed.',
                footer: 'The waiting-to-be-reviewed half only shows for people who can approve checks, so the same fleet can group differently for two people looking at it.'
            }
        ],
        cta: {label: 'Open Apparatus', to: '/apparatus'}
    },
    {
        id: 'ref-check-types',
        kind: 'guide',
        hidden: true,
        category: 'apparatus',
        docType: 'reference',
        title: 'Apparatus check types',
        summary: 'The check types an apparatus can be run through.',
        sections: [
            {
                title: 'Types',
                bullets: [
                    '**Weekly** — the routine walkthrough. Sorts first wherever checks are listed.',
                    '**Full** — the deeper inspection.',
                    '**Maintenance** — work done on the apparatus rather than an inspection of it.',
                    '**BCWS Readiness** — the wildfire deployment readiness check.',
                    '**Pre-Trip** — run before taking the apparatus out. Handled on its own path, and can be set to skip review.'
                ],
                footer: 'Each type is set up separately, so a truck can carry a different section list for its weekly than for its full.'
            }
        ],
        cta: {label: 'Open Apparatus', to: '/apparatus'}
    },
    {
        id: 'ref-check-settings',
        kind: 'guide',
        category: 'apparatus',
        docType: 'reference',
        title: 'Apparatus check settings',
        summary: 'What **Settings** > **Checks** controls: how a completed pre-trip or post-trip is signed off, and who gets a copy. Everything on that page is about apparatus checks — gear check scheduling lives on the gear category itself.',
        sections: [
            {
                title: 'Workflow',
                bullets: [
                    '**Paper pre-trips** — allow scanned paper forms to be imported, for crews not yet doing them on a screen.',
                    '**Approval** — treat completed pre-trips and post-trips as done without an approval step. Other check types still need approving when they carry a defect or a note, so nothing with a problem on it slips through.',
                    '**Approval emails** — send them only to approvers at the apparatus’s own station rather than every approver. Admins always get them regardless.'
                ]
            },
            {
                title: 'Completed PDFs',
                body: 'A completed pre-trip or post-trip can be emailed as a PDF to a list of addresses — which is how departments get the paperwork to a mechanic, a shop or a regional office without anyone forwarding it by hand.',
                footer: 'These go to whatever addresses you list, so they’re not limited to members with accounts.'
            }
        ],
        cta: {label: 'Open Settings', to: '/settings/checks'}
    },
    {
        id: 'about-gear',
        kind: 'guide',
        category: 'gear',
        docType: 'explanation',
        title: 'About gear',
        summary: 'Gear is whatever your department decides to track. There’s no fixed list: you create categories and define the fields each one carries, so PPE, SCBA, ladders, hose and consumables all live in the same system on their own terms.',
        sections: [
            {
                title: 'Categories define their own shape',
                body: 'A category is a kind of thing you track, and it carries its own field list. Categories can hold subcategories, so Structural PPE can sit above Helmets, Turnout Jackets and Boots.',
                bullets: [
                    'The **first field is the item’s name** — it’s what shows wherever the item is listed.',
                    'Any field can be marked **required**, or **unique** so no two items share a value such as a serial.',
                    'Add as many other fields as the category needs.'
                ]
            },
            {
                title: 'Field types change what a category does',
                body: 'Most field types just store a value. Three of them change behaviour, which is the part worth knowing before you design a category:',
                bullets: [
                    '**Expiry date** — items get expiry tasks generated for them automatically.',
                    '**Location** — items become placeable, on an apparatus or at a location.',
                    '**Member** — items become assignable to a person, for issued kit.'
                ],
                footer: 'A **Number** field named quantity, count or amount turns the category into counted stock rather than individually tracked items.'
            },
            {
                title: 'Items don’t have a status',
                body: 'Unlike apparatus, a gear item has no In Service or Out of Service setting. What you see on an item is derived from its own data: an expiry approaching, stock below its threshold, or a check that’s due.'
            }
        ],
        cta: {label: 'Open Gear', to: '/gear'}
    },
    {
        id: 'guide-gear-categories',
        kind: 'guide',
        category: 'gear',
        docType: 'how-to',
        orderIn: {gear: -6},
        title: 'Adding a gear category',
        summary: 'A category is a kind of thing you track — helmets, SCBA bottles, hose. Creating a category sets the fields each of its items records, so everything in it captures the same information.',
        taskLabel: 'To add a category',
        how: [
            'In the sidebar, go to **Gear**.',
            'Select **All Categories** on the left, then select **Add Category** at the top right.',
            'Enter a name, or select a suggested category to load a name and a standard set of fields.',
            'Add or remove fields to match what you track.',
            'Select **Save**.'
        ],
        note: 'Once the category is saved, you can add items and set up a check for it.',
        images: [
            {
                src: '/help/gear-categories.webp',
                alt: 'The Gear page with All Categories selected, the category cards listed, and the Add Category button at the top right.',
                caption: 'All Categories lists every top-level category. Add Category is at the top right.'
            }
        ],
        sections: [
            {
                title: 'Subcategories',
                body: 'A category can contain other categories, so Structural PPE can sit above Helmets, Turnout Jackets and Boots. To add a subcategory:',
                steps: [
                    'Open the parent category.',
                    'Select the pencil icon, then select **Add subcategory**.',
                    'Enter a name and define its fields.'
                ],
                images: [
                    {
                        src: '/help/gear-subcategory.webp',
                        alt: 'A gear category page with the pencil menu open, showing Edit category, Add subcategory and Add check.',
                        caption: 'The pencil icon on a category opens Edit category, Add subcategory and Add check.'
                    }
                ],
                footer: 'Subcategories do not inherit fields from the parent. Each one defines its own set, so helmets and boots can record different information. Categories can be nested up to twelve levels deep. At the twelfth level, **Add subcategory** is unavailable.'
            },
            {
                title: 'Suggested categories',
                body: 'Selecting a suggested category creates the full branch at once: the parent category, its subcategories and their fields. Delete anything that doesn’t apply.'
            }
        ],
        cta: {label: 'Open Gear', to: '/gear'}
    },
    {
        id: 'about-gear-locations',
        kind: 'guide',
        category: 'gear',
        docType: 'explanation',
        title: 'Where gear lives',
        summary: 'An item is placed somewhere, and that somewhere is either an apparatus or a location you manage — a hall, a locker, a shelf. Both are the same kind of thing to Ember Tracking.',
        sections: [
            {
                title: 'Two kinds of placement',
                bullets: [
                    'On an **apparatus** — the item rides on that truck, and can be checked as part of the truck’s walkthrough.',
                    'At a **location** — a place you define, for anything not carried on a vehicle.'
                ],
                footer: 'Apparatus is deliberately treated as one kind of location rather than a separate concept, so a single Location field on a category can point at either.'
            },
            {
                title: 'Locations nest',
                body: 'Locations form a tree, so a shelf can sit inside a locker inside a hall. An item shows its full path, which is what tells someone where to actually walk to:',
                bullets: [
                    'Hall › Bay 2 › Locker 4'
                ]
            }
        ],
        cta: {label: 'Open Gear', to: '/gear'}
    },
    {
        id: 'guide-gear-location-add',
        kind: 'guide',
        category: 'gear',
        docType: 'how-to',
        orderIn: {gear: -7},
        title: 'Adding a gear location',
        summary: 'Create the places gear lives, and nest them as deep as your building needs.',
        taskLabel: 'To add a location',
        how: [
            'In the sidebar, go to **Gear**.',
            'Scroll to the bottom of the gear sidebar to see the location list. Select the **+** button to create a top-level location and give it a name.',
            'To nest one inside another, use **Add sublocation** under the three dots on the location you want it under.'
        ],
        note: 'Names only have to be unique among their siblings, so every hall can have its own Bay 1.',
        images: [
            {
                src: '/help/gear-locations.webp',
                alt: 'The Locations list in the gear sidebar with a location’s three-dot menu open, showing Add sublocation, Rename, Move to and Archive. Bottom Shelves and Top Shelves are nested under Large Closet, and apparatus are listed below.',
                caption: 'The three dots on a location open **Add sublocation**. The apparatus are listed below the locations.'
            }
        ],
        sections: [
            {
                title: 'What counts as a location',
                body: 'A location is anywhere gear can live. That’s either an apparatus, or a physical place you define — a closet, a shelf, a bay, a hall.',
                footer: 'Both kinds sit in the same list, and a category’s **Location** field can point at either. So moving a helmet off a shelf and onto a truck is one field changing its value, not a different kind of record.'
            }
        ],
        cta: {label: 'Open Gear', to: '/gear'}
    },
    {
        id: 'guide-gear-check-create',
        kind: 'guide',
        category: 'gear',
        docType: 'how-to',
        orderIn: {gear: -3},
        title: 'Gear checks',
        summary: 'A category can have its own checks. Set one up once and every item in the category is checked against the same list.',
        sections: [
            {
                title: 'Creating a gear check',
                steps: [
                    'Open the category, then select the pencil icon at the top right of the page.',
                    'Select **Add check**.',
                    'Choose a check type from the list, or select **Other** and enter your own name.',
                    'Select **Add check** to create it.',
                    'Add sections, then items within each section. Select the **+** on a row, or press **Enter** while typing in it, to add the next item below.',
                    'Optionally mark a section **Sealable**. Sealable sections are signed off as a whole, with inspector, date, next expiry and notes recorded once for every item in the section.',
                    'Select **Save**.'
                ],
                images: [
                    {
                        src: '/help/gear-check-menu.webp',
                        alt: 'A gear category’s pencil menu open, listing Edit category, Add subcategory, Edit Full check and Add check.',
                        caption: 'The pencil icon carries both **Add check** and an **Edit** entry for each check the category already has.'
                    }
                ]
            },
            {
                title: 'Setting a schedule on a check',
                body: 'A check with no schedule runs only when someone runs it. Give it a frequency and it comes due on its own.',
                steps: [
                    'Open the category, select the pencil icon at the top right, then select **Edit category**.',
                    'Scroll to the bottom. Against each check type, choose a frequency: **Weekly**, **Monthly**, **Quarterly**, **Every 6 months** or **Yearly**.',
                    'Select **Save**.'
                ],
                images: [
                    {
                        src: '/help/gear-check-schedule.webp',
                        alt: 'The Gear Check Schedule card at the foot of the edit-category form, with a frequency dropdown per check type.',
                        caption: 'Gear Check Schedule sits at the foot of the edit-category form, one dropdown per check type.'
                    }
                ],
                footer: 'Scheduled checks generate tasks as they come due, so a yearly hydro test doesn’t depend on anyone remembering it. Leave a check on **No schedule** if it’s only ever run on demand.'
            },
            {
                title: 'Editing a check',
                body: 'The pencil icon at the top right lists the category’s existing checks. Select the one you want to edit.'
            }
        ],
        cta: {label: 'Open Gear', to: '/gear'}
    },
    {
        id: 'guide-gear-check-run',
        kind: 'guide',
        category: 'gear',
        docType: 'how-to',
        orderIn: {gear: -2},
        title: 'Running a gear check',
        summary: 'Checks are run against an item rather than the category — each helmet, each bottle, on its own record. Crews work through a section at a time, marking each item complete or skipped, with notes and photos on anything that needs them.',
        taskLabel: 'To run a gear check',
        how: [
            'Open the gear item. The buttons on the right start a check — a check already part-way through reads **Continue** instead of **Start**.',
            'Work down each section, marking every item **Complete** or **Skip**.',
            'Add a note beside anything that failed or needs following up, and a photo if it helps.',
            'On a sealable section, confirm the seal is intact rather than checking every item behind it.',
            'Add the users who ran the check.',
            'Select **Finish Check**.'
        ],
        note: 'A check is saved as you go, so it can be put down and picked up later — the button reads **Continue** until it’s finished. It can’t be finished without members on it, and once finished it goes to the item’s **Records**.',
        sections: [
            {
                title: 'Station login or personal login',
                body: 'A check can be run from either. Signed in as yourself, you work on your own account; signed in as a station, a device at the hall or on the truck stays on a shared account with a shared password, so nobody has to sign in and out to run a check. Station sign-in is turned on in **Settings** > **Login**.',
                footer: 'A station is a shared account rather than a person, which is why the check asks who ran it — the users you add are the record of who did the work, whichever login was used.'
            },
            {
                title: 'A note is what flags a problem',
                body: 'Adding a note to an item is what marks it as a problem. A finished check carrying notes waits for review, and someone with permission to approve gear checks answers the note and marks it resolved.'
            }
        ],
        cta: {label: 'Open Gear', to: '/gear'}
    },
    {
        id: 'guide-gear-in-apparatus-check',
        kind: 'guide',
        category: 'gear',
        docType: 'how-to',
        orderIn: {gear: -1},
        title: 'Including gear in an apparatus check',
        summary: 'Gear placed on an apparatus can be checked as part of that truck’s walkthrough, so a crew covers the rig and what it carries in one pass instead of two.',
        taskLabel: 'To add gear to an apparatus check',
        how: [
            'Edit the apparatus check.',
            'Select **Add Gear Check** and choose the category.',
            'Pick which of that category’s checks to include.',
            'Drag the card to where those items should appear — on its own below the sections, or inside a section to nest them under it.',
            'Save.'
        ],
        sections: [
            {
                title: 'What a category needs before it can be joined',
                body: 'The picker only offers a category once all three of these are true, which is why it can come up empty:',
                bullets: [
                    'The category has a **Location** field.',
                    'It has items placed on **this** apparatus.',
                    'It has a check of its own to run.'
                ],
                footer: 'When nothing qualifies, the button is disabled and names which of the three is missing rather than silently offering an empty list.'
            },
            {
                title: 'The gear keeps its own record',
                body: 'Items checked this way are checked in their own right — the result belongs to the gear item, not only to the truck check that prompted it.',
                footer: 'Which items are covered is worked out when the check runs, from where each one is placed. Moving an item onto or off the apparatus changes what the next check covers, with no edit to the check itself.'
            }
        ],
        cta: {label: 'Open Apparatus', to: '/apparatus'}
    },
    {
        id: 'guide-gear-move-items',
        kind: 'guide',
        category: 'gear',
        docType: 'how-to',
        title: 'Moving items to another category',
        summary: 'Items can be moved between categories in bulk when a category turns out to be the wrong shape, without losing what’s already recorded against them.',
        taskLabel: 'To move items',
        how: [
            'Open the category and select the items you want to move.',
            'In the actions bar, select **Move**.',
            'Pick the target category and confirm.'
        ],
        note: 'The move runs on the server so each item’s records, checks and tasks travel with it. Large selections are sent in batches, so a move of several hundred items reports progress rather than appearing to stall.',
        cta: {label: 'Open Gear', to: '/gear'}
    },
    {
        id: 'ref-gear-field-types',
        kind: 'guide',
        category: 'gear',
        docType: 'reference',
        title: 'Category field types',
        summary: 'The field types a gear category can use, and which ones do more than store a value.',
        sections: [
            {
                title: 'Types',
                bullets: [
                    '**Text** — free text. The category’s first field is always a text Name.',
                    '**Number** — a figure. Named quantity, count or amount, it becomes the category’s stock count.',
                    '**Yes/No** — a checkbox.',
                    '**Date** — a plain date, such as a manufacture date.',
                    '**Expiry date** — a date that generates expiry tasks as it approaches.',
                    '**Location** — places the item on an apparatus or at a location.',
                    '**Member** — assigns the item to a person.'
                ],
                footer: 'Any field can be **required**, or **unique** to stop two items sharing a value. Serial numbers are the usual case for unique.'
            }
        ],
        cta: {label: 'Open Gear', to: '/gear'}
    },
    {
        id: 'about-tasks',
        kind: 'guide',
        category: 'tasks',
        docType: 'explanation',
        title: 'About tasks',
        summary: 'A task is a piece of work assigned to a user with a due date. Every task hangs off something — an apparatus, a gear item or a user — which is how anyone reading it knows what it’s actually about.',
        sections: [
            {
                title: 'Where tasks come from',
                body: 'Most tasks aren’t typed in by hand. They arrive from work the system already knows is due:',
                bullets: [
                    'Created directly, when you want someone to do something.',
                    'From a **failed check item**, carrying the item, the apparatus and the note with it.',
                    'From **gear check frequencies**, when a recurring inspection comes due.',
                    'From **expiry dates**, for any gear category with an expiry field.'
                ],
                footer: 'A task is either **Open** or **Complete**. There’s no in-between state, so a task that’s been started still reads as open to everyone else.'
            },
            {
                title: 'Reminders',
                body: 'Reminders go out in three stages, each of which can be switched off:',
                bullets: [
                    '**Upcoming reminders** — before the due date.',
                    '**Due-date reminder** — on the day.',
                    '**Overdue reminders** — after it passes.'
                ],
                footer: 'Your department sets the defaults and the hour they send at, and a task can override both. Leave a task matching the defaults and it keeps following them, so changing the department setting later moves every task that hasn’t been deliberately customised. Reminders arrive inside the daily digest alongside other overdue work, not as one email per task.'
            }
        ],
        cta: {label: 'Go to Tasks', to: '/tasks'}
    },
    {
        id: 'guide-task-complete',
        kind: 'guide',
        category: 'tasks',
        docType: 'how-to',
        title: 'Completing a task',
        summary: 'Completing a task records who did it and what they found, not just that it’s done.',
        taskLabel: 'To complete a task',
        how: [
            'Open the task and select **Complete Task**.',
            'Add a **Note** describing what was done.',
            'Set **Completed By** if it wasn’t you.',
            'Attach any files worth keeping with the record.',
            'Select **Complete Task** to save it.'
        ],
        note: 'On a recurring task you’ll also see when the next one is due, with a **Skip** checkbox beside it. Skip ends the series rather than skipping the task in front of you — completing without it creates the next occurrence as normal.',
        cta: {label: 'Go to Tasks', to: '/tasks'}
    },
    {
        id: 'guide-task-recurring',
        kind: 'guide',
        category: 'tasks',
        docType: 'how-to',
        title: 'Setting a task to recur',
        summary: 'A recurring task creates its replacement when you complete it, so the next one is always dated from the work actually being done.',
        taskLabel: 'To make a task recurring',
        how: [
            'Open the task and turn on **Recurring**.',
            'Pick a **Frequency**.',
            'Save. Completing this task is what creates the next one.'
        ],
        note: 'The next occurrence is dated from this task’s **due date**, not from when you completed it, so finishing late doesn’t shift the schedule. A weekly task closed three days late is still due a week after the original date.',
        cta: {label: 'Go to Tasks', to: '/tasks'}
    },
    {
        id: 'ref-task-dates',
        kind: 'guide',
        category: 'tasks',
        docType: 'reference',
        title: 'Due dates and recurrence',
        summary: 'The date precisions a task can use, and the frequencies it can repeat on.',
        sections: [
            {
                title: 'Date type',
                body: 'A due date doesn’t have to be a specific day:',
                bullets: [
                    '**Day** — a precise date.',
                    '**Month** — due sometime that month.',
                    '**Year** — due sometime that year.'
                ],
                footer: 'Month and year suit things like certifications that expire in a period rather than on a date.'
            },
            {
                title: 'Frequencies',
                bullets: [
                    '**Weekly**',
                    '**Monthly**',
                    '**Quarterly**',
                    '**Semi-annually**',
                    '**Yearly**'
                ]
            }
        ],
        cta: {label: 'Go to Tasks', to: '/tasks'}
    },
    {
        id: 'guide-task-reminders',
        kind: 'guide',
        category: 'tasks',
        docType: 'how-to',
        title: 'Setting when task reminders go out',
        summary: 'Tasks chase themselves by email. What you choose here is how loudly.',
        taskLabel: 'To set up task reminders',
        how: [
            'Go to **Settings** > **Emails**.',
            'Set the **Reminder Time** — the hour each day that reminders are sent, in Pacific time.',
            'Turn on the heads-up emails to warn people 3 months, 1 month, 1 week and 1 day before a task is due.',
            'Turn on the due-day email, and the escalating overdue emails, to whatever suits your department.',
            'Save.'
        ],
        note: 'The three switches are independent, so a department that finds the long-range warnings noisy can keep just the due-day and overdue mail. Reminders arrive folded into the daily digest rather than as separate messages, so somebody with four due tasks gets one email.',
        cta: {label: 'Open Settings', to: '/settings/emails'}
    },
    {
        id: 'about-fuel',
        kind: 'guide',
        category: 'fuel',
        docType: 'explanation',
        title: 'About fuel tracking',
        summary: 'A tank is the unit of fuel tracking. Fuel goes in as deliveries and comes out as fill-ups, and the level you see is worked out from those movements rather than read off a sensor.',
        sections: [
            {
                title: 'Two movements, opposite directions',
                bullets: [
                    'A **delivery** puts fuel in: a supplier drop, with litres, cost and a receipt.',
                    'A **fill-up** takes fuel out: an apparatus, or anything else on site, drawing from the tank.'
                ],
                footer: 'A fill-up can record the dispenser’s meter before and after, so the litres you log can be reconciled against what the pump counted.'
            },
            {
                title: 'The level is derived, not measured',
                body: 'Nothing reads the tank. The current level is calculated by adding deliveries and subtracting fill-ups from a known starting point, which has two consequences worth knowing:',
                bullets: [
                    'A tank added without a starting level shows **unknown** rather than a number. That’s different from a known **0 L**, which means measured and empty.',
                    'A delivery marked **filled to capacity** re-establishes a known level, which is how an unknown tank becomes a tracked one.',
                    'The level gauge only appears if you gave the tank a **capacity** — without one there’s nothing to show a percentage against.'
                ]
            }
        ],
        cta: {label: 'Open Fuel', to: '/fuel'}
    },
    {
        id: 'guide-fuel-add-tank',
        kind: 'guide',
        category: 'fuel',
        docType: 'how-to',
        title: 'Adding a fuel tank',
        summary: 'A tank needs a name and a fuel type. Everything else shapes how much the app can tell you later.',
        taskLabel: 'To add a tank',
        how: [
            'In the sidebar, go to **Fuel** and select **Add Tank**.',
            'Give it a **Tank name** and pick a **Fuel type**.',
            'Set the **Station** it lives at, if you run more than one.',
            'Enter the **Starting meter reading** — the dispenser’s cumulative total as it reads today.',
            'Enter the **Capacity** in litres to turn on the level gauge.',
            'Select **Add Tank**.'
        ],
        note: 'Capacity is optional but does real work: without it there’s no percentage to draw, so the tank shows litres only and never warns you that it’s running low.',
        cta: {label: 'Open Fuel', to: '/fuel'}
    },
    {
        id: 'guide-fuel-fill-up',
        kind: 'guide',
        category: 'fuel',
        docType: 'how-to',
        title: 'Logging an apparatus fill-up from a tank',
        summary: 'Record fuel leaving a tank and which apparatus took it. Anything that isn’t a truck — a generator, a pump — is logged the same way under Other.',
        taskLabel: 'To log a fill-up',
        how: [
            'Open the tank and start a fill-up.',
            'Pick the **Apparatus**. For anything that isn’t a truck, choose Other and describe it under **Purpose** — a generator top-up, say.',
            'Enter the **Litres** and the **Date**.',
            'If you track the dispenser, enter **Meter before** and **Meter after**.',
            'Set **Filled up by**, then save.'
        ],
        cta: {label: 'Open Fuel', to: '/fuel'}
    },
    {
        id: 'guide-fuel-delivery',
        kind: 'guide',
        category: 'fuel',
        docType: 'how-to',
        title: 'Recording a fuel delivery to a tank',
        summary: 'Record fuel arriving from a supplier: how much, what it cost, and who took receipt of it.',
        taskLabel: 'To record a delivery',
        how: [
            'Open the tank and start a delivery.',
            'Enter the **Litres** and the **Date**.',
            'Tick **Filled tank to capacity** if the tank was topped right off.',
            'Add the **Supplier** and **Total $** if you’re tracking spend.',
            'Set **Received by**, attach the **Receipt**, then save.'
        ],
        note: '**Filled tank to capacity** is the one field that changes the maths rather than just recording it: it resets the tank to a known full level, which is how a tank whose level has drifted, or was never established, gets back to a trustworthy number.',
        cta: {label: 'Open Fuel', to: '/fuel'}
    },
    {
        id: 'ref-fuel-types',
        kind: 'guide',
        category: 'fuel',
        docType: 'reference',
        title: 'Fuel types and placards',
        summary: 'The two fuel types a tank can hold, and the transport placard each one carries.',
        sections: [
            {
                title: 'Types',
                bullets: [
                    '**Diesel** — shown with a yellow pump dot, following the usual pump convention.',
                    '**Gasoline** — shown with a green pump dot.'
                ]
            },
            {
                title: 'Placards',
                body: 'Each type maps to its TDG Class 3 (flammable liquid) UN number, which the app renders as a placard:',
                bullets: [
                    'Diesel — **UN 1202**',
                    'Gasoline — **UN 1203**'
                ]
            },
            {
                title: 'Level colours',
                body: 'The gauge and its chips read red, amber or green by how full the tank is, matching the Fuel Level field on a pre-trip so the same tank reads the same way in both places.'
            }
        ],
        cta: {label: 'Open Fuel', to: '/fuel'}
    },
    {
        id: 'guide-fuel-alerts',
        kind: 'guide',
        category: 'fuel',
        docType: 'how-to',
        title: 'Getting told when a tank runs low',
        summary: 'Set the level that counts as low and who hears about it, so nobody discovers an empty tank at 2am.',
        taskLabel: 'To set up low-fuel alerts',
        how: [
            'Go to **Settings** > **Fuel**.',
            'Turn on **Email alerts**.',
            'Add **Recipients** — members, or plain email addresses for people without an account.',
            'Set the **Low threshold** as a percentage of capacity.',
            'Save.'
        ],
        note: 'A tank has to have a **capacity** for this to work at all — without one there’s no percentage to compare against, so those tanks are skipped. An alert fires once and then stays quiet until the tank is refilled back above the threshold, so a tank sitting at 18% doesn’t mail you every day.',
        cta: {label: 'Open Settings', to: '/settings/fuel'}
    },
    {
        id: 'about-ohs',
        kind: 'guide',
        category: 'ohs',
        docType: 'explanation',
        title: 'About OH&S meetings',
        summary: 'Each hall holds a health and safety meeting every month. The OH&S Meetings page is where it’s recorded: who came, what was discussed, the hazards raised, the action items agreed and the minutes.',
        sections: [
            {
                title: 'The year at a glance',
                bullets: [
                    'The cards across the top show each month: **Held** with its date, **Missed**, **Due** for this month, or still to come.',
                    'A held month opens its meeting; any other month starts recording one for it.'
                ]
            },
            {
                title: 'Recording a meeting',
                bullets: [
                    'The meeting is titled by its month, so there’s no title to type.',
                    'The previous meeting’s action items, hazards and notes sit beside the form, so open items can be followed up.',
                    'Attendance counts as **practice hours** in payroll and shows in each member’s history, like any training.'
                ]
            }
        ],
        cta: {label: 'Open OH&S Meetings', to: '/ohs'}
    },
    {
        id: 'guide-ohs-reminders',
        kind: 'guide',
        category: 'ohs',
        docType: 'how-to',
        title: 'Choosing who is reminded about the OH&S meeting',
        summary: 'Pick who gets the monthly reminder, so the meeting is held and recorded every month.',
        taskLabel: 'To choose the OH&S designees',
        how: [
            'Go to **Settings** > **OH&S Meetings**.',
            'Add the **Designees** — usually the safety officer or committee chair.',
            'Save.'
        ],
        note: 'With nobody picked, the Chief is reminded. Designees get this month’s meeting as a task on their dashboard until it’s recorded, and get an email on the 1st and again a week before the month ends if it still isn’t.',
        cta: {label: 'Open Settings', to: '/settings/ohs'}
    },
    {
        id: 'about-incidents',
        kind: 'guide',
        category: 'incidents',
        docType: 'explanation',
        title: 'About incidents',
        summary: 'An incident is the record of a call: its times, type, location, who responded and what happened. Records move through a review workflow rather than staying editable forever, so a finished report is one nobody can quietly change.',
        sections: [
            {
                title: 'How incidents arrive',
                bullets: [
                    'Logged by hand, when someone writes the call up from scratch.',
                    'Created from a dispatch email, or pulled in from your incident provider, which lands the record as **Pending Import** for someone to complete.'
                ],
                footer: 'A **Pending Import** incident carries a banner saying it was imported and needs review, and becomes **In Progress** the first time it’s saved — so that state only ever means “arrived, not yet touched”.'
            },
            {
                title: 'The review workflow',
                body: 'A record moves in one direction until it’s approved, and only an approver can move it back:',
                bullets: [
                    '**In Progress** — being written. Anyone who can edit incidents can work on it.',
                    '**Awaiting Review** — submitted, optionally to a named reviewer. The fields are read-only to everyone except an approver.',
                    '**Approved & Locked** — final. The report stands as the department’s record of the call.',
                    '**Rejected** — sent back with a reason, editable again so it can be corrected and resubmitted.'
                ],
                footer: 'Approving, rejecting and unlocking all need permission to approve incidents. Submitting doesn’t, so anyone writing a report can hand it on but nobody signs off their own work.'
            },
            {
                title: 'Only approved calls count',
                body: 'Incident statistics, the Incident Summary report, the dashboard and every per-member incident total are built from **Approved & Locked** records only.',
                footer: 'A call that’s still In Progress or Awaiting Review is deliberately left out — it’s still being documented, and counting it would make the numbers move under you. If a month looks light in reporting, the usual reason is reports that were never approved.'
            }
        ],
        cta: {label: 'Go to Incidents', to: '/incidents'}
    },
    {
        id: 'guide-log-incident',
        kind: 'guide',
        category: 'incidents',
        docType: 'how-to',
        orderIn: {incidents: -9},
        title: 'Logging an incident',
        summary: 'Write up a call: its number and type, the times, where it was, and what happened.',
        taskLabel: 'To log an incident',
        how: [
            'In the sidebar, go to **Incidents** and select **Add Incident**.',
            'Under **Identity**, enter the **Incident #**, the **Date** and the **Type**.',
            'Fill in the **Times** along the timeline — **Dispatch**, **En Route**, **Arrival**, **Scene Cleared**, **Back In Service**.',
            'Set the **Location**. It autocompletes against a map, so the incident plots where it happened.',
            'Under **Summary**, name the **Officer In Charge** and write the narrative.',
            'Record who responded on the **Responses** tab, and attach anything you have on the **Files** tab.',
            'Select **Save Incident**.'
        ],
        note: 'The **Incident #** isn’t required to save — a call imported from a provider arrives before the number is known. It is required to submit the report for review, which is where it actually has to exist.',
        sections: [
            {
                title: 'The times drive the hours',
                body: 'Hours are calculated from **Dispatch Time** until **Back In Service**, falling back to **Scene Cleared** when Back In Service is blank. A caption under the times says what the resulting default is, so you can see the number before anyone is paid it.',
                footer: 'If your department sets a minimum, that applies instead whenever it’s the larger of the two.'
            },
            {
                title: 'Saving isn’t finishing',
                body: 'Saving leaves the report **In Progress** and fully editable. It’s the **Submit for Review** step that hands it on — and until a report is approved, it doesn’t appear in statistics or reports.'
            }
        ],
        cta: {label: 'Go to Incidents', to: '/incidents'}
    },
    {
        id: 'guide-incident-responses',
        kind: 'guide',
        category: 'incidents',
        docType: 'how-to',
        orderIn: {incidents: -8},
        title: 'Recording who responded',
        summary: 'Attendance is recorded per apparatus rather than as one long list, so the record shows who rode which rig — and separates the people who stood by or were cancelled from the people who turned out.',
        taskLabel: 'To record responses',
        how: [
            'Open the incident and go to the **Responses** tab.',
            'Add the **Apparatus** that responded. Each one gets its own assignment card below.',
            'On each card, assign the members who rode that rig, and set the rig’s own times if they differ from the incident’s.',
            'Put anyone who attended without an apparatus under **Unassigned Members**.',
            'Use **Standby** for members held at the hall, and **Cancelled** for members stood down before they arrived.',
            'Save the incident.'
        ],
        note: 'A member can only appear once — somebody already assigned to an apparatus is filtered out of the other pickers, so the same person can’t be counted twice on one call.',
        sections: [
            {
                title: 'Standby and cancelled aren’t the same as responding',
                body: 'The three lists are paid differently. Standby members are credited the department’s standby hours instead of the call’s duration, and the incident minimum doesn’t apply to them.',
                footer: 'Getting the list right is what makes payroll right — nothing re-enters these hours by hand later.'
            },
            {
                title: 'When there are several rigs',
                body: 'Apparatus cards start expanded. With more than one on the call, **Collapse all** and **Expand all** appear at the top of the section so you can work through them one at a time.'
            }
        ],
        cta: {label: 'Go to Incidents', to: '/incidents'}
    },
    {
        id: 'guide-incident-hours',
        kind: 'guide',
        category: 'incidents',
        docType: 'how-to',
        orderIn: {incidents: -7},
        title: 'Adjusting a member’s hours on a call',
        summary: 'Everyone on the call is credited the same default. Where somebody was there longer or shorter, override just that person rather than changing the call.',
        taskLabel: 'To adjust hours',
        how: [
            'Open the incident and go to the **Hours** tab.',
            'Read the caption at the top — it says what the default is and which rule produced it.',
            'Leave a member’s **Hours** blank to credit them the default, or type a number to override just them.',
            'Members on standby are listed in their own **Standby** table below, with their own default.',
            'Save the incident.'
        ],
        note: 'The blank cell isn’t zero — it’s the default shown in the caption. Clearing an override puts a member back on the default rather than crediting them nothing.',
        sections: [
            {
                title: 'Where the default comes from',
                body: 'Dispatch Time to Back In Service (or Scene Cleared, if Back In Service is blank), or the department minimum where that’s larger. Standby members are credited the standby hours instead, and the minimum never applies to them.',
                footer: 'Both numbers are set in **Settings** > **Incidents**.'
            }
        ],
        cta: {label: 'Go to Incidents', to: '/incidents'}
    },
    {
        id: 'guide-incident-exposures',
        kind: 'guide',
        category: 'incidents',
        docType: 'how-to',
        orderIn: {incidents: -6},
        title: 'Recording exposures',
        summary: 'What a member was exposed to on a call, recorded against them at the time rather than reconstructed years later.',
        taskLabel: 'To record exposures',
        how: [
            'Open the incident and go to the **Exposures** tab.',
            'Beside each member, describe what they were exposed to.',
            'Enter the number of **SCBA Bottles** they went through, if any.',
            'Save the incident.'
        ],
        note: 'The tab only appears once the incident has members on it, so record responses first. Exposure notes are part of the locked report once it’s approved — which is the point of writing them down at the time.',
        cta: {label: 'Go to Incidents', to: '/incidents'}
    },
    {
        id: 'guide-incident-submit',
        kind: 'guide',
        category: 'incidents',
        docType: 'how-to',
        orderIn: {incidents: -5},
        title: 'Submitting an incident for review',
        summary: 'Hand a finished write-up to whoever approves reports, optionally naming who should look at it.',
        taskLabel: 'To submit an incident',
        how: [
            'Open the incident and select **Submit for Review** at the top right.',
            'Name a reviewer if you want it to land with someone in particular.',
            'Add a note if there’s anything they should know.',
            'Select **Submit for Review** to confirm.'
        ],
        note: 'Submitting needs no special permission — anyone who can edit the incident can send it on. It saves the incident first, so there’s no separate save step. A rejected incident goes back the same way once it’s been corrected.',
        sections: [
            {
                title: 'An incident number is required',
                body: 'The report can be saved without one, but not submitted. If the **Incident #** is blank, submitting drops you back on the **Details** tab with the field flagged.',
                footer: 'Rejecting is exempt from this — an approver handing a report back shouldn’t be blocked by the very thing they’re sending it back to fix.'
            },
            {
                title: 'It stops being editable',
                body: 'Once submitted, the fields are read-only to everyone except an approver. If you need to change something, it has to come back — either an approver rejects it, or they unlock it after approving.'
            }
        ],
        cta: {label: 'Go to Incidents', to: '/incidents'}
    },
    {
        id: 'guide-incident-approve',
        kind: 'guide',
        category: 'incidents',
        docType: 'how-to',
        orderIn: {incidents: -4},
        title: 'Approving, rejecting or unlocking an incident',
        summary: 'The approver’s side of the workflow: accept a report as final, send it back, or reopen one that was already locked.',
        taskLabel: 'To review a submitted incident',
        how: [
            'Open an incident that’s **Awaiting Review**.',
            'Select **Approve & Lock** to accept it as the department’s record of the call.',
            'Or select **Reject**, and give a reason — the reason is required, and it’s what the writer sees.',
            'To reopen a report that’s already locked, select **Unlock**.'
        ],
        note: 'All three need permission to approve incidents. Without it, a submitted incident opens read-only with no review buttons on it.',
        sections: [
            {
                title: 'Approving is what puts a call into reporting',
                body: 'Statistics, the Incident Summary report and every per-member incident total count **Approved & Locked** records only. A report that’s never approved is invisible to all of them.',
                footer: 'So approval isn’t a formality at the end — it’s the step that makes the call count.'
            },
            {
                title: 'Unlocking leaves a trail',
                body: 'Every status change is recorded with who made it, when, and any note they left. The incident’s **History** tab is where that reads back, so a report that was reopened after approval says so.'
            }
        ],
        cta: {label: 'Go to Incidents', to: '/incidents'}
    },
    {
        id: 'guide-incident-import-wr',
        kind: 'guide',
        category: 'incidents',
        docType: 'how-to',
        orderIn: {incidents: -2},
        title: 'Importing calls from Who’s Responding',
        summary: 'Calls arrive on their own every few minutes, but a date range can be pulled in by hand when you’re catching up or backfilling.',
        taskLabel: 'To import calls',
        how: [
            'In the sidebar, go to **Incidents**.',
            'Select **Import from Who’s Responding**.',
            'Set the **Start Date** and **End Date** to cover the calls you want.',
            'Select **Start Import**, then pick which of the calls it found to bring in.'
        ],
        note: 'The button only appears for departments running Who’s Responding, and only for members who can edit incidents. Imported calls land as **Pending Import** — they still need someone to complete and submit them.',
        sections: [
            {
                title: 'Linking a call you already wrote up',
                body: 'If the incident was logged by hand before the import ran, open it and select **Connect to Who’s Responding**. That attaches the provider’s callout — and its responses — to the record you already have, rather than leaving a duplicate.'
            }
        ],
        cta: {label: 'Go to Incidents', to: '/incidents'}
    },
    {
        id: 'guide-incident-import-iar',
        kind: 'guide',
        category: 'incidents',
        docType: 'how-to',
        orderIn: {incidents: -1},
        title: 'Importing responses from IamResponding',
        summary: 'Calls and responses arrive on their own every few minutes. This is how to pull responses onto a call by hand — to widen the time range, or to fill in one the sweep missed.',
        taskLabel: 'To import responses',
        how: [
            'Open the incident and go to the **Responses** tab.',
            'Select **Import Responses from IaR**.',
            'Check the **Date Range**. It’s prefilled from the call’s dispatch time and scene cleared time.',
            'Tick **Include 2 hours before/after** to catch early and late arrivals, if your department allows it.',
            'Select **Start Import**, then choose which responses to apply.'
        ],
        note: 'You don’t have to do this routinely — IamResponding is swept automatically every few minutes, which creates the call and fills in its responders. Use this when you want a different time range, or for a call that came in another way. For calls from before you connected IamResponding, use **Import past calls** on Settings > Integrations. The controls only appear on departments running it as their provider.',
        cta: {label: 'Go to Incidents', to: '/incidents'}
    },
    {
        id: 'guide-incident-provider',
        kind: 'guide',
        category: 'incidents',
        docType: 'how-to',
        orderIn: {incidents: -3},
        title: 'Connecting an incident provider',
        summary: 'Point Ember Tracking at the service your department already uses to alert and track responders, so calls and responses arrive without being typed twice.',
        taskLabel: 'To connect an incident provider',
        how: [
            'Go to **Settings** > **Integrations**.',
            'Set the **Incident provider** to **IamResponding**, **Who’s Responding** or **Active911**. You can only run one.',
            'Enter that provider’s credentials.',
            'Save.'
        ],
        note: 'Changing the provider can take up to five minutes to take effect. The scheduler caches which departments have Who’s Responding switched on, and that cache is what decides whether the next sweep runs for you.',
        cta: {label: 'Open Settings', to: '/settings/integrations'}
    },
    {
        id: 'guide-incident-fields',
        kind: 'guide',
        category: 'incidents',
        docType: 'how-to',
        orderIn: {incidents: 1},
        title: 'Adding your own fields to an incident',
        summary: 'Fields are set up in groups, and a group is shown only for the incident types it applies to — so a hazmat call can ask for what a hazmat call needs without every alarm call carrying the same blanks.',
        taskLabel: 'To add fields to an incident',
        how: [
            'Go to **Settings** > **Incidents**, and find **Additional fields**.',
            'Select **Add Fields**. Once you have a group, the same button reads **Add New Field Group**.',
            'Enter a **Grouping Name** — this is the heading the fields appear under.',
            'Choose the **Incident Types** the group applies to. Leave it blank and it shows on every incident.',
            'Select **Add Field**, name it, and pick its type from the list beside the name.',
            'Select **Close**, then save the settings.'
        ],
        note: 'A group with no incident types shows on everything, which is usually not what you want — **All** on the group’s chip is the giveaway.',
        sections: [
            {
                title: 'Field types',
                bullets: [
                    '**Text** · **Phone** · **Date** · **Time** · **Checkbox**',
                    '**Select** — a list you define. The options are entered on the field itself.',
                    '**User** — picks a member from your roster.',
                    '**Location** — an address, autocompleted against the map.'
                ],
                footer: 'What a field offers depends on its type: **Text** can be **Multiline**, **User** can **Allow Multiple**, and everything except a checkbox can be marked **Required**.'
            },
            {
                title: 'Giving a group its own tab',
                body: 'Tick **Show as tab** and the group leaves the **Additional Information** tab for one of its own, named after the group.',
                footer: 'Worth doing once a group holds more than a few fields. Anything left untabbed stacks up under Additional Information.'
            },
            {
                title: 'Order is what people read',
                body: 'Drag a group by its handle to move it among the others, and drag a field within a group. The order here is the order they appear on the incident, so put what gets filled in most at the top.'
            }
        ],
        cta: {label: 'Open Settings', to: '/settings/incidents'}
    },
    {
        id: 'ref-incident-tabs',
        kind: 'guide',
        category: 'incidents',
        docType: 'reference',
        title: 'What’s on an incident',
        summary: 'The tabs across an incident, and what each one holds. Several only appear once there’s something to put on them.',
        sections: [
            {
                title: 'Always there',
                bullets: [
                    '**Details** — the incident number, date, type, the five times, location and narrative.',
                    '**Additional Information** — your department’s own fields, plus the CISM follow-up flag.',
                    '**Responses** — apparatus, who rode each one, and the standby and cancelled lists.'
                ]
            },
            {
                title: 'Once there’s something to show',
                bullets: [
                    '**Files** — attachments. Attach a recognised dispatch report and its times are offered for review straight away. If your department has chosen its dispatch report in **Settings** > **Incidents**, an open incident asks for it until its times are filled from it.',
                    '**Hours** and **Exposures** — appear once the incident has members on it.',
                    '**Forms** — appears on a saved incident when a form pack applies to it.',
                    '**Dispatch Notes** — the notes that came with the call, where it arrived from a provider or a dispatch email.',
                    '**History** — every change to the record: created, updated, and each status change with its note.'
                ],
                footer: 'A field category set up in **Settings** > **Incidents** can also claim a tab of its own, which sits alongside these.'
            }
        ],
        cta: {label: 'Go to Incidents', to: '/incidents'}
    },
    {
        id: 'ref-incident-statuses',
        kind: 'guide',
        category: 'incidents',
        docType: 'reference',
        title: 'Incident statuses',
        summary: 'The five states an incident can hold, and who can move it between them.',
        sections: [
            {
                title: 'Statuses',
                bullets: [
                    '**Pending Import** — arrived from an import or a dispatch email, not yet touched.',
                    '**In Progress** — being written, and editable.',
                    '**Awaiting Review** — submitted, waiting on an approver. Read-only to everyone else.',
                    '**Rejected** — sent back with a reason, editable again.',
                    '**Approved & Locked** — final, and the only status that counts toward statistics and reports.'
                ]
            },
            {
                title: 'Who can move it',
                bullets: [
                    'In Progress, Pending Import or Rejected → **Awaiting Review**: anyone who can edit the incident.',
                    'Awaiting Review → **Approved & Locked** or **Rejected**: approvers only.',
                    'Approved & Locked → **In Progress**: approvers only.'
                ],
                footer: 'An **Incident #** has to exist before a report can be submitted or approved. Rejecting is exempt.'
            }
        ],
        cta: {label: 'Go to Incidents', to: '/incidents'}
    },
    {
        id: 'ref-incident-types',
        kind: 'guide',
        category: 'incidents',
        docType: 'reference',
        title: 'Incident types',
        summary: 'The call types an incident can be recorded as.',
        sections: [
            {
                title: 'Types',
                bullets: [
                    '**Structure Fire** · **Wildland Fire** · **Vehicle Fire** · **Alarm**',
                    '**Motor Vehicle Incident** · **Rescue** · **Hazmat** · **Line Down**',
                    '**First Medical Response** · **Assist** · **Mutual Aid**',
                    '**Burning Complaint** · **Recheck** · **Service Call** · **Public Service**',
                    '**Unknown** — for a call that can’t be classified.'
                ],
                footer: 'Type drives how calls group in reporting, and which of your additional fields and forms apply — so it’s worth setting even on a record that’s otherwise thin.'
            }
        ],
        cta: {label: 'Go to Incidents', to: '/incidents'}
    },
    {
        id: 'ref-incident-providers',
        kind: 'guide',
        category: 'incidents',
        docType: 'reference',
        title: 'Incident providers',
        summary: 'The services Ember Tracking can pull from, what each needs, and the different way each one brings data in.',
        sections: [
            {
                title: 'Who’s Responding',
                body: 'Needs your **Subdomain** (the myagency part of myagency.ims-login.com), **Username** and **Password**.',
                bullets: [
                    'Sweeps on its own roughly every five minutes.',
                    'Creates **incidents**, which land as **Pending Import** waiting for someone to complete them.',
                    'A date range can also be imported by hand from **Settings > Integrations**, for backfilling. You pick which of the calls it finds to keep.'
                ],
                footer: 'This is where most Pending Import records come from — nobody has to go and fetch them.'
            },
            {
                title: 'IamResponding',
                body: 'Needs your **IaR Agency**, **Username** and **Password**.',
                bullets: [
                    'Sweeps on its own roughly every five minutes.',
                    'Creates **incidents** from the dispatch report as **Pending Import**, then fills in who responded to each.',
                    'Responses can also be pulled onto a single call by hand from its **Responses** tab, or onto a training record from its **Attendance** tab.',
                    'A date range can be imported from **Settings > Integrations**, for calls from before you connected it.'
                ],
                footer: 'It used to be pull-only, and older notes may still say so. Since the dispatch report is polled, it brings calls to you the same way the others do.'
            },
            {
                title: 'Active911',
                body: 'Needs the **Username** and **Password** your department already uses to sign in to Active911. Read-only — Ember never changes anything there.',
                bullets: [
                    'Sweeps on its own roughly every five minutes.',
                    'Creates **incidents** as **Pending Import**, and fills in who responded from the same alert.',
                    'Keeps checking a recently imported call, because members acknowledge over the minutes after a page and arrive later still.',
                    'A date range can be imported by hand from **Settings > Integrations**, for calls from before you connected it.'
                ],
                footer: '**Responding** and **Arriving** count as attendance, and **Available** lands on standby. **Watch** doesn’t count either way — it’s recorded automatically when a member opens the app, so it means they looked, not that they’re coming. Members are matched to their Active911 devices by name the first time, then by the device itself, so renaming a device later doesn’t lose anyone. A response that matches nobody is still shown on the incident rather than dropped.'
            },
            {
                title: 'Also on this page',
                body: 'A **Streaming link** sits alongside the provider settings. It has nothing to do with importing — set it and a link to your radio stream appears in the menu.'
            }
        ],
        cta: {label: 'Open Settings', to: '/settings/integrations'}
    },
    {
        id: 'ref-incident-settings',
        kind: 'guide',
        category: 'incidents',
        docType: 'reference',
        title: 'Incident settings',
        summary: 'What **Settings** > **Incidents** decides — most of it about how a call turns into paid time.',
        sections: [
            {
                title: 'Response pay',
                bullets: [
                    '**Minimum hours** — the least a member who responded is paid, however short the call.',
                    '**Standby hours** — the paid time for a member placed on standby, used instead of the call’s duration.'
                ],
                footer: 'A member is paid under one of these, never both: the minimum doesn’t apply to somebody on standby, and standby hours don’t apply to somebody who responded.'
            },
            {
                title: 'Statistics',
                body: 'A **Start year** sets where incident statistics begin, so a department that imported a decade of history can report from the year its records are actually trustworthy.'
            },
            {
                title: 'Additional fields',
                body: 'Your own fields, grouped, and shown only for the incident types they apply to — so a hazmat call can ask for what a hazmat call needs without every alarm call carrying the same blanks.',
                footer: 'A group can be shown as its own tab on the incident, which is worth doing once it holds more than a few fields.'
            }
        ],
        cta: {label: 'Open Settings', to: '/settings/incidents'}
    },
    {
        id: 'about-training',
        kind: 'guide',
        category: 'training',
        docType: 'explanation',
        title: 'About training records',
        summary: 'A training record brings a drill, course or meeting together: what was covered, when it ran, who attended and which competencies each member completed. Save the record to use that information in attendance statistics, payroll and the Training Matrix.',
        sections: [
            {
                title: 'One record, three answers',
                body: 'A session gets written up once, and three different questions are answered from it:',
                bullets: [
                    '**Who turned out** — the attendance lists feed the practice-attendance statistics and the member performance review.',
                    '**How long they were there** — hours flow into payroll for everyone on the Members list.',
                    '**What they completed** — competencies ticked off for each member can be verified in the Training Matrix.'
                ],
                footer: 'Record attendance and competencies separately: adding someone to the Members list does not tick off their competencies.'
            },
            {
                title: 'Hours come off the clock',
                body: 'The **Hours** field on Details can’t be typed into. It’s the gap between start and end time, rounded **up** to the next quarter hour, which has two consequences:',
                bullets: [
                    'A session with no end time records **0 hours**, for everyone on it.',
                    'To pay one member differently from the rest, override them on the **Hours** tab rather than changing the times.'
                ]
            },
            {
                title: 'Attendance is counted by night, not by record',
                body: 'The Practice Attendance statistics work in practice **nights** — the distinct days a Practice ran — not in records:',
                bullets: [
                    'Two drills filed on one evening are one practice night, so splitting a night across two halls doesn’t halve everybody’s percentage.',
                    'Instructing counts as attending. An officer who ran the drill was there.',
                    'A **Course (Internal)** on a practice night counts as having made that night.'
                ],
                footer: 'Only firefighter-rank, unarchived members appear in the table, so an admin account never drags the department average down.'
            }
        ],
        cta: {label: 'Go to Training Records', to: '/training'}
    },
    {
        id: 'about-training-competencies',
        kind: 'guide',
        category: 'training',
        docType: 'explanation',
        title: 'What are competencies?',
        summary: 'A competency is a skill or area of knowledge a firefighter needs to perform their role. On a training record, competencies describe what each member completed — for example, ground ladders, water supply or using PPE and SCBA.',
        sections: [
            {
                title: 'Based on B.C.’s minimum training standards',
                body: 'The **OFC Minimum Standards** competencies are based on the **British Columbia Structure Firefighter Minimum Training Standards**, published by the **Office of the Fire Commissioner (OFC)**. The standards set out competencies for Exterior, Interior and Full-Service operations, along with functions such as apparatus driver/operator and team leader.',
                footer: 'The official manual links those competencies to the relevant NFPA standards and other safety requirements. Use the PDF link below to read the source document.'
            },
            {
                title: 'The requirements behind a competency',
                body: 'Each OFC competency connects the training topic to its linked requirements. These include **NFPA Job Performance Requirements (JPRs)** and, where applicable, **WorkSafeBC** references and other requirements.',
                bullets: [
                    '**JPRs** describe the specific tasks and performance requirements behind a competency, so instructors can review what the training needs to cover.',
                    '**WorkSafeBC references** connect the topic to occupational health and safety requirements, including **Part 31 — Firefighting**. The references vary by competency.'
                ],
                footer: 'For example, the **Ground Ladders** competency brings together its linked NFPA requirements and the relevant WorkSafeBC ladder reference.'
            },
            {
                title: 'From a training session to the matrix',
                body: 'On a training record, open **Competencies** and tick the competencies each member completed. The record’s **JPRs** tab shows the linked requirements for the OFC competencies recorded. Save the record, then use the **Training Matrix** to review that member’s recorded training and the requirements behind it.',
                footer: 'Attendance and competency completion are recorded separately. See [[guide-training-competencies]] for the sign-off steps and [[guide-training-matrix]] for verification. Technical training categories let you track additional topics alongside the OFC competencies.'
            }
        ],
        cta: {
            label: 'Read the BC OFC standards (PDF)',
            to: 'https://www2.gov.bc.ca/assets/gov/public-safety-and-emergency-services/public-safety/fire-safety/training/minimum_training_standards_structural_ff_manual.pdf'
        }
    },
    {
        id: 'guide-log-training',
        kind: 'guide',
        category: 'training',
        docType: 'how-to',
        title: 'Adding a training record',
        summary: 'Start with the session details, then record attendance and competencies on their own tabs. You can complete the tabs before saving or return to the record later.',
        taskLabel: 'To add a training record',
        how: [
            'In the sidebar, go to **Training Records** and select **Add Training**.',
            'On **Details**, enter a **Title** and **Date**. Set or check the **Start Time** and **End Time**, including any times filled in from your department’s defaults.',
            'Choose the **Type**. A **Discipline** is required for Practice, Extra Practice and both Course types.',
            'Use **Summary** to describe what was covered, select the **Instructors**, and add any detail or follow-ups under **Notes**.',
            'Go to **Attendance** to add the members who attended, then **Competencies** to record what each member completed.',
            'Select **Save Training**. You’ll return to Training Records; open the record again whenever you need to update it.'
        ],
        note: '**Hours** is calculated from the start and end times, rounded up to the next quarter hour. Use the **Hours** tab for someone who attended for a different length of time.',
        cta: {label: 'Add a training record', to: '/training/add'}
    },
    {
        id: 'guide-training-attendance',
        kind: 'guide',
        category: 'training',
        docType: 'how-to',
        title: 'Recording training attendance',
        summary: 'The Attendance tab is a roll call of every firefighter, by rank. Mark who attended, excuse anyone who let you know they couldn’t make it, and any firefighter left over is recorded as AWOL.',
        taskLabel: 'To record attendance',
        how: [
            'Open the training record and go to the **Attendance** tab.',
            'Tap the name of everyone who came to mark them **Attended**. Tap again to undo. Instructors whose hours or competencies you need to record must be marked here too; selecting them under **Instructors** on Details does not add them.',
            'Select **Excuse** beside anyone who let you know they couldn’t make it to mark them **Absent**. Select it again to clear the excuse.',
            'Select **Save Training**.'
        ],
        note: 'Only firefighter ranks are listed; employees and administrators aren’t. Anyone already recorded on an overlapping training record is listed so you can mark them but isn’t counted AWOL; fill in the session’s date and times first so overlaps are found. The totals at the top update as you go. Archived members, and anyone else saved on an older record or brought in by an import, keep their tile, with a **×** to take them off the record.',
        cta: {label: 'Go to Training Records', to: '/training'}
    },
    {
        id: 'guide-training-competencies',
        kind: 'guide',
        category: 'training',
        docType: 'how-to',
        title: 'Recording completed competencies',
        summary: 'Tick the competencies each member actually completed during the session. Once saved, those ticks provide the record you can verify through the Training Matrix.',
        taskLabel: 'To sign off competencies',
        how: [
            'Add attendees under **Members** on the **Attendance** tab, then open **Competencies**. Each attendee has a column; instructors included in Members appear first.',
            'Select **Add Competency** and search for the competency covered. You can also use **Guess Competencies** to suggest rows from the session details.',
            'Tick each member who completed the competency. The double-check icon selects everyone on a row; selecting it again when everyone is ticked clears that row’s ticks.',
            'Review each row and member, then select **Save Training**.'
        ],
        note: 'If a competency is missing from the list, you can type a custom entry. Use a listed competency when it matches the session so it can appear in the corresponding Training Matrix column.',
        sections: [
            {
                title: 'Viewing the linked JPRs',
                body: 'JPRs are **Job Performance Requirements**: the detailed requirements linked to a competency. Adding an OFC competency with linked requirements makes the **JPRs** tab available on the training record. When recording completed competencies, use this tab to see the requirements behind them.',
                bullets: [
                    'Open **JPRs** and expand a row, or select **Expand All**, to read the requirements grouped by standard and section.',
                    'The tab shows the requirements linked to the competency rows. Record each member’s completion using the ticks on **Competencies**.',
                    'Technical and custom competencies may have no linked JPRs. If none of the record’s competencies have linked requirements, the JPRs tab is hidden.'
                ]
            }
        ],
        cta: {label: 'Go to Training Records', to: '/training'}
    },
    {
        id: 'guide-training-guess-competencies',
        kind: 'guide',
        category: 'training',
        docType: 'how-to',
        title: 'Guessing competencies from a training record',
        summary: 'Guess Competencies uses AI to suggest competencies from the title, summary, notes and linked lesson plan. It gives you a starting point to review against what actually happened.',
        taskLabel: 'To guess competencies',
        how: [
            'On **Details**, describe the activities in **Summary** and **Notes**. For example, “Crews practised ladder placement, raising and lowering, then climbing with tools” gives more detail than “Ladder practice”. Link a **Lesson Plan** if you used one.',
            'Add attendees under **Members** on **Attendance** before guessing, so they can be included in the suggested rows.',
            'Open **Competencies**, select **Guess Competencies**, and wait for the suggestions.',
            'Review the added rows. New suggestions are ticked for every member currently on the Members list. Clear ticks for anyone who did not complete an activity, remove unsuitable rows, and use **Add Competency** for anything missing.',
            'If you add attendees afterwards, review their ticks too. Select **Save Training** when the grid matches the session.'
        ],
        note: 'The button is available once you have a title, summary, notes or linked lesson plan. Guessing adds new suggestions without replacing existing rows or ticks. If nothing is added, give the session more detail or choose competencies manually.',
        cta: {label: 'Go to Training Records', to: '/training'}
    },
    {
        id: 'guide-training-matrix',
        kind: 'guide',
        category: 'training',
        docType: 'how-to',
        title: 'Verifying competencies in the Training Matrix',
        summary: 'The Training Matrix brings recorded competencies together by member. Its OFC Minimum Standards view is based on the B.C. Office of the Fire Commissioner’s Structure Firefighter Minimum Training Standards, with competencies linked to the relevant requirements.',
        taskLabel: 'To verify a recorded competency',
        how: [
            'On the training record, include the member under **Members**, tick their completed competency on **Competencies**, and select **Save Training**.',
            'Open **Training Matrix** from the sidebar. Set the date range to include the session; the matrix initially shows the start of this year through today.',
            'Choose **OFC Minimum Standards**, or the relevant technical category, and find the member’s row and competency column.',
            'Select the competency cell to inspect its details. For an OFC competency, the **JPRs** tab shows the linked requirements and their completion within the selected period.',
            'Open **Training Records** in the details to see the sessions where that member’s competency was ticked. With permission to edit training, select a record to open it and verify the entry.'
        ],
        note: 'Matrix completion reflects the selected date range. The detail’s Training Records list and Last Date show the member’s history across all dates, so an older session can appear there even when the matrix cell is incomplete for this year.',
        sections: [
            {
                title: 'Training coverage and qualifications',
                body: 'The Training Matrix helps you trace completed training back to the sessions where it was recorded. Formal qualifications and certificates are maintained separately in the **Qualification Matrix**; ticking a competency on a training record does not award a qualification.'
            }
        ],
        cta: {label: 'Open Training Matrix', to: '/matrix/training'}
    },
    {
        id: 'guide-training-lesson-plan',
        kind: 'guide',
        category: 'training',
        docType: 'how-to',
        title: 'Linking a lesson plan to a training',
        summary: 'Attach the plan the session was taught from, and the competencies it covers come with it.',
        taskLabel: 'To link a lesson plan',
        how: [
            'Open the training record and go to the **Lesson Plan** tab.',
            'Select **Select existing lesson plan**, search by name or description, then select **Use this plan**. You can also choose **Create new lesson plan** or **Import** to prepare a plan for the session.',
            'Add attendees under **Members** on **Attendance**, then open **Competencies**. The plan’s competencies appear under **From Lesson Plan**; tick the members who completed each one.',
            'Review any linked requirements on **JPRs**, then select **Save Training**.'
        ],
        note: 'A training can link more than one plan — once one is linked, select **Add existing lesson plan**, **Create another** or **Import** beneath it. Inherited competency rows cannot be renamed or removed while they belong to a linked plan. Editing the shared plan affects every record using it; duplicate the plan for a session-specific version. On an existing training record, changing the plan links saves immediately. Attendance and competency ticks still need **Save Training**.',
        cta: {label: 'Go to Training Records', to: '/training'}
    },
    {
        id: 'guide-training-hours',
        kind: 'guide',
        category: 'training',
        docType: 'how-to',
        title: 'Giving one member different hours',
        summary: 'Everyone on a record gets the session’s hours. Someone who came late, left early or stayed behind can be adjusted on their own.',
        taskLabel: 'To adjust a member’s hours',
        how: [
            'Open the training record and go to the **Hours** tab.',
            'Find the member and nudge their hours up or down with the arrows.',
            'Clear the box to put them back on the session’s hours.',
            'Save.'
        ],
        note: 'A blank box means that member is on the session default, which is what most rows should look like. Note that payroll pays the **Members** list — an instructor who should be paid for running the session needs to be under Members too, not just Instructors.',
        cta: {label: 'Go to Training Records', to: '/training'}
    },
    {
        id: 'guide-training-suggestions',
        kind: 'guide',
        category: 'training',
        docType: 'how-to',
        title: 'Planning training around your competency gaps',
        summary: 'Ember Tracking can look at what your department has and hasn’t trained recently, and draft sessions aimed at the gaps.',
        taskLabel: 'To get training suggestions',
        how: [
            'On **Training Records**, select **Suggest Training**.',
            'Your competency coverage is analysed and grouped, from never covered through to fully covered.',
            'Pick the competencies to aim at — or switch to **Custom** and describe the session you want instead.',
            'Select **Generate Suggestions**, then **Refine** any draft with a note on what to change.',
            'Select **Use This Training** to start a record pre-filled with the title, description, hours and competencies.'
        ],
        note: 'The gaps are read from your own training records over the last twelve months, so this is only as good as what’s been signed off on the Competencies tab. A department that logs attendance but skips competencies will be told it has trained nothing.',
        cta: {label: 'Go to Training Records', to: '/training'}
    },
    {
        id: 'ref-training-types',
        kind: 'guide',
        category: 'training',
        docType: 'reference',
        title: 'Training types',
        summary: 'The six types a session can be, and what each one changes downstream.',
        sections: [
            {
                title: 'Types',
                bullets: [
                    '**Practice** — the regular drill night. The only type that creates a practice night for the attendance percentages.',
                    '**Extra Practice** — additional drill time beyond the scheduled night.',
                    '**Course (Internal)** — run in-house. Counts as making the practice night when it lands on one.',
                    '**Course (External)** — run by somebody else, and attended by enrolment.',
                    '**Officer Meeting** — a rank’s obligation rather than a turnout.',
                    '**Social** — department events that aren’t training as such.'
                ]
            },
            {
                title: 'What the type decides',
                bullets: [
                    '**Discipline is required** for Practice, Extra Practice and both Course types — not for Officer Meeting or Social.',
                    '**Payroll** splits hours into practice and training. Practice, Extra Practice, Officer Meeting and Social count as practice.',
                    '**Performance reviews** grade turnout on Practice, Extra Practice and Social only — a course runs on enrolment and a meeting is an obligation, so marking somebody absent from either would measure the schedule rather than them.'
                ]
            }
        ],
        cta: {label: 'Go to Training Records', to: '/training'}
    },
    {
        id: 'ref-training-disciplines',
        kind: 'guide',
        category: 'training',
        docType: 'reference',
        title: 'Training disciplines',
        summary: 'The area a session belongs to, which is what breaks the statistics down by subject rather than just by date.',
        sections: [
            {
                title: 'Disciplines',
                bullets: [
                    '**Exterior** · **Interior** · **FR** · **Ropes**',
                    '**Wildland** · **AutoX** · **Hazmat** · **CISM**',
                    '**Officer** · **Driver/Operator**',
                    '**Other** — for a session that doesn’t fit the rest.'
                ]
            },
            {
                title: 'Where it shows up',
                bullets: [
                    'The **Disciplines** tab of the Training Records statistics.',
                    'A per-discipline column for every member on the Practice Attendance table.',
                    'A member’s own training tab, as a card per discipline.'
                ],
                footer: 'Discipline is a coarser cut than competencies, and the two answer different questions: discipline tells you a member has done ropes work lately, competencies tell you which ropes skills they’ve been signed off on.'
            }
        ],
        cta: {label: 'Go to Training Records', to: '/training'}
    },
    {
        id: 'ref-training-settings',
        kind: 'guide',
        category: 'training',
        docType: 'reference',
        title: 'Training record settings',
        summary: 'What **Settings** > **Training Records** controls: the times a new training record opens with.',
        sections: [
            {
                title: 'Default times',
                body: 'Most departments practise on the same evening for the same length of time every week, so a new record opens pre-filled with that window on whatever date you pick. Someone recording a session that ran long changes the times; everyone else leaves them alone.',
                footer: 'Set both ends or neither — a start with no end opens every new record needing a field filled in, which is the opposite of the point. Leave both blank and new records open with no times at all.'
            }
        ],
        cta: {label: 'Open Settings', to: '/settings/training'}
    },
    {
        id: 'about-qualification-matrix',
        kind: 'guide',
        category: 'qualifications',
        docType: 'explanation',
        title: 'About the qualification matrix',
        summary: 'The Qualification Matrix shows the qualifications recorded for each member: what is complete, what is in progress and what needs renewing. Open a cell to see its dates and supporting certificates, or update it when you have permission to edit members.',
        sections: [
            {
                title: 'Rows, columns and levels',
                bullets: [
                    'A **row** is a member holding a firefighter rank, from Recruit through Chief.',
                    'A **column** is one qualification, grouped under the level it belongs to.',
                    'A **level** is a block of columns — the progression through Exterior, Interior and Full Service, then the officer levels, then standing categories like Rescue Training, Wildland and Education.'
                ],
                footer: 'Each level also carries a summary column so you can review a member’s progress across the level.'
            },
            {
                title: 'Choosing VIERA or JIBC',
                body: 'The **Training provider** setting determines which qualification framework the matrix displays. Choose **VIERA** or **JIBC** to match the provider your department trains with; the matrix shows the levels and qualification columns for that provider.',
                footer: 'To check or change it, go to **Settings** > **Qualifications** > **Training provider**. If the setting is managed by a hub, the hub controls the selection for your department.'
            },
            {
                title: 'The level column works itself out',
                body: 'A level reads as complete when any one of three things is true:',
                bullets: [
                    'It was marked complete in its own right.',
                    'Every skill underneath it is complete.',
                    'The member holds a **higher** level — somebody signed off as Full Service reads as Interior and Exterior too.'
                ],
                footer: 'A completed level can reflect a higher qualification or a prior qualification credited by an officer. Open the individual cells when you need their own dates and supporting records.'
            },
            {
                title: 'How it relates to the Training Matrix',
                body: 'The two matrices show different parts of a member’s record:',
                bullets: [
                    'The **Qualification Matrix** shows recorded qualifications, their status, dates and certificates.',
                    'The **Training Matrix** is derived from training records — the competencies ticked off on sessions in a date range.'
                ],
                footer: 'Ticking competencies on a training record updates training coverage. Qualifications are recorded separately; completing a requirement configured to award a qualification can also update the member’s qualification record.'
            },
            {
                title: 'Viewing and editing',
                body: 'Changing qualifications and uploading certificates requires permission to edit members. Members can open cells in their own row to read their qualification details and view attached certificates.',
                footer: 'Filters, Options and access to other members’ cell details are available to users who can edit members and to Administrator-role users. The History tab requires permission to edit members.'
            }
        ],
        cta: {label: 'Open the qualification matrix', to: '/matrix/qualification'}
    },
    {
        id: 'guide-qualification-record',
        kind: 'guide',
        category: 'qualifications',
        docType: 'how-to',
        title: 'Recording a qualification for a member',
        summary: 'Record a qualification’s status and dates against the member, with notes and a certificate to support it. The fields vary by qualification.',
        taskLabel: 'To record a qualification',
        how: [
            'Open **Qualification Matrix** from the sidebar and find the member’s row. Expand the level heading if the qualification’s column is hidden.',
            'Select the qualification cell to open its details.',
            'Set the **Status** — Complete, or one of the stages on the way there.',
            'Check the dates that appear: **Completed Date** for something finished, **Target Date** for something needed, or a scheduled date for something booked. Replace any suggested date with the date on the member’s record.',
            'For a qualification with **Theory** and **Practical**, record the parts completed and check their dates. Add licence details or other fields where the qualification asks for them.',
            'Add relevant **Notes** and use **Upload File** for the certificate, then select **Save Skill**.'
        ],
        note: 'Most completion dates are recorded by month and year. If saving offers to credit a prior qualification or its prerequisites, review the proposed items before confirming. To record an entire level, use its summary cell rather than an individual skill cell.',
        cta: {label: 'Open the qualification matrix', to: '/matrix/qualification'}
    },
    {
        id: 'guide-qualification-expiry',
        kind: 'guide',
        category: 'qualifications',
        docType: 'how-to',
        title: 'Tracking a qualification that expires',
        summary: 'Record expiry dates for qualifications that need renewing, then use the matrix to find members coming due.',
        taskLabel: 'To track an expiry',
        how: [
            'Open the qualification’s cell and set its **Status** to Complete.',
            'Enter the **Expiry Date** where available. For First Aid or First Responder qualifications, also complete the **Licence #**, **Issue Date** and **Licence Type** fields.',
            'Select **Save Skill**.',
            'Use the matrix’s filter box to choose **Expiring within 12 months**. Review expired qualifications in the results too.',
            'When the member renews, reopen the cell, update the dates and attach the new certificate, then select **Save Skill**.'
        ],
        note: 'An expiry date creates a renewal task assigned to the member and due on that date. Updating the date updates the task; clearing it removes the task. Cells display the expiry month and flag approaching or expired qualifications. Hover a cell for the expiry details.',
        cta: {label: 'Open the qualification matrix', to: '/matrix/qualification'}
    },
    {
        id: 'guide-qualification-certificate',
        kind: 'guide',
        category: 'qualifications',
        docType: 'how-to',
        title: 'Attaching a certificate to a qualification',
        summary: 'Attach a PDF or photo directly to the qualification it supports, so the certificate is easy to find later.',
        taskLabel: 'To attach a certificate',
        how: [
            'Open the member’s qualification cell and select **Upload File** under **Files**, then choose a PDF or photo. You can also drag the file onto the qualification cell in the matrix.',
            'Wait for the upload confirmation. The file appears under **Files**, and a paperclip marks the cell in the matrix.',
            'If you also changed the status, dates or notes, select **Save Skill** to save those changes.',
            'To view the certificate later, reopen the cell and select the file.'
        ],
        note: 'Uploading a certificate saves the file immediately; it does not mark the qualification complete. To collect a member’s attached certificates, open their **Qualifications** tab and select **Download All Qualifications** to download a zip.',
        cta: {label: 'Open the qualification matrix', to: '/matrix/qualification'}
    },
    {
        id: 'guide-qualification-history',
        kind: 'guide',
        category: 'qualifications',
        docType: 'how-to',
        title: 'Reviewing a qualification’s history',
        summary: 'See who changed a qualification, when it changed and the details of each recorded update.',
        taskLabel: 'To review qualification changes',
        how: [
            'Open the member’s qualification cell in **Qualification Matrix**.',
            'Select **History** to see the changes recorded for that qualification.',
            'Select a change to expand its details and compare the previous and updated values.',
            'Return to **Details** to review the current status, dates and attached files.'
        ],
        note: 'History is available to users with permission to edit members. Some older changes may show **Not recorded** where the original value is no longer available.',
        cta: {label: 'Open the qualification matrix', to: '/matrix/qualification'}
    },
    {
        id: 'guide-qualification-find-gaps',
        kind: 'guide',
        category: 'qualifications',
        docType: 'how-to',
        title: 'Finding missing or upcoming qualifications',
        summary: 'Use the filters to focus on a qualification, a group of members or work still to complete.',
        taskLabel: 'To find qualification gaps',
        how: [
            'Open the matrix’s filter box and select the skill or level you want to review. Type part of its name to find it.',
            'Add **Incomplete** to find qualifications not marked Complete, or choose **Needed**, **Scheduled** or **In Progress** for a particular stage. Use **Expiring within 12 months** to review renewals.',
            'Add a **Station**, **Rank** or **Member** filter to narrow the people shown.',
            'Review the matching cells. Remove filters to return to the wider department view.'
        ],
        note: 'Filters and collapsed levels are remembered in your browser. Keep **Options** > **Show All Skills** on when looking for gaps so untouched qualifications remain visible. Level and skill filters narrow columns; station, rank and member filters narrow rows.',
        cta: {label: 'Open the qualification matrix', to: '/matrix/qualification'}
    },
    {
        id: 'guide-qualification-export',
        kind: 'guide',
        category: 'qualifications',
        docType: 'how-to',
        title: 'Exporting the matrix to a spreadsheet',
        summary: 'Download the department’s qualification records as an Excel workbook for a review or reporting package.',
        taskLabel: 'To export the matrix',
        how: [
            'Open the **Options** menu on the Qualification Matrix.',
            'Select **Export to Excel**.'
        ],
        note: 'The workbook includes **Qualification Matrix**, **By Member** and **Key** sheets, with status colours and recorded dates. It exports all unarchived firefighter members and the full qualification catalog, regardless of your current filters or collapsed levels.',
        cta: {label: 'Open the qualification matrix', to: '/matrix/qualification'}
    },
    {
        id: 'guide-qualification-custom',
        kind: 'guide',
        category: 'qualifications',
        docType: 'how-to',
        title: 'Adding additional qualifications',
        summary: 'The matrix is designed to cover your department’s qualifications. If you can’t find one you need, you can add it under **Additional qualifications** in Settings.',
        taskLabel: 'To add your own qualifications',
        how: [
            'Go to **Settings** > **Qualifications**.',
            'Under **Additional qualifications**, select **Add Category** and name it.',
            'Select **Add Qualification** within it for each one you want a column for.',
            'Save the settings, then return to **Qualification Matrix** to record member progress in the new columns.'
        ],
        note: 'A custom category has its own group of columns and summary. If the settings show **Managed by** a hub, that hub controls the shared categories and they cannot be edited here.',
        cta: {label: 'Open Settings', to: '/settings/qualifications'}
    },
    {
        id: 'ref-qualification-statuses',
        kind: 'guide',
        category: 'qualifications',
        docType: 'reference',
        title: 'Qualification statuses',
        summary: 'The five states a qualification can hold, and what each one puts in the cell.',
        sections: [
            {
                title: 'Statuses',
                bullets: [
                    '**Incomplete** — not held, and nothing planned. The cell is left empty.',
                    '**Needed** — identified as a gap. Shows the target year.',
                    '**Scheduled** — booked. Shows the month, or the middle of a date range.',
                    '**In Progress** — started but not finished.',
                    '**Complete** — held. Shows a tick, or the expiry month where there is one.'
                ]
            },
            {
                title: 'Cells that say more than a status',
                bullets: [
                    'A qualification split into theory and practical shows **T**, **P** or **T & P** until both are done.',
                    'The **Driver/Operator** column shows progress through the applicable check sheet items until the qualification is signed off. Open the cell for the branch breakdown and a link to the Driver/Operator requirement.',
                    'First Aid shows the licence type it was recorded under, and flags a licence still needing its scope update.',
                    'Fit testing shows the mask size.'
                ],
                footer: 'Hover a cell for its details, such as an expiry month, a scheduled date or the number of completed items. A paperclip indicates an attached file.'
            }
        ],
        cta: {label: 'Open the qualification matrix', to: '/matrix/qualification'}
    },
    {
        id: 'ref-qualification-settings',
        kind: 'guide',
        category: 'qualifications',
        docType: 'reference',
        title: 'Qualification settings',
        summary: 'What **Settings** > **Qualifications** changes about the matrix.',
        sections: [
            {
                title: 'Curriculum',
                bullets: [
                    '**Training provider** — JIBC or VIERA. This swaps the columns the matrix is built from, so pick the one your department actually trains against.',
                    '**JIBC records** — allows JIBC transcripts to be stored against members, and adds the JIBC number and exam password columns to the Options menu.'
                ]
            },
            {
                title: 'Additional qualifications',
                body: 'If you can’t find a qualification in the matrix, add it here and group it with related qualifications under a category.',
                footer: 'A hub may manage the training provider and additional categories for your department. Managed controls identify the hub and cannot be changed locally.'
            },
            {
                title: 'Matrix appearance',
                bullets: [
                    '**Level colors** — display accountability tag colours on the level columns.',
                    '**Accountability tag colors** — the colour used for each level, matching the tags your department already uses on scene.'
                ],
                footer: 'The export picks these colours up too, so a printed matrix reads the same way as the screen.'
            }
        ],
        cta: {label: 'Open Settings', to: '/settings/qualifications'}
    },
    {
        id: 'about-lesson-plans',
        kind: 'guide',
        category: 'lessonplans',
        docType: 'explanation',
        title: 'About lesson plans',
        summary: 'A lesson plan keeps the objectives, materials, safety concerns and teaching instructions for a session together. Reuse it whenever you teach the same lesson, so instructors have a consistent starting point.',
        sections: [
            {
                title: 'Prepare once, use across sessions',
                body: 'Describe the lesson, list what the crew needs, and break the activities into time blocks. Add the competencies the lesson covers. You can write a plan in Ember Tracking or import an existing Word or PDF document.',
                footer: 'Link the saved plan to each training record that uses it. The plan’s **Training Records** tab shows those sessions.'
            },
            {
                title: 'The plan and the session record',
                bullets: [
                    'The **lesson plan** describes what you intend to teach and the competencies it covers.',
                    'The **training record** records the session’s date, times, instructors, attendance and the competencies each member completed.',
                    'A linked plan supplies competency rows and their associated **JPRs**. Tick each member’s completion on the training record and save it to make that completion verifiable in the **Training Matrix**.'
                ],
                footer: 'Selecting a competency on a plan does not sign off any member or award a qualification.'
            },
            {
                title: 'One shared plan, or a separate version',
                body: 'Training records link to the saved plan, so edits to that plan are shared by the records using it. Use **Duplicate** when you want to adapt a lesson for one session or keep a separate version.'
            },
            {
                title: 'Access to lesson plans',
                body: 'Viewing plans requires access to Lesson Plans. Creating, importing, editing and duplicating require permission to edit lesson plans; deleting requires permission to delete them. Linking a plan also requires permission to edit the training record.'
            }
        ],
        cta: {label: 'Go to Lesson Plans', to: '/lessonplans'}
    },
    {
        id: 'guide-lesson-plan-create',
        kind: 'guide',
        category: 'lessonplans',
        docType: 'how-to',
        title: 'Creating a lesson plan',
        summary: 'Write a plan that another instructor can pick up and teach, with the activities and resources in one place.',
        taskLabel: 'To create a lesson plan',
        how: [
            'Open **Lesson Plans** from the sidebar and select **Add Lesson Plan**. If the library is empty, select **Create lesson plan**.',
            'Enter a clear **Name**. Use **Description / Materials** for the lesson overview and equipment or resources needed.',
            'Fill in **Lesson Objectives** and **Safety Concerns** so instructors know the intended outcomes and precautions.',
            'Under **Competencies**, choose the competencies the lesson covers or use **Guess Competencies** to suggest them from the plan.',
            'Use **Add time block** to lay out the session. Enter the time or duration and instructions for each activity. Drag blocks to reorder them, or use their duplicate and remove icons.',
            'Add any **Notes**, then select **Save**.'
        ],
        note: 'The plan’s time blocks describe the teaching schedule. Set the actual date, start and end times on each training record when you use the plan.',
        cta: {label: 'Add a lesson plan', to: '/lessonplans/add'}
    },
    {
        id: 'guide-lesson-plan-import',
        kind: 'guide',
        category: 'lessonplans',
        docType: 'how-to',
        title: 'Importing a lesson plan from Word or PDF',
        summary: 'Bring an existing lesson plan into Ember Tracking. Import uses AI to turn a Word document or PDF into an editable draft for you to review.',
        taskLabel: 'To import a lesson plan',
        how: [
            'Open **Lesson Plans** and select **Import**.',
            'Select **Choose file** and choose a Word **.docx** file or a **PDF**.',
            'Select **Import** and wait while the document is read.',
            'Review the draft’s name, description and materials, objectives, safety concerns, time blocks and notes against the original. Correct anything missing or misread.',
            'Choose the plan’s **Competencies**, or use **Guess Competencies** and review the suggestions.',
            'Select **Save** to add the plan to the library.'
        ],
        note: 'Importing prepares a draft; the lesson plan is created when you save it. You can also start **Import** from a training record’s empty **Lesson Plan** tab, then review and save the plan to link it to that session.',
        cta: {label: 'Go to Lesson Plans', to: '/lessonplans'}
    },
    {
        id: 'guide-lesson-plan-edit',
        kind: 'guide',
        category: 'lessonplans',
        docType: 'how-to',
        title: 'Viewing and editing a lesson plan',
        summary: 'Find a plan in the library, review its content and linked sessions, then update it when the shared lesson needs to change.',
        taskLabel: 'To review or edit a plan',
        how: [
            'Open **Lesson Plans**, search for the plan, and select its row.',
            'Read the plan on **Details**. Open **Training Records** to see which sessions use it; access to those records depends on your training permissions.',
            'To update the plan, select **Edit** and change the relevant fields or competencies.',
            'Select **Save** to apply the changes, or **Cancel** to leave the saved plan as it was.'
        ],
        note: 'Edits change the shared plan for every linked training record. If the change is only for one session, duplicate the plan first. From a training record’s **Lesson Plan** tab, **Edit** offers a choice to edit the shared plan or duplicate it.',
        cta: {label: 'Go to Lesson Plans', to: '/lessonplans'}
    },
    {
        id: 'guide-lesson-plan-duplicate',
        kind: 'guide',
        category: 'lessonplans',
        docType: 'how-to',
        title: 'Duplicating a lesson plan',
        summary: 'Start from a saved lesson when you need a variation, such as a different crew, location or set of activities.',
        taskLabel: 'To make a separate version',
        how: [
            'Open the saved lesson plan and select **Duplicate**.',
            'The copy opens with “(Copy)” added to its name. Select **Edit** and give it a name that identifies the variation.',
            'Update the instructions, time blocks and competencies, then select **Save**.',
            'Link the copy to the training records that should use this version.'
        ],
        note: '**Duplicate** creates a separate plan immediately. Existing sessions keep their original link. If you duplicate from a training record’s **Lesson Plan** tab, the copy is linked to that record and opens for editing.',
        cta: {label: 'Go to Lesson Plans', to: '/lessonplans'}
    },
    {
        id: 'guide-lesson-plan-competencies',
        kind: 'guide',
        category: 'lessonplans',
        docType: 'how-to',
        title: 'Adding competencies and viewing JPRs on a lesson plan',
        summary: 'Choose the competencies the lesson is designed to cover. Their linked Job Performance Requirements (JPRs) let instructors review the requirements behind the lesson.',
        taskLabel: 'To set the plan’s competencies',
        how: [
            'Create a plan, or open a saved plan and select **Edit**.',
            'Under **Competencies**, use **Add a competency** to search and select each competency the lesson covers.',
            'Remove any selections that do not belong in the lesson, then select **Save**.',
            'If the selected competencies have linked requirements, open **JPRs**. Expand a row or select **Expand All** to read the requirements grouped by standard and section.'
        ],
        note: 'Use a listed competency when it fits. You can enter a custom competency if needed, but custom and technical competencies may have no linked JPRs. The JPRs tab appears only when the saved plan has competencies with linked requirements.',
        sections: [
            {
                title: 'What carries into a training record',
                body: 'Linking the plan adds its competencies under **From Lesson Plan** on the training record. Those rows cannot be renamed or removed while inherited from the plan, but you can tick the members who completed them and add further competencies for that session.',
                footer: 'The plan holds a list of competencies, without member ticks. Adding attendees and recording their completion happens on the training record. Its **JPRs** tab shows the requirements linked to its competency rows.'
            }
        ],
        cta: {label: 'Go to Lesson Plans', to: '/lessonplans'}
    },
    {
        id: 'guide-lesson-plan-guess-competencies',
        kind: 'guide',
        category: 'lessonplans',
        docType: 'how-to',
        title: 'Guessing competencies from a lesson plan',
        summary: 'Guess Competencies uses AI to suggest competencies from the plan’s name, description, objectives, safety concerns, time blocks and notes.',
        taskLabel: 'To guess the plan’s competencies',
        how: [
            'Create a plan, or open one and select **Edit**. Describe the activities and objectives in enough detail to identify what will be taught.',
            'Under **Competencies**, select **Guess Competencies** and wait for the suggestions.',
            'Review the added competencies against the lesson. Remove unsuitable suggestions and choose any missing competencies manually.',
            'Select **Save**.'
        ],
        note: 'The button becomes available when the plan has a name or other content. Guessing adds new suggestions to the existing list. It does not record attendance or tick completion for members; do that on the linked training record after the session.',
        cta: {label: 'Go to Lesson Plans', to: '/lessonplans'}
    },
    {
        id: 'guide-lesson-plan-link-training',
        kind: 'guide',
        category: 'lessonplans',
        docType: 'how-to',
        title: 'Using a lesson plan on a training record',
        summary: 'Link a saved plan to a session so its instructions, competencies and associated JPRs are available alongside attendance and training details.',
        taskLabel: 'To use a plan for a session',
        how: [
            'Add or open a **Training Record** and go to **Lesson Plan**.',
            'Select **Select existing lesson plan**, search by name or description, choose the plan, and select **Use this plan**.',
            'On **Attendance**, add the people who attended under **Members**.',
            'Open **Competencies** and tick each member who completed the rows under **From Lesson Plan**. Use **Add Competency** for any additional activities covered in the session.',
            'Review the linked requirements on **JPRs** where available, then select **Save Training**.',
            'To verify a member’s completed competency, open **Training Matrix** with a date range that includes the session and select the member’s competency cell.'
        ],
        note: 'Linking does not tick member completion automatically. On an existing training record, choosing, changing or removing a plan link saves immediately; a new training record needs **Save Training**. Attendance and competency changes also need **Save Training**.',
        sections: [
            {
                title: 'Changing or removing the link',
                body: 'On the training record’s **Lesson Plan** tab, use **Change** on a plan to choose another in its place or **Remove** to unlink it. Removing the link keeps the plan in the library and keeps the training record. Review the competency rows and member ticks after changing the link.'
            },
            {
                title: 'Linking more than one plan',
                body: 'A session that covers several lessons can link each plan. Once a plan is linked, select **Add existing lesson plan**, **Create another** or **Import** beneath it. Every plan’s competencies appear under **From Lesson Plan**, and each plan with a performance checklist can be assessed on the **Checklist** tab, which offers a switch between them.'
            }
        ],
        cta: {label: 'Go to Training Records', to: '/training'}
    },
    {
        id: 'guide-lesson-plan-export',
        kind: 'guide',
        category: 'lessonplans',
        docType: 'how-to',
        title: 'Exporting a lesson plan as a PDF',
        summary: 'Download a copy to print or share with an instructor.',
        taskLabel: 'To export a plan',
        how: [
            'Open the lesson plan and save any edits.',
            'Select **Export PDF** to download the saved plan.',
            'For a copy with the linked session’s details, use **Export PDF** on that training record’s **Lesson Plan** tab.'
        ],
        cta: {label: 'Go to Lesson Plans', to: '/lessonplans'}
    },
    {
        id: 'guide-lesson-plan-delete',
        kind: 'guide',
        category: 'lessonplans',
        docType: 'how-to',
        title: 'Deleting a lesson plan',
        summary: 'Remove a plan you no longer need. A plan must have no linked training records before it can be deleted.',
        taskLabel: 'To delete an unused plan',
        how: [
            'Open the saved plan and check **Training Records** for sessions using it.',
            'If a session should use another plan, open that training record and use **Change** on **Lesson Plan**. Use **Remove** only when the session should no longer have a plan linked.',
            'Once no training records link to the plan, return to it. Finish or cancel any edits, select **Delete Lesson Plan**, and confirm **Delete**.'
        ],
        note: 'Deleting requires permission to delete lesson plans. Deletion is blocked while any training record still links to the plan. Keep a plan that is part of a session’s record; use **Duplicate** when you want a different version for future training.',
        cta: {label: 'Go to Lesson Plans', to: '/lessonplans'}
    },
    {
        id: 'about-requirements',
        kind: 'guide',
        category: 'requirements',
        docType: 'explanation',
        title: 'About requirements',
        summary: 'A requirement is something you need every member — or every driver, or everyone at Station 2 — to have done. You build it once as a series of steps, publish it, and hand it out; each member gets an assignment they work through in their own time.',
        sections: [
            {
                title: 'Built from steps',
                body: 'A requirement is an ordered set of steps, and the mix is what makes it worth doing online rather than in a room:',
                bullets: [
                    'Something to **read** — a PDF or an in-app page, optionally signed off at the end.',
                    'Something to **watch**, with a minimum viewing time if it matters.',
                    'Something to **answer** — a quiz with a passing score, graded the moment it’s submitted.',
                    'Something to **do elsewhere** — a link out, or a file the member has to upload back.'
                ]
            },
            {
                title: 'Draft, published, assigned',
                bullets: [
                    'A requirement starts as a **draft**, editable and invisible to members.',
                    '**Publishing** takes a numbered snapshot of it. You can’t assign anything until you have.',
                    'Editing a published requirement makes new draft changes, which don’t reach anyone until you publish again.'
                ],
                footer: 'Publishing again moves members who haven’t started onto the new version, and leaves anyone mid-way through on the one they began — nobody has the ground moved under them halfway.'
            },
            {
                title: 'Finishing it can credit a qualification',
                body: 'A requirement can be pointed at a cell in the qualification matrix. When a member completes it, that qualification is written for them — dated, with an expiry if the requirement has a validity window, and with any file they uploaded attached to it.',
                footer: 'That’s the join between the two: the requirement is the work, the matrix is the record. Nobody re-types the result into the matrix afterwards.'
            },
            {
                title: 'Where a requirement comes from',
                bullets: [
                    '**Local** — built by your department, and yours to edit.',
                    '**Shared** — published to you from a hub. You can assign it and track it, but not change it, so every department on it stays on the same version.'
                ]
            }
        ],
        cta: {label: 'Go to Requirements', to: '/requirements'}
    },
    {
        id: 'guide-requirement-build',
        kind: 'guide',
        category: 'requirements',
        docType: 'how-to',
        title: 'Building and publishing a requirement',
        summary: 'Write the steps, check it the way a member will see it, then publish the version they’ll get.',
        taskLabel: 'To build a requirement',
        how: [
            'Go to **Requirements** and add one.',
            'On **Details**, give it a name, a category, and a description of what it’s for.',
            'On **Steps**, add each step and fill it in — a document to read, a video, a quiz, a link, or a file to send back.',
            'Select **Save draft**, then **Preview** to run through it as a member would.',
            'When it reads right, select **Publish**.'
        ],
        note: 'A requirement needs at least one step before it can be published, and it can’t be assigned until it has been. Come back and edit whenever you like — the chip on the page tells you when there are unpublished changes sitting in front of the version members are actually getting.',
        cta: {label: 'Go to Requirements', to: '/requirements'}
    },
    {
        id: 'guide-requirement-assign',
        kind: 'guide',
        category: 'requirements',
        docType: 'how-to',
        title: 'Assigning a requirement to members',
        summary: 'Hand it to a named few, or to a whole rank or station — and decide whether people who join later get it too.',
        taskLabel: 'To assign a requirement',
        how: [
            'Open the published requirement and go to its **Assignments** tab.',
            'Start an assignment and choose who gets it: **Everyone**, named **Members**, **Ranks**, or **Stations**.',
            'For a rank or station, leave auto-assign on if you want future members who match to be given it automatically.',
            'Confirm — the button tells you how many members it’s about to go to.'
        ],
        note: 'Assigning by named members is always one-time: nobody is added later. Assigning by rank or station can leave a standing rule behind, so the next recruit at Station 2 picks it up on their first day without anyone remembering to do it. Members already holding a live assignment are skipped rather than assigned twice.',
        cta: {label: 'Go to Requirements', to: '/requirements'}
    },
    {
        id: 'guide-requirement-recurring',
        kind: 'guide',
        category: 'requirements',
        docType: 'how-to',
        title: 'Making a requirement come round again',
        summary: 'For the annual SOP re-acknowledgement, or anything else with a shelf life, set how long a completion is good for and let it reassign itself.',
        taskLabel: 'To make a requirement recur',
        how: [
            'Open the requirement and edit its **Details**.',
            'Set **Valid for (months)** — 12 for something re-done yearly.',
            'Tick **Re-assign automatically on a recurring schedule**.',
            'Save and publish.'
        ],
        note: 'A completion then carries an expiry date. Once it passes, the old assignment expires and a fresh one opens for the next round. Without the tick it simply expires and stays expired, which is the right shape for something that lapses but shouldn’t be re-issued automatically. Archived members are left out either way.',
        cta: {label: 'Go to Requirements', to: '/requirements'}
    },
    {
        id: 'guide-requirement-validate',
        kind: 'guide',
        category: 'requirements',
        docType: 'how-to',
        title: 'Validating a member’s completion',
        summary: 'Where a requirement is set to need checking, finished work waits for an officer rather than crediting itself.',
        taskLabel: 'To validate a completion',
        how: [
            'Open the requirement’s **Assignments** tab and find the rows awaiting validation.',
            'Open one to see what the member did, step by step, including anything they uploaded.',
            'Select **Approve** to credit it — or **Reject**, with a reason.'
        ],
        note: 'An approval is dated from when the member submitted, not from when you got to it, so a slow review never costs them time on a certificate. A rejection reopens the whole assignment: the reason is shown to the member, and their steps are cleared so they genuinely redo the work rather than press submit again.',
        cta: {label: 'Go to Requirements', to: '/requirements'}
    },
    {
        id: 'guide-requirement-credit',
        kind: 'guide',
        category: 'requirements',
        docType: 'how-to',
        title: 'Crediting someone who did it in person',
        summary: 'Not everything happens in the app. Record a completion on a member’s behalf and it counts exactly as if they’d worked through it.',
        taskLabel: 'To record a completion for a member',
        how: [
            'Open the requirement’s **Assignments** tab and start a **Record completion**.',
            'Pick the **Members** it applies to.',
            'Choose the **Method** — instructor-led and in person, or external, like an academy or a certificate.',
            'For an external completion, name the **Issuing body** and upload the certificate as evidence.',
            'Select **Record completion**.'
        ],
        note: 'This awards the linked qualification the same way a self-completion does, so a member who did the course at JIBC ends up in the matrix beside everyone who did it online.',
        cta: {label: 'Go to Requirements', to: '/requirements'}
    },
    {
        id: 'ref-requirement-steps',
        kind: 'guide',
        category: 'requirements',
        docType: 'reference',
        title: 'Requirement step types',
        summary: 'The six kinds of step a requirement can be built from, and what each asks of the member.',
        sections: [
            {
                title: 'Step types',
                bullets: [
                    '**Document** — a PDF or image to read. The member scrolls to the end before they can acknowledge it.',
                    '**Page** — a rich-text page written in the app, for anything that doesn’t need to be a document.',
                    '**Video** — uploaded or linked. Set a minimum viewing time, or let watching to the end count.',
                    '**Quiz** — multiple choice, single or multi answer, with a passing score and a retake limit.',
                    '**External link** — sends the member somewhere else to complete something.',
                    '**File upload** — asks the member to send a file back.'
                ]
            },
            {
                title: 'Signing a step',
                body: 'A Document, Page or Video step can require an acknowledgement. The member types their name to sign, and the signature is recorded with the date, the time and the exact document they were shown.',
                footer: 'That’s what makes this worth using for SOP sign-off: the record says who read what, and when — not just that a box was ticked.'
            },
            {
                title: 'How quizzes are marked',
                bullets: [
                    'Marking happens on the server the moment a quiz is submitted, so the answers are never in the browser to be read.',
                    'The passing score defaults to 80%.',
                    'A retake limit is the number of further attempts after the first. Leave it blank for unlimited.',
                    'Every attempt is kept, with the questions as they were at the time — so an old result still makes sense after the quiz is rewritten.'
                ]
            }
        ],
        cta: {label: 'Go to Requirements', to: '/requirements'}
    },
    {
        id: 'ref-requirement-permissions',
        kind: 'guide',
        category: 'requirements',
        docType: 'reference',
        title: 'Who can do what with requirements',
        summary: 'Requirements are split across five permissions, so building, handing out and signing off can sit with different people.',
        sections: [
            {
                title: 'Permissions',
                bullets: [
                    '**Read** — see the requirements list.',
                    '**Write** — create, edit and publish. Shared requirements from a hub stay read-only regardless.',
                    '**Assign** — hand a published requirement to members.',
                    '**Report** — see other members’ assignments and how far through they are.',
                    '**Approve** — validate a self-completion, or send it back.'
                ],
                footer: 'The Assignments tab on a requirement needs Report or Approve; it’s the officer’s view of everyone’s progress, which is why it isn’t bundled with Write.'
            },
            {
                title: 'What a member gets without any of them',
                body: 'All of it is officer-facing, and a firefighter holds none of it by default. They still get their own **My Assignments** page — that needs no permission at all, because an assignment is theirs.',
                footer: 'The exception is a station login. A shared apparatus account isn’t a person, so it has no assignments and no page.'
            }
        ],
        cta: {label: 'Go to Requirements', to: '/requirements'}
    },
    {
        id: 'about-assignments',
        kind: 'guide',
        category: 'assignments',
        docType: 'explanation',
        title: 'About assignments',
        summary: 'An assignment is one member’s copy of a requirement — their progress through it, their signatures, their quiz attempts. Every member has an Assignments page, whatever else they can or can’t see in Ember Tracking.',
        sections: [
            {
                title: 'What a member sees',
                bullets: [
                    '**Outstanding** — not started or part-done, sorted by what’s due first. The sidebar carries a count of these.',
                    '**Pending validation** — finished and waiting on an officer. Nothing left for the member to do.',
                    '**Complete** — done, showing the date it was finished or how long it stays valid.'
                ],
                footer: 'Progress is per step, and it’s kept as they go. An assignment can be picked up and put down across days or devices without losing anything.'
            },
            {
                title: 'Due dates and chasing',
                body: 'A requirement can carry a completion window, which sets each member’s due date when it’s assigned. From there the reminders look after themselves:',
                bullets: [
                    'A week out, the day before, and on the day.',
                    'Then at a week, a month and six months overdue — the same cadence tasks use.',
                    'Once it’s overdue, whoever assigned it hears about it too.'
                ],
                footer: 'Reminders arrive in the daily digest rather than as separate mail, so a member with three overdue items gets one email, not three.'
            },
            {
                title: 'What finishing does',
                body: 'Completing every required step finishes the assignment — unless the requirement asks for validation, in which case it goes to an officer first. Either way, if the requirement awards a qualification, completing it records that qualification against the member.',
                footer: 'So an assignment isn’t busywork filed away somewhere: it’s how a certificate gets into the matrix.'
            }
        ],
        cta: {label: 'Go to My Assignments', to: '/assignments'}
    },
    {
        id: 'guide-do-assignment',
        kind: 'guide',
        category: 'assignments',
        docType: 'how-to',
        title: 'Working through an assignment',
        summary: 'Open it, go through the steps in order, and finish. It remembers where you got to.',
        taskLabel: 'To complete an assignment',
        how: [
            'Go to **Assignments** and select **Start** — or **Continue**, if you’ve been here before.',
            'Work through each step. A document or video has to be read or watched before it will let you past.',
            'Where a step asks you to sign, type your name and use the **Sign & Continue** button.',
            'Answer any quiz and submit it — you’ll see your score straight away.',
            'On the last step, select **Finish**.'
        ],
        note: 'Nothing is lost if you stop halfway; every step is saved as you complete it. If the requirement needs checking by an officer, the final button reads **Submit for validation** instead, and the assignment sits with them until they’ve looked at it.',
        cta: {label: 'Go to My Assignments', to: '/assignments'}
    },
    {
        id: 'guide-assignment-returned',
        kind: 'guide',
        category: 'assignments',
        docType: 'how-to',
        title: 'Redoing an assignment that came back',
        summary: 'An officer can send a submission back with a reason. It reopens for you to do again.',
        taskLabel: 'To redo a returned assignment',
        how: [
            'Find the assignment under **Outstanding**, marked **Returned for changes**.',
            'Read the reason on the row.',
            'Open it and work through the steps again.',
            'Select **Re-submit for validation** at the end.'
        ],
        note: 'The steps are genuinely reopened, not just the submit button — being sent back means doing the work again, including any signature, so what the officer eventually approves is a fresh pass rather than a re-press of the same button.',
        cta: {label: 'Go to My Assignments', to: '/assignments'}
    },
    {
        id: 'ref-assignment-statuses',
        kind: 'guide',
        category: 'assignments',
        docType: 'reference',
        title: 'Assignment statuses',
        summary: 'The six states an assignment can be in, and who it’s waiting on.',
        sections: [
            {
                title: 'Statuses',
                bullets: [
                    '**Not started** — assigned, not opened yet. Waiting on the member.',
                    '**In progress** — part-way through. Waiting on the member.',
                    '**Pending validation** — finished and submitted. Waiting on an officer.',
                    '**Complete** — done, and any linked qualification has been credited.',
                    '**Expired** — its validity window has run out. A recurring requirement opens a fresh round; anything else just lapses.',
                    '**Cancelled** — withdrawn by an officer, and no longer anybody’s to do.'
                ]
            },
            {
                title: 'The one that isn’t a status',
                body: '**Returned for changes** is a rejected submission, which sits back in **In progress** with the officer’s reason attached rather than in a state of its own — because the work is the member’s again, exactly as it was before they submitted.'
            }
        ],
        cta: {label: 'Go to My Assignments', to: '/assignments'}
    },
    {
        id: 'about-reports',
        kind: 'guide',
        category: 'reports',
        docType: 'explanation',
        title: 'About reports',
        summary: 'Reports turn what your crews already record into the documents you hand to somebody else — an officer, a council, a regional board. Nothing is entered twice: pick a report, pick a period, and it’s built from the incidents, training and hours already in the system.',
        sections: [
            {
                title: 'Three kinds, by who’s asking',
                bullets: [
                    '**Operations** — internal situational awareness. What we responded to, how the fleet is used, who turns out.',
                    '**Governance & Budget** — defensible numbers for councils and regional boards.',
                    '**Regulatory** — formal fire reporting to the BC Office of the Fire Commissioner.'
                ]
            },
            {
                title: 'Two permissions, not one',
                body: 'Getting into Reports needs permission to read reports. Each individual report additionally needs permission for the data it’s built from — every incident-derived report needs to be able to read incidents, and so on.',
                footer: 'A report you can’t see the data for isn’t listed, and its link bounces you back to the catalogue. So handing someone the Reports permission doesn’t quietly hand them the underlying records.'
            },
            {
                title: 'Print is the output, not a screenshot',
                body: 'Printing a report doesn’t photograph the screen — the app’s furniture drops away and what lands on paper is a document: your department’s logo as letterhead, the reporting period, who prepared it, and the numbers.',
                footer: 'Long tables split across pages with their headings repeated, and the colours that carry meaning — incident-type swatches, on-target chips — are kept while decorative tints are dropped. It’s meant to be signed and filed.'
            }
        ],
        cta: {label: 'Go to Reports', to: '/reports'}
    },
    {
        id: 'guide-run-report',
        kind: 'guide',
        category: 'reports',
        docType: 'how-to',
        title: 'Running a report',
        summary: 'Pick the report, set the period, and narrow it to what you’re being asked about.',
        taskLabel: 'To run a report',
        how: [
            'Go to **Reports** and select the one that matches the question you’re answering.',
            'Choose the period — **Last 90 days**, **This year**, **Last year**, or a **Custom** range.',
            'If you run more than one station, narrow to one with the station picker.',
            'Adjust whatever else that report offers, such as which attendance counts or which benchmark to measure against.'
        ],
        note: 'Every card names its own question, so the catalogue reads as a list of things you might be asked rather than a list of features. Where a report shows a change against last period, it’s comparing with the equivalent period before the one you picked — a custom fortnight compares against the fortnight before it.',
        cta: {label: 'Go to Reports', to: '/reports'}
    },
    {
        id: 'guide-report-export',
        kind: 'guide',
        category: 'reports',
        docType: 'how-to',
        title: 'Exporting a report as PDF or CSV',
        summary: 'Take the report away as a document to file, or as numbers to work with.',
        taskLabel: 'To export a report',
        how: [
            'Run the report and set the period you want.',
            'Select the download action in the header.',
            'Choose **PDF** to open your browser’s print dialog — save to PDF or send it to a printer.',
            'Choose **CSV** for the same figures as a spreadsheet.'
        ],
        note: 'Where a report has counting rules, they’re written into the CSV as well as onto the page, so a file that gets emailed on still explains what it counted. If your PDF comes out missing the coloured chips, turn on **Background graphics** in the print dialog.',
        cta: {label: 'Go to Reports', to: '/reports'}
    },
    {
        id: 'guide-member-performance',
        kind: 'guide',
        category: 'reports',
        docType: 'how-to',
        title: 'Running a member performance review',
        summary: 'One printable page per member: what they attended, out of what was possible while they were actually on the roster.',
        taskLabel: 'To review one member',
        how: [
            'Open the **Member Performance Review** and set the period — usually last year.',
            'Choose the member from the picker.',
            'Decide whether standby counts as attendance, using the **Include standby** tick.',
            'Export it as a PDF for the review meeting.'
        ],
        note: 'Percentages are measured against what happened while the member was active, so a mid-year joiner or somebody on leave is judged on the part of the year they were there. The page states its own counting rules at the foot — in print and in the CSV too — so a signed review explains itself years later without anyone having to remember how it was produced.',
        cta: {label: 'Go to Reports', to: '/reports'}
    },
    {
        id: 'ref-report-catalog',
        kind: 'guide',
        category: 'reports',
        docType: 'reference',
        title: 'The reports available',
        summary: 'What each report answers, and what it needs you to be recording.',
        sections: [
            {
                title: 'Operations',
                bullets: [
                    '**Incident Summary** — what you responded to, and how call volume is trending.',
                    '**Apparatus Utilization** — how each apparatus is used, and who crews it.',
                    '**Member Activity** — who’s responding, including overnight, and who runs incidents.',
                    '**Member Performance Review** — per member: incidents, training and admin hours against what was possible while they were active.'
                ]
            },
            {
                title: 'Governance & Budget',
                bullets: [
                    '**Response Performance** — response times at the 90th percentile against the NFPA benchmark.',
                    '**Annual Performance Summary** — the full board packet. On the roadmap.'
                ]
            },
            {
                title: 'Regulatory — BC OFC',
                body: 'The **Fire Loss Return**, **Mutual-Aid** and **Emerging-Hazard (EV / battery)** reports are on the roadmap, and gated on capturing the fields they need first.'
            },
            {
                title: 'Why Response Performance says “partial data”',
                body: 'It reports four steps — turnout, travel, total response and effective-response-force assembly — but only total response can be worked out from a dispatch and an arrival time. The rest stay blank until the underlying times are being recorded:',
                bullets: [
                    'Turnout and travel need an **en-route time** on incidents.',
                    'ERF assembly needs **per-apparatus arrival times**.'
                ],
                footer: 'Each step says on the report which time it’s waiting for, so the gap doubles as a list of what to start capturing.'
            }
        ],
        cta: {label: 'Go to Reports', to: '/reports'}
    },
    {
        id: 'ref-report-counting-rules',
        kind: 'guide',
        category: 'reports',
        docType: 'reference',
        title: 'How reports count things',
        summary: 'The decisions behind the numbers — worth knowing before you defend one across a table.',
        sections: [
            {
                title: 'Only finished incidents count',
                body: 'Call-volume figures count **Approved & Locked** incidents only. A locked report is frozen, so the same period always produces the same number — a figure that shifted every time somebody edited a draft couldn’t be handed to a council.'
            },
            {
                title: 'Response times use the 90th percentile',
                bullets: [
                    'The headline is the time 90% of responses came in at or under — not the average, where a handful of slow calls can hide behind fast ones.',
                    'The median is shown beside it, so you can see the spread.',
                    '**NFPA 1710** (career) measures against 8 minutes, **1720** (volunteer) against 14, and the benchmark is meeting it on 90% of calls.'
                ]
            },
            {
                title: 'Performance reviews measure turnout, prorated',
                bullets: [
                    'Training counts practice, extra practice and social sessions — what a member chooses to turn out for. Courses and officer meetings are left out, because a course is attended by enrolment and a meeting is a rank’s obligation.',
                    'Sessions running at the same time count once: a member can only be at one of them.',
                    'Instructing a session counts as attending it.',
                    'Admin hours come from approved timesheets only, and — being a count of work done rather than a rate — are never scaled for time on leave.'
                ],
                footer: 'These numbers deliberately won’t match the member profile tabs, which dedupe practices by day and don’t prorate. The report states its own rules on its face so the two are never mistaken for the same measure.'
            }
        ],
        cta: {label: 'Go to Reports', to: '/reports'}
    },
    {
        id: 'about-deployments',
        kind: 'guide',
        category: 'deployments',
        docType: 'explanation',
        title: 'About deployments',
        summary: 'When your apparatus and crews go out to a wildfire or to a neighbouring department, the deployment record is what gets you paid for it. It holds the incident, which crews went out and when, and every day’s paperwork in the shape the reimbursement package needs.',
        sections: [
            {
                title: 'Rotations, not one long trip',
                body: 'A deployment can run for weeks; a crew can’t. So the record is organised as **rotations** — a crew, on an apparatus, over a window of dates, replaced by the next crew.',
                bullets: [
                    'The 14-day limit is a **rotation’s**, counted from that rotation’s own start date.',
                    'A deployment outlives it by handing over to a replacement crew.'
                ],
                footer: 'That’s why every day count, hour total and forecast anchors on the rotation rather than the deployment — a member’s time away is their rotation’s window, not the incident’s.'
            },
            {
                title: 'The day is the unit of record',
                body: 'Each day of a rotation holds what actually happened: the hours worked, whether it was a travel day, and the paperwork filed for it. Hours come only from the times the crew recorded — never from a planned shift pattern — because billed hours have to reflect the day that happened, and a 07:00–19:00 crew regularly works to 02:00.',
                footer: 'An end time at or before the start is read as an overnight shift, so a night crew’s hours land on the day they started rather than being split in two.'
            },
            {
                title: 'It ends up in three other places',
                bullets: [
                    'An apparatus that’s out shows as **currently deployed**, visible to anyone who can see apparatus — even without access to deployments.',
                    'Days away feed a member’s **performance review**, so nobody is marked down for missing practice while they were on a fire.',
                    'The **export** is the package itself: every file, foldered by day and crew, in one zip.'
                ]
            }
        ],
        cta: {label: 'Go to Deployments', to: '/deployments'}
    },
    {
        id: 'guide-deployment-create',
        kind: 'guide',
        category: 'deployments',
        docType: 'how-to',
        title: 'Starting a deployment',
        summary: 'Open the record when the call comes, so the crew has somewhere to file from day one.',
        taskLabel: 'To start a deployment',
        how: [
            'Go to **Deployments** and select **Add Deployment**.',
            'Enter the **Incident Number** and **Incident Name** you were given.',
            'Set the **Location** and the **Start Date**.',
            'Save. The deployment opens as **Active**.'
        ],
        note: 'Leave the demob date empty until you know it — it’s asked for when you complete the deployment, not when you open it. Get the record open early: the crew files DTRs and receipts against days, and a day with nowhere to file is a receipt in somebody’s truck.',
        cta: {label: 'Go to Deployments', to: '/deployments'}
    },
    {
        id: 'guide-deployment-rotations',
        kind: 'guide',
        category: 'deployments',
        docType: 'how-to',
        title: 'Adding apparatus and crew rotations',
        summary: 'Record which apparatus went out and who was on it — then the replacement crew that took over.',
        taskLabel: 'To set up rotations',
        how: [
            'Open the deployment and go to its **Apparatus** tab.',
            'Add the apparatus that deployed.',
            'Add a **rotation** to it: its date window, and the crew — a **Crew Leader**, plus structural protection or apparatus members.',
            'When a replacement crew takes over, add another rotation rather than editing the first.'
        ],
        note: 'Each rotation gets its own 14-day clock. If day 1 of a rotation was a travel day, the record tells you which day to plan the trip home for, and which day a replacement crew needs to leave to arrive in time — the two dates people most often work out on the back of an envelope.',
        cta: {label: 'Go to Deployments', to: '/deployments'}
    },
    {
        id: 'guide-deployment-day',
        kind: 'guide',
        category: 'deployments',
        docType: 'how-to',
        title: 'Recording a day and its paperwork',
        summary: 'Times, travel, and the documents that day needs — filed against the day itself so nothing has to be sorted out afterwards.',
        taskLabel: 'To record a day',
        how: [
            'Open the deployment’s **Days** tab and find the day.',
            'Enter the **start** and **end** times the crew actually worked, and any travel time.',
            'Tick it as a travel day if that’s what it was.',
            'Upload that day’s **DTR** and **ICS 214**, plus any receipts, and tag each receipt as fuel, lodging, food or other.'
        ],
        note: 'A day is short until it has both a DTR and a 214, and the tab keeps a count of how many days still need attention. Today, and a night shift still running, are left out of that count — the crew hasn’t had their chance to file yet. On a deployment where you’re an external resource the paperwork is marked optional instead, since the crew often can’t get a copy of something they didn’t author.',
        cta: {label: 'Go to Deployments', to: '/deployments'}
    },
    {
        id: 'guide-deployment-export',
        kind: 'guide',
        category: 'deployments',
        docType: 'how-to',
        title: 'Building the reimbursement package',
        summary: 'Everything filed against the deployment, foldered by day and crew, in one zip.',
        taskLabel: 'To export a deployment',
        how: [
            'Open the deployment and start its export.',
            'Leave it building — it runs on the server, so you can carry on working or close the tab.',
            'Download the zip when it’s ready. A link is emailed to you as well.'
        ],
        note: 'Inside, each day is a folder — with a folder per crew where more than one worked that day — holding that day’s DTR, ICS 214 and IAP, and Receipts and Other folders beside them. Anything uploaded but not yet classified lands under Unsorted, which makes it easy to spot what still needs tagging. If files change after an export is built, the app stops offering that zip rather than letting you send a stale package.',
        cta: {label: 'Go to Deployments', to: '/deployments'}
    },
    {
        id: 'guide-deployment-close',
        kind: 'guide',
        category: 'deployments',
        docType: 'how-to',
        title: 'Completing and locking a deployment',
        summary: 'Two steps, deliberately: closing out the operation, and finalising the record once the money is settled.',
        taskLabel: 'To close out a deployment',
        how: [
            'When the crews are home, set the deployment to **Complete** and enter the **Demob Date**.',
            'Carry on uploading whatever paperwork is still outstanding — completing doesn’t freeze the record.',
            'Once the reimbursement package is filed and settled, **Lock** it.'
        ],
        note: 'Completing releases the apparatus, which stop showing as deployed. Locking makes the record read-only and needs the approve permission — the same shape as signing off an incident. An officer can unlock it if a correction is needed, and a deployment can be set back to Active if the crews go out again.',
        cta: {label: 'Go to Deployments', to: '/deployments'}
    },
    {
        id: 'ref-deployment-paperwork',
        kind: 'guide',
        category: 'deployments',
        docType: 'reference',
        title: 'Deployment paperwork',
        summary: 'What gets filed, what’s required, and where each piece ends up in the export.',
        sections: [
            {
                title: 'File types',
                bullets: [
                    '**DTR** — the daily time report. Required for every day.',
                    '**ICS 214** — the unit log. Required for every day.',
                    '**Receipts** — tagged as fuel, lodging, food or other.',
                    '**IAP** — the incident action plan for the operational period. Handed down to the crew rather than written by them, so it’s never treated as required.',
                    '**Other** — anything else worth keeping with the day.'
                ]
            },
            {
                title: 'Crew roles',
                bullets: [
                    '**Crew Leader** — one per rotation.',
                    '**Structural Protection Crew Member**',
                    '**Apparatus Member**'
                ]
            },
            {
                title: 'In the exported zip',
                body: 'Each day is a folder named for its date, with a crew folder inside when more than one crew worked it. The DTR, ICS 214 and IAP sit at the root of the day; receipts and everything else go into **Receipts/**, **Other/** and **Unsorted/**.'
            }
        ],
        cta: {label: 'Go to Deployments', to: '/deployments'}
    },
    {
        id: 'ref-deployment-reference',
        kind: 'guide',
        category: 'deployments',
        docType: 'reference',
        title: 'The IAA reference library',
        summary: 'The **Reference** button on Deployments opens the provincial rules and rates, so nobody is working from a printout in a binder.',
        sections: [
            {
                title: 'Rules at a glance',
                bullets: [
                    'The maximum deployment length, and the R&R owed between deployments.',
                    'Billing deadlines, and how long you have to file a damage claim.',
                    'Standby hours, and the minimum compensable hours in a day.',
                    'How self-sufficient a crew has to arrive.'
                ],
                footer: 'Each one cites its section of the Inter-Agency Agreement, so a number you’re about to quote can be checked against the source in a click.'
            },
            {
                title: 'Also on the page',
                bullets: [
                    'Personnel **wage rates** and single-response apparatus rates.',
                    '**Minimum requirements** for engines, tenders, trucks, rescue vehicles and mobile comms or command units.',
                    'Mandatory **crew requirements** by tier, and personal PPE.',
                    'The **deployment process**, radio frequencies, and the local-government responsibility matrix.',
                    'The **SPU inventory**, the full IAA as a PDF, and a blank fillable ICS 214.'
                ]
            },
            {
                title: 'Per-apparatus material',
                body: 'An apparatus flagged as BCWS deployment-ready gets its own entry, holding an example DTR filled in for that rig and its IAA checklist — so a crew leader filling one in at 22:00 has a worked example rather than a blank form.'
            }
        ],
        cta: {label: 'Open the deployment reference', to: '/deployments/resources'}
    },
    {
        id: 'about-calendar',
        kind: 'guide',
        category: 'calendar',
        docType: 'explanation',
        title: 'About the calendar',
        summary: 'The calendar brings events, time off, duty shifts, training and calls together. Use Add to schedule an event, record time off or assign a duty shift.',
        sections: [
            {
                title: 'What appears on it',
                bullets: [
                    '**Training** — sessions, coloured by discipline.',
                    '**Incidents** — calls that came in, with how long they ran.',
                    '**Duty shifts** — who is on duty or standby.',
                    '**Time off** — vacation and other periods when a member is away.',
                    '**Events** — the calendar’s own entries, for anything that isn’t one of the above.',
                    '**Public education** — school visits, open houses and the like.',
                    '**Scheduled qualifications** — courses booked against members in the qualification matrix.'
                ],
                footer: 'Each layer can be switched off, and you only ever see the layers you have permission for — somebody who can’t read incidents doesn’t get them here by the back door.'
            },
            {
                title: 'Adding to the calendar',
                body: '**Add** opens a menu; choose an entry to open its side panel. Every member can record their own **Time off**; recording it for somebody else needs member-editing permission, and a shared station login can’t record any. **Event** is available with event-editing permission and **Duty shift** with duty-shift editing permission. A shift created here is the same record shown under Duty Shifts.'
            }
        ],
        cta: {label: 'Open the calendar', to: '/calendar'}
    },
    {
        id: 'guide-calendar-event',
        kind: 'guide',
        category: 'calendar',
        docType: 'how-to',
        title: 'Adding an event to the calendar',
        summary: 'For anything that isn’t a practice, a call or a shift, but still needs to be on the department’s calendar.',
        taskLabel: 'To add an event',
        how: [
            'Open the **Calendar**, select **Add**, then **Event**.',
            'Give it a title, and a description if it needs one.',
            'Set the date. Leave the time blank for an all-day event.',
            'Save.'
        ],
        note: 'Adding, editing and deleting events are three separate permissions, so a department can let officers put things on the calendar without letting them clear it.',
        cta: {label: 'Open the calendar', to: '/calendar'}
    },
    {
        id: 'guide-calendar-time-off',
        kind: 'guide', category: 'calendar', docType: 'how-to',
        title: 'Recording time off',
        summary: 'Record vacation or another absence so missed calls and practices do not count against attendance.',
        taskLabel: 'To record time off',
        how: [
            'Open **Calendar → Add → Time off**.',
            'Choose a reason and dates. Leave times blank for whole days; the end date is the last day away.',
            'Select **Save time off**. No approval is required.',
            'Select the entry on the calendar to edit or delete it.'
        ],
        note: 'Time off is always recorded for yourself, and no permission is needed. Only users with member-editing permission can record or manage entries for other members. A shared station login cannot record time off. Attendance completed during time off still counts. Time off does not create paid hours or a shift stipend. Duty-shift scheduling checks for overlapping time off.',
        cta: {label: 'Open the calendar', to: '/calendar'}
    },
    {
        id: 'guide-calendar-subscribe',
        kind: 'guide',
        category: 'calendar',
        docType: 'how-to',
        title: 'Subscribing to the calendar on your phone',
        summary: 'Put the department’s calendar into the one you already carry, instead of remembering to check the app.',
        taskLabel: 'To subscribe',
        how: [
            'Open the **Calendar** and start a subscription.',
            'Copy the link you’re given.',
            'Add it in your own calendar app as a subscribed calendar — on iPhone, Settings > Calendar > Accounts > Add Account > Other > Add Subscribed Calendar.'
        ],
        note: 'The link is yours alone and carries your own permissions, so it shows you what you’d see in the app and nothing more. It covers the last six months and the next twelve, and refreshes on your phone’s own schedule. Treat it as a password — anyone with the link sees your calendar until you revoke it and generate a new one.',
        cta: {label: 'Open the calendar', to: '/calendar'}
    },
    {
        id: 'about-duty-shifts',
        kind: 'guide',
        category: 'dutyshifts',
        docType: 'explanation',
        title: 'About duty shifts',
        summary: 'A duty shift records that named members were covering a window of time — on duty at the hall or on standby at home. It answers “who did we have on Saturday night”, and it’s what a per-shift stipend is paid from.',
        sections: [
            {
                title: 'Shift types',
                bullets: [
                    '**Duty** — on shift.',
                    '**Standby** — available and expected to respond.',
                    '**BCWS Initial Attack Crew Standby** — the wildland standby windows, which follow the provincial activation rather than your own roster.',
                    '**Other** — a named coverage assignment such as a marine watch. Record vacation under **Calendar → Add → Time off**.'
                ]
            },
            {
                title: 'Presets, because most shifts repeat',
                body: 'Rather than typing the same times every week, a shift can be booked from a named preset — a Day or a Night — which your department defines once in settings. A preset whose end time isn’t after its start crosses midnight, which is how a 20:00–08:00 night or a 24-hour block is expressed.',
                footer: 'You can always ignore the presets and set an explicit date range and times instead.'
            },
            {
                title: 'Nobody is booked twice',
                body: 'Two shifts overlap when they share any actual instant, so a Day ending at 20:00 and a Night starting at 20:00 don’t clash. Booking a member onto a window they’re already covering is refused, and the dates they’re already on are disabled in the picker.',
                footer: 'That matters beyond tidiness: where a department pays a stipend per shift, a double booking is a double payment.'
            }
        ],
        cta: {label: 'Go to Duty Shifts', to: '/dutyshifts'}
    },
    {
        id: 'guide-duty-shift-book',
        kind: 'guide',
        category: 'dutyshifts',
        docType: 'how-to',
        title: 'Booking a duty shift',
        summary: 'Pick the window, pick who’s covering it, and save.',
        taskLabel: 'To book a shift',
        how: [
            'Go to **Duty Shifts** and add one.',
            'Choose the **Type** — duty, standby or another coverage assignment.',
            'Pick a preset and the day it falls on, or switch to an explicit date range with start and end times.',
            'Add the **Members** covering it, and any **Notes**.',
            'Save.'
        ],
        note: 'If somebody is already booked on an overlapping shift you’ll be told who, and the save is refused until you change the times or the members.',
        cta: {label: 'Go to Duty Shifts', to: '/dutyshifts'}
    },
    {
        id: 'guide-duty-shift-presets',
        kind: 'guide',
        category: 'dutyshifts',
        docType: 'how-to',
        title: 'Setting up your shift presets',
        summary: 'Define the windows your department actually runs, so booking a shift is two clicks.',
        taskLabel: 'To set up presets',
        how: [
            'Go to **Settings** > **Duty Shifts**.',
            'Add a preset, name it the way your crews say it — Day, Night, Weekend.',
            'Set its **Start** and **End** times.',
            'Save.'
        ],
        note: 'An end time at or before the start means the window runs into the next day, so a Night of 20:00 to 08:00 works as you’d expect, and 18:00 to 18:00 is a full 24 hours. Without any presets configured, a sensible Day and Night pair is offered.',
        cta: {label: 'Open Settings', to: '/settings/dutyshifts'}
    },
    {
        id: 'about-preplans',
        kind: 'guide',
        category: 'preplans',
        docType: 'explanation',
        title: 'About preplans',
        summary: 'A preplan is what a crew needs to know about a building before they’re standing in front of it at three in the morning — how to get in, what’s inside that will hurt them, and where the water is.',
        sections: [
            {
                title: 'What one holds',
                bullets: [
                    '**Details** — the property, its address, occupancy and construction type, floors and size.',
                    '**Access & Security** — door and gate codes, where the key box is, and anything that will slow entry.',
                    '**Hazards & Response** — what’s stored there, what it will take, and the notes a crew should have read.',
                    '**Water Supply** — hydrants, standpipes, sprinkler systems and alternative sources.',
                    '**Documents** — floor plans and anything else worth having on a screen.',
                    '**Additional Info** — your own fields, for whatever the standard ones don’t cover.'
                ]
            },
            {
                title: 'Written by officers, read by everyone',
                body: 'Reading and writing preplans are separate permissions, and the split is deliberate: a firefighter role can read them without being able to change them, because a preplan is only worth having if a crew trusts what it says.'
            },
            {
                title: 'On the map',
                body: 'Preplans can be viewed as a map instead of a list, with each property marked and coloured by occupancy type — which is the faster way to answer “what have we got preplanned in that part of town”.'
            }
        ],
        cta: {label: 'Go to Preplans', to: '/preplans'}
    },
    {
        id: 'guide-preplan-create',
        kind: 'guide',
        category: 'preplans',
        docType: 'how-to',
        title: 'Writing a preplan',
        summary: 'Start with the address and build it up — a thin preplan that exists beats a thorough one that doesn’t.',
        taskLabel: 'To write a preplan',
        how: [
            'Go to **Preplans** and select **Add Preplan**.',
            'On **Details**, enter the property name and address, and set the occupancy and construction type.',
            'Fill in **Access & Security** — codes, key box, anything that delays entry.',
            'Record what’s hazardous and what the response needs under **Hazards & Response**.',
            'Add hydrants and other sources under **Water Supply**, and attach floor plans under **Documents**.'
        ],
        note: 'The address is what puts it on the map, so get that right even if the rest waits. Codes and key box locations sit behind the same read permission as the rest of the preplan — worth remembering when deciding who holds it.',
        cta: {label: 'Go to Preplans', to: '/preplans'}
    },
    {
        id: 'about-admin-hours',
        kind: 'guide',
        category: 'adminhours',
        docType: 'explanation',
        title: 'About admin hours',
        summary: 'Not all department work is a call, a practice or a shift. Admin hours are how the rest gets recorded — the afternoon spent taking a truck for its inspection, the evening writing a report — logged by the member who did it and approved by an officer before it reaches payroll.',
        sections: [
            {
                title: 'A timesheet, not a single entry',
                body: 'Hours are grouped into a timesheet: a set of dated lines, each with the time worked — a number of hours, or a start and end time on that day in 15-minute steps — and a note saying what the work was. It moves through four states:',
                bullets: [
                    '**Draft** — the member’s to edit.',
                    '**Waiting for approval** — submitted, and locked while it waits.',
                    '**Approved** — final, and countable by payroll.',
                    '**Returned for changes** — sent back with a reason, editable again.'
                ],
                footer: 'A member can withdraw a submitted sheet to keep editing, as long as nobody has acted on it yet.'
            },
            {
                title: 'One sheet can cover several members',
                body: 'Two people who took an apparatus for a commercial vehicle inspection share one timesheet rather than keeping two copies that drift apart. Each line counts for everyone on it — a three-hour line with two members is three admin hours each, not an hour and a half — so the sheet’s total is six.',
                footer: 'When someone arrived late or stayed on, open **Adjust per member** under that line and give them their own start and end time (or hours). Everyone else keeps the line’s time.'
            },
            {
                title: 'Approved is the only state that counts',
                body: 'A timesheet exists to become payable hours, and **approved** is the only status payroll looks at. A draft or a sheet waiting on an officer is worth nothing on a report, however carefully it was filled in.',
                footer: 'So the chasing has to finish before the period does — see [[ref-admin-hours-payroll]] for how the two line up.'
            },
            {
                title: 'Who sees it',
                body: 'The page is self-service: it shows a member their own sheets and nobody else’s. It appears in the sidebar for anyone who can submit hours, for payroll admins who log hours on somebody’s behalf, and — importantly — for a member who holds neither permission but has had a sheet created in their name, so what was logged for them is never something they can’t open.',
                footer: 'The badge counts the sheets still waiting on them: drafts, and anything returned for changes. Submitting your own hours is a permission of its own, deliberately separate from running payroll — a member logging an afternoon’s work needs nothing else.'
            }
        ],
        cta: {label: 'Go to Admin Hours', to: '/admin-hours'}
    },
    {
        id: 'guide-admin-hours-submit',
        kind: 'guide',
        category: 'adminhours',
        docType: 'how-to',
        title: 'Logging and submitting admin hours',
        summary: 'Add a line per day, say what the work was, and send it for approval.',
        taskLabel: 'To log admin hours',
        how: [
            'Go to **Admin Hours** and start a **New Timesheet**.',
            'Choose **Hours** or **Start/End Times** for the sheet, then add a line for each day: the date, the hours (or the start and end time), and a note saying what you worked on.',
            'Attach anything that supports it — an invoice, an inspection slip.',
            'Select **Save** as you go, and **Submit for Review** when the sheet is finished.'
        ],
        note: 'Every line needs a note. It’s what an approver is actually approving, and what a member reads back months later when asked about a line on a pay statement.',
        cta: {label: 'Go to Admin Hours', to: '/admin-hours'}
    },
    {
        id: 'guide-admin-hours-request',
        kind: 'guide',
        category: 'adminhours',
        docType: 'how-to',
        title: 'Asking members to submit their admin hours',
        summary: 'Rather than chasing people at period end, send a timesheet to everyone who owes you one.',
        taskLabel: 'To request admin hours',
        how: [
            'Open the payroll period and start an admin-hours request.',
            'Choose who it goes to.',
            'Add a message if there’s something they should know.',
            'Send.'
        ],
        note: 'Each recipient gets an email and a timesheet already scoped to that period, so there’s nothing for them to set up — they open it, add their lines and submit. Approved sheets are the only ones payroll counts, so the chasing has to finish before the period closes.',
        cta: {label: 'Go to Admin Hours', to: '/admin-hours'}
    },
    {
        id: 'guide-admin-hours-approve',
        kind: 'guide',
        category: 'adminhours',
        alsoIn: ['payroll'],
        docType: 'how-to',
        title: 'Approving a member’s admin hours',
        summary: 'Read what they logged, then approve it — which is the step that turns it into payable hours.',
        taskLabel: 'To approve a timesheet',
        how: [
            'Open the payroll period and go to its **Admin Hours** tab, or use the review queue on the Payroll page.',
            'Filter to **Pending** to see what’s waiting on you.',
            'Open a sheet to read its entries and anything attached.',
            'Select **Approve** — or **Reject** with a reason, which sends it back to the member to fix.'
        ],
        note: 'Approving flows the hours straight into any draft period covering those dates, so there’s nothing else to press. Once a period is submitted or approved, its Admin Hours tab goes read-only. For an approved report, create a correction report and approve the timesheet into it; otherwise reopen the report first.',
        cta: {label: 'Go to Payroll', to: '/payroll'}
    },
    {
        id: 'ref-admin-hours-payroll',
        kind: 'guide',
        category: 'adminhours',
        alsoIn: ['payroll'],
        docType: 'reference',
        title: 'How admin hours reach payroll',
        summary: 'The join between a member’s timesheet and a payroll report — worth understanding, because it’s where “I submitted those” and “they’re not on the report” usually meet.',
        sections: [
            {
                title: 'Entries land by date',
                body: 'Hours attach to a payroll period the same way incident and training hours do — by the date on the line, not by which period the sheet was created from. Two consequences follow:',
                bullets: [
                    'A sheet a member started themselves can feed several periods at once, if its lines span the boundary.',
                    'A sheet requested for a period still only contributes the lines actually dated inside it.'
                ]
            },
            {
                title: 'Approving is what moves them',
                body: 'Approving a timesheet refreshes the admin-hours portion of every **draft** period its dates touch, immediately. It deliberately doesn’t re-derive anything else — incident, training and shift hours stay as they were, so approving one sheet can’t quietly pull in source changes an admin hasn’t reviewed.',
                footer: 'Reopening an approved timesheet works the same way in reverse.'
            },
            {
                title: 'A locked period blocks the edit',
                body: 'Once a report is submitted or approved it stops accepting changes, and a timesheet whose dates fall inside it can’t be approved or reopened until something covers those days — the app names the report and stops you rather than letting the two drift apart. The member can still edit and submit it. For an approved report, create a correction report from it (**Show Differences**, or **Create Correction** in the timesheet’s review drawer): the timesheet can then be approved, and **Recalculate** on the correction picks its hours up. The Payroll list marks an approved report **Pending hours** while a timesheet like this is waiting.',
                bullets: [
                    'Reopen that report to draft, change the hours, and recalculate.',
                    'Or, if it has already been paid, create a **correction** for it.'
                ]
            },
            {
                title: 'The same day can’t be claimed twice',
                body: 'A member can’t have two timesheets covering the same date, across sheets as well as within one — which is what stops an afternoon being paid once on a self-submitted sheet and again on a requested one.'
            }
        ],
        cta: {label: 'Go to Payroll', to: '/payroll'}
    },
    {
        id: 'about-files',
        kind: 'guide',
        category: 'files',
        docType: 'explanation',
        title: 'About files',
        summary: 'The department’s document library — SOPs, guidelines, forms, anything a crew might need to find. Organised into categories, and into folders within them, so finding a document doesn’t depend on knowing who uploaded it.',
        sections: [
            {
                title: 'Categories, then folders',
                body: 'A **category** is the top-level division your department chooses — Standard Operating Procedures and Standard Operating Guidelines are offered to start with, and you can add whatever else fits. Inside a category, **folders** group documents further.',
                footer: 'Categories are configured in settings rather than created ad hoc, which keeps the top level of the library stable enough to learn.'
            },
            {
                title: 'Files can be attached to records, too',
                body: 'A document already in the library can be linked to a training record or a public-education event rather than uploaded again. The record shows the link, and the file stays in one place — so revising an SOP doesn’t leave old copies attached to last year’s records.'
            },
            {
                title: 'Reading and changing are separate',
                body: 'Reading files, adding them, and deleting them are three permissions. Most departments give the whole roster read access and keep uploads with officers — the library is only useful if crews can actually reach it.'
            }
        ],
        cta: {label: 'Go to Files', to: '/files'}
    },
    {
        id: 'guide-files-upload',
        kind: 'guide',
        category: 'files',
        docType: 'how-to',
        title: 'Adding documents to the library',
        summary: 'Drop them into the right category, and use folders once a category gets big.',
        taskLabel: 'To add a document',
        how: [
            'Go to **Files** and open the category it belongs in.',
            'Drag the documents in, or use the upload action. Several at once is fine.',
            'Add a folder first if the category is getting long, and upload into that.'
        ],
        note: 'If the category you want doesn’t exist, it’s created in **Settings** > **Files** rather than here — categories are meant to be a stable shelf, not something that grows a new one per upload.',
        cta: {label: 'Go to Files', to: '/files'}
    },
    {
        id: 'guide-files-categories',
        kind: 'guide',
        category: 'files',
        docType: 'how-to',
        title: 'Setting up file categories',
        summary: 'The shelves your library is organised onto.',
        taskLabel: 'To manage categories',
        how: [
            'Go to **Settings** > **Files**.',
            'Add a category and give it a name and an icon.',
            'Reorder or rename as the library grows.'
        ],
        note: 'Departments starting out are offered Standard Operating Procedures and Standard Operating Guidelines, which is enough for most. Add more only when there’s a real division — a category nobody can predict the contents of is worse than a folder inside one they can.',
        cta: {label: 'Open Settings', to: '/settings/files'}
    },
    {
        id: 'about-payroll',
        kind: 'guide',
        category: 'payroll',
        docType: 'explanation',
        title: 'About payroll reports',
        summary: 'A payroll report is a date range with every member’s hours rolled up into it — pulled from the calls, practices, shifts and admin work already recorded, rather than typed in again. It goes through review the same way an incident does, and comes out the far side as the numbers you hand to whoever pays people.',
        sections: [
            {
                title: 'Where the hours come from',
                bullets: [
                    '**Incidents** — including standby, tracked separately so it can be reported under its own heading.',
                    '**Training and practice** — with any per-member override applied.',
                    '**Public education**, where a department runs it.',
                    '**Duty shifts** — counted as shifts rather than hours, because the usual arrangement pays a stipend per shift.',
                    '**Admin hours** — from approved timesheets only.'
                ],
                footer: 'Duty-shift hours deliberately stay out of the hours total: they’re paid per shift, so adding them to an hourly figure would be counting the same work in two currencies.'
            },
            {
                title: 'Hours are stored, dollars are worked out',
                body: 'A report holds hours. Every dollar figure is computed when the report is rendered or exported, from the rate that was in effect on the **period’s start date**.',
                footer: 'That’s what stops a pay rise rewriting history. Raise the rate in January and January’s periods onward use it, while last year’s approved report still reprints the numbers that were actually invoiced.'
            },
            {
                title: 'Review, the same shape as everything else',
                bullets: [
                    '**Draft** — being built, and recalculable.',
                    '**Waiting for approval** — submitted, and frozen while it waits.',
                    '**Approved** — final.',
                    '**Rejected** — sent back with a reason, editable again.'
                ],
                footer: 'Submitting needs permission to write payroll; approving, rejecting and reopening need permission to approve it. Payroll is an explicit add-on that even officers don’t get by default — it’s the one area where being an officer and being the person who runs pay are different questions.'
            }
        ],
        cta: {label: 'Go to Payroll', to: '/payroll'}
    },
    {
        id: 'guide-payroll-create',
        kind: 'guide',
        category: 'payroll',
        docType: 'how-to',
        title: 'Creating a payroll report',
        summary: 'Pick the period — from your regular schedule, or a one-off range — and the hours roll up on their own.',
        taskLabel: 'To create a payroll report',
        how: [
            'Go to **Payroll** and select **Create Payroll Report**.',
            'Choose a **Scheduled period** to take the next slot in your regular cycle, or a **Custom range** for a one-off.',
            'Create it. Every member’s hours for that range are rolled up immediately.',
            'Open the report and switch between its views to check the numbers before submitting.'
        ],
        note: 'Set a cadence in **Settings** > **Payroll** and the scheduled option knows the next period without anyone counting fortnights — and can create each one automatically as the previous elapses. A draft warns you when its source hours have moved since it was built, so recalculating is a decision rather than a habit.',
        cta: {label: 'Go to Payroll', to: '/payroll'}
    },
    {
        id: 'guide-payroll-review',
        kind: 'guide',
        category: 'payroll',
        docType: 'how-to',
        title: 'Submitting and approving a payroll report',
        summary: 'The hand-off from whoever builds the report to whoever signs it off.',
        taskLabel: 'To review a payroll report',
        how: [
            'With the draft finished, submit it for approval — optionally naming who should look at it.',
            'The approver opens it and checks the figures.',
            'They **approve** it, which makes it final.',
            'Or they **reject** it with a reason, which sends it back as editable.'
        ],
        note: 'A report can’t be submitted with no line items, and once submitted it stops recalculating — the numbers an approver reads are the numbers that were submitted. An approver can reopen an approved report back to draft if something needs fixing, though if it has already been paid a correction is usually the better move.',
        cta: {label: 'Go to Payroll', to: '/payroll'}
    },
    {
        id: 'guide-payroll-correction',
        kind: 'guide',
        category: 'payroll',
        docType: 'how-to',
        title: 'Correcting a report that’s already been paid',
        summary: 'For hours that landed after approval: a second, smaller report carrying only the difference.',
        taskLabel: 'To correct an approved report',
        how: [
            'Find it on the Payroll list: an approved report with hours added after approval shows **Needs correction**, and one with incidents, training or timesheets still awaiting approval shows **Pending hours**. Both also appear in My Tasks.',
            'Open the approved report. If hours have moved since it was approved, it says so and offers a correction.',
            'Create the correction. It covers the same dates as the original and contains only the members whose hours changed.',
            'Review it, then submit and approve it like any other report. The list shows **Correction in progress** until it’s approved.'
        ],
        note: 'The list is re-checked every day for reports that ended in the last 120 days, and whenever a report is opened. A submitted timesheet dated inside an approved report can only be approved once a correction covers its days, so for one of those create the correction first, approve the timesheet, then **Recalculate** the correction. A correction carries **additions** only, since you can’t un-pay an hour with a second payment — if hours need to come down, reopen the original and re-approve it instead. Only one correction can be open at a time, and a correction can’t itself be corrected: a second fix is another correction of the same original, whose baseline already includes the first.',
        cta: {label: 'Go to Payroll', to: '/payroll'}
    },
    {
        id: 'ref-payroll-reports',
        kind: 'guide',
        category: 'payroll',
        docType: 'reference',
        title: 'Payroll report views',
        summary: 'One period, several ways of reading it. Switch views on an open report; the one it opens on is a setting.',
        sections: [
            {
                title: 'Views',
                bullets: [
                    '**Total Hours** — every member’s hours by category. The default.',
                    '**Duty Shifts** — shifts worked, for the per-shift stipend.',
                    '**Incident Attendance** · **Practice Attendance** · **Training Attendance** — who was at what.',
                    '**Duty Shift Attendance** and **Public Education Attendance**, where those are in use.',
                    '**RDCO Summary** and **RDCK Summary** — regional district forms, each behind its own setting.'
                ],
                footer: 'Set which one a period opens on in **Settings** > **Payroll**. A default pointing at a view this period doesn’t show falls back to Total Hours rather than opening something empty.'
            }
        ],
        cta: {label: 'Go to Payroll', to: '/payroll'}
    },
    {
        id: 'ref-payroll-rates',
        kind: 'guide',
        category: 'payroll',
        docType: 'reference',
        title: 'Payroll settings',
        summary: 'What **Settings** > **Payroll** holds — pay rates, stipends and how often periods repeat — plus the rule that decides which rate a report uses.',
        sections: [
            {
                title: 'Rates are dated',
                body: 'Rates are entered as a list, each with the date it takes effect. A report uses the entry in effect on its **period start date** — not whatever is configured today.',
                footer: 'A period from before your earliest entry falls back to the built-in defaults, so entering your first rate changes nothing retroactively.'
            },
            {
                title: 'What each entry can hold',
                bullets: [
                    '**Hourly rate** — required. An entry without one is ignored.',
                    '**Rank overrides** — where a rank is paid differently. A rank you leave out is paid the base rate.',
                    '**Duty shift stipend** — paid per shift worked, not per hour.',
                    '**Officer stipends** — per rank. Here, blank means no stipend for that rank rather than a fallback.'
                ]
            },
            {
                title: 'Cadence',
                bullets: [
                    '**Bi-weekly** or **quarterly**, from a start date that sets the phase.',
                    'Leave the cadence blank to build every report by hand.',
                    'Turn on automatic creation and the next report appears once the previous period has elapsed.'
                ],
                footer: 'The start date matters for bi-weekly: which fortnight you’re on can’t be derived, so it has to come from your real pay calendar.'
            }
        ],
        cta: {label: 'Open Settings', to: '/settings/payroll'}
    },
    {
        id: 'about-your-account',
        kind: 'guide',
        category: 'account',
        docType: 'explanation',
        title: 'About your profile',
        summary: 'Your profile is both a form and a record. Some of it you keep current yourself, some of it only an officer can set, and the rest is a read-only account of what you’ve attended, hold and owe.',
        sections: [
            {
                title: 'What you can change, and what you can’t',
                body: 'On your own profile you can edit almost everything — your name, date of birth, phone numbers, address, driver’s licence, emergency contacts, photo and preferences.',
                bullets: [
                    'Your **rank**, **station**, **employee ID**, **firefighter number** and full-time status are set by someone with permission to edit members.',
                    'Everything else saves straight from your profile.'
                ],
                footer: 'That split is deliberate: the things an officer sets are the ones other people’s records depend on — attendance percentages, the qualification matrix and payroll all read your rank and station.'
            },
            {
                title: 'The tabs beside your profile',
                bullets: [
                    '**Qualifications** — what you’re signed off on, and what’s expiring.',
                    '**Assignments** — requirements you’ve been given. See [[about-assignments]].',
                    '**Records** — your HR records. You see your own, minus anything marked confidential.',
                    '**Incidents**, **Training**, **Gear**, **Tasks** — your own activity and what you’re holding.'
                ],
                footer: 'The tabs you get depend on your permissions, even on your own profile. Someone without incidents access doesn’t get the incidents tab on their own record, which looks like a bug if you don’t know it’s the rule. What your department has switched on decides the rest.'
            },
            {
                title: 'Reaching it',
                body: 'Your profile is under your name at the bottom of the sidebar. Everything on it is yours — an officer opening the same tabs on your record sees the officer’s view, which can include things your own view filters out.'
            }
        ],
        cta: {label: 'Open your profile', to: '/profile'}
    },
    {
        id: 'guide-profile-details',
        kind: 'guide',
        category: 'account',
        docType: 'how-to',
        title: 'Keeping your details current',
        summary: 'The department calls the number on your profile. Keep it, your address and your emergency contacts right.',
        taskLabel: 'To update your details',
        how: [
            'Open your profile from your name at the bottom of the sidebar.',
            'Update whatever has changed — phone, address, driver’s licence, emergency contacts.',
            'Add or replace your photo if you want one.',
            'Save.'
        ],
        note: 'If your email address isn’t verified you’ll see a **Verify** action beside it — worth doing, since it’s where account mail goes. Your driver’s licence expiry is worth keeping accurate too: it raises a task for you as it approaches, the same way a qualification expiry does.',
        cta: {label: 'Open your profile', to: '/profile'}
    },
    {
        id: 'guide-profile-password',
        kind: 'guide',
        category: 'account',
        docType: 'how-to',
        title: 'Changing your password',
        summary: 'Set a new password from your own profile, without needing anyone to reset it for you.',
        taskLabel: 'To change your password',
        how: [
            'Open your profile.',
            'Start a password change.',
            'Enter the new password and confirm it.',
            'Save.'
        ],
        note: 'If you can’t sign in at all, use the forgotten-password link on the sign-in page instead — that route doesn’t need you to be logged in. A shared station login is a different thing entirely: its password is set by an administrator in **Settings** > **Login**, not here.',
        cta: {label: 'Open your profile', to: '/profile'}
    },
    {
        id: 'guide-profile-preferences',
        kind: 'guide',
        category: 'account',
        docType: 'how-to',
        title: 'Turning your email notifications off',
        summary: 'How much the app mails you, and how dates are written for you, are both your own settings.',
        taskLabel: 'To set your preferences',
        how: [
            'Open your profile.',
            'Untick **Receive email notifications** to stop the reminder and digest mail.',
            'Set the **Date Format** to whatever you read most easily — the field shows today’s date in the format as you change it.',
            'Save.'
        ],
        note: 'Turning notifications off is a master switch across reminders, digests and assignment mail, so nothing routine reaches you — but account and security mail, like a password reset, still does. Officers should think twice: it also silences the reminders about work assigned to you.',
        cta: {label: 'Open your profile', to: '/profile'}
    },
    {
        id: 'guide-pin-menu-items',
        kind: 'guide',
        category: 'account',
        alsoIn: ['getting-started'],
        docType: 'how-to',
        title: 'Pinning the pages you use most',
        summary: 'Ember Tracking covers a lot of ground, and most people live in three or four pages of it. Pin those and they sit at the top of the sidebar, above everything else.',
        taskLabel: 'To pin a page',
        how: [
            'In the sidebar, hover the item you want.',
            'Select the **pin** that appears on its right.',
            'It moves to the shortcuts list at the very top of the menu.',
            'Drag the pinned items to put them in the order you want.',
            'Select the pin again to unpin.'
        ],
        note: 'Dashboard, Apparatus, Incidents and Training Records are pinned to begin with — replace them with whatever you actually open. Your shortcuts are yours alone: they’re saved to your account rather than the browser, so they follow you to another computer without changing anybody else’s sidebar. A page you lose access to quietly drops off the list rather than sitting there broken. The pin itself is hidden on a phone-width screen, so set your shortcuts up on something bigger — the list still shows on your phone.',
        cta: {label: 'Go to the dashboard', to: '/'}
    },
    {
        id: 'about-settings',
        kind: 'guide',
        category: 'settings',
        docType: 'explanation',
        title: 'About settings',
        summary: 'Settings is organised by the thing being configured rather than by who configures it. Most of it belongs to a feature — fuel alerting sits under Fuel, shift presets under Duty Shifts — and is documented alongside that feature. What’s left is department-wide.',
        sections: [
            {
                title: 'The department-wide pages',
                bullets: [
                    '**General** — who you are: name, acronym, logo, and what appears on exported PDFs.',
                    '**Stations** — the stations that apparatus, gear and members are assigned to.',
                    '**Login** — who can register, and whether stations get a shared sign-in.',
                    '**Features** — which areas of the app your department uses at all.'
                ]
            },
            {
                title: 'The per-feature pages',
                body: 'Incidents, Checks, Fuel, Duty Shifts, Qualifications, Payroll, Files, Emails, Integrations and Incident Forms each configure one feature, and each is covered in that feature’s own section of this help.',
                footer: 'So if you’re looking for how to set something up, the feature’s section is the place to start — not this one.'
            },
            {
                title: 'Two pages that aren’t settings at all',
                bullets: [
                    '**Release Notes** — what shipped in each version of Ember Tracking.',
                    '**Legal** — the privacy policy and terms of use shown to your members.'
                ],
                footer: 'They live here because there’s nowhere better, not because there’s anything to configure.'
            },
            {
                title: 'Changing a setting is immediate',
                body: 'There’s no publish step: a saved setting applies from that moment. The exception is anything the server caches on a schedule, which the setting itself says so where it matters.'
            }
        ],
        cta: {label: 'Open Settings', to: '/settings'}
    },
    {
        id: 'guide-settings-identity',
        kind: 'guide',
        category: 'settings',
        docType: 'how-to',
        title: 'Setting up your department’s identity',
        summary: 'The name and logo the app wears, and the letterhead on everything it prints.',
        taskLabel: 'To set your department identity',
        how: [
            'Go to **Settings** > **General**.',
            'Upload your **Department logo**.',
            'Enter the **Department name** and an **Acronym** for where space is tight.',
            'Set the **Municipality or regional district** you serve.',
            'Add a **Subtitle** and **Footer text** if exported PDFs need them.'
        ],
        note: 'The logo isn’t only decoration — it’s the letterhead on every exported PDF, including payroll reports and the reports you hand to a council. The jurisdiction does real work too: it fills the EMCR Task Registration form.',
        cta: {label: 'Open Settings', to: '/settings/general'}
    },
    {
        id: 'guide-settings-login',
        kind: 'guide',
        category: 'settings',
        docType: 'how-to',
        title: 'Controlling who can sign in',
        summary: 'How members get accounts, and how a shared device at the hall signs in without being a person.',
        taskLabel: 'To set up sign-in',
        how: [
            'Go to **Settings** > **Login**.',
            'Decide whether someone can register without having been added as a user first.',
            'Limit registration to a single email domain if your department uses one.',
            'Turn on **Station sign-in** and set a shared **Station password** for devices at the hall.'
        ],
        note: 'A station login is a shared account with restricted permissions, not a person — it has no individual identity, so it gets no assignments, no admin hours and no calendar of its own. **Force re-login** revokes every active session at once, which is what to reach for after a permission change or if an account may have been compromised.',
        cta: {label: 'Open Settings', to: '/settings/login'}
    },
    {
        id: 'guide-settings-features',
        kind: 'guide',
        category: 'settings',
        docType: 'how-to',
        title: 'Turning features on and off',
        summary: 'Ember Tracking covers more than any one department runs. Switch off what you don’t use and it leaves the sidebar entirely.',
        taskLabel: 'To turn a feature on or off',
        how: [
            'Go to **Settings** > **Features**.',
            'Switch the feature on or off.',
            'Save.'
        ],
        note: 'Turning a feature off hides its pages rather than deleting anything — switch it back on and the records are as they were. Most features are on by default and cost nothing until a department starts using them, so the usual reason to turn one off is to keep the sidebar honest about what your crews actually do.',
        cta: {label: 'Open Settings', to: '/settings/features'}
    }
];

/* ============================== Rules engine ============================== */

const hasPerms = (reqs, perms) => !reqs || reqs.every(p => perms.has(p));

// Every module an entry files under. `category` is its home — the one the pill
// shows — and `alsoIn` cross-lists it elsewhere. "Adding apparatus" is a Getting
// Started step AND an Apparatus how-to; it's one document either way, so it's one
// entry rather than two ids sharing dismiss state.
// Diátaxis doc types, in the order someone needs them: understand the thing,
// then do something with it, then look details up. Entries without a docType
// (Getting Started, which reads as a tutorial and is allowed to mix) render
// ungrouped and first.
// `label` is what the reader sees on the row glyph, so this is the one definition —
// cards.js used to keep a second copy, and the two had already drifted ("About" here
// against "Explanation" on screen).
export const DOC_TYPES = [
    {id: 'explanation', label: 'Explanation'},
    {id: 'how-to', label: 'How to'},
    {id: 'reference', label: 'Reference'}
];

export const docTypeLabel = id => (DOC_TYPES.find(t => t.id === id) || {}).label;

// Order a category's entries the way someone needs them: understand the thing,
// then do something with it, then look details up. Entries without a docType keep
// their catalog position at the front, so an untyped section is unchanged.
// `sectionId` opts an entry into a per-section position via `orderIn` (below).
// Without it the order is doc type, then CATALOG order — which is the right
// default, but a cross-listed article carries its home section's position into
// every section it appears in, and that isn't always where it belongs.
export const orderByDocType = (entries, sectionId) => {
    const rank = e => (e.docType ? DOC_TYPES.findIndex(t => t.id === e.docType) + 1 : 0);
    const place = e => (sectionId && e.orderIn && e.orderIn[sectionId] != null ? e.orderIn[sectionId] : 0);
    return entries
        .map((entry, i) => ({entry, i}))
        .sort((a, b) => rank(a.entry) - rank(b.entry) || place(a.entry) - place(b.entry) || a.i - b.i)
        .map(x => x.entry);
};

export const entryCategories = entry => [entry.category, ...(entry.alsoIn || [])];
export const inCategory = (entry, id) => entryCategories(entry).includes(id);

/* ================================= Search ================================= */

// Everything a reader might plausibly type, flattened into one lowercase string:
// the whole article, not just its title and summary. Matching on the title alone
// meant the answer to "VIN" or "mutual aid" was in the library and unfindable —
// the words are in a step or a section body, which is exactly where an answer
// lives. Section labels are included for every category the entry files under, so
// a cross-listed article is findable by either section's name.
//
// `**` emphasis markers are stripped: the catalog writes UI labels as **Add User**,
// and a reader searching "click add user" shouldn't be defeated by the asterisks.
const articleTitles = new Map(CATALOG.map(entry => [entry.id, entry.title]));
export const articlePlainText = entry => [
    entry.title,
    entry.summary,
    entry.taskLabel,
    entry.note,
    ...(entry.how || []),
    ...(entry.sections || []).flatMap(section => [
        section.title,
        section.body,
        section.footer,
        section.note,
        ...(section.steps || []),
        ...(section.bullets || [])
    ]),
    ...entryCategories(entry).map(id => (MODULES[id] || {}).label)
].filter(Boolean).join(' ').replace(/\*\*/g, '')
    .replace(/\[\[([\w-]+)\]\]/g, (match, id) => articleTitles.get(id) || id);

// Catalog content is static for the life of the bundle, so a corpus is built once
// per entry. Keyed by id rather than by object identity because every reader hands
// out fresh spreads of an entry ({...e, active, cta}), which a WeakMap would miss.
const corpusCache = new Map();
export const entrySearchText = entry => {
    if (!corpusCache.has(entry.id)) corpusCache.set(entry.id, articlePlainText(entry).toLowerCase());
    return corpusCache.get(entry.id);
};

// A term matches where it STARTS a word, not anywhere inside one. Plain `includes`
// is unusable once the whole article is searchable: "VIN" — the example question
// this search exists to answer — came back with eleven articles, every one of them
// a hit inside "driving" or "serving". Prefix matching still grows plurals and
// tenses ("check" finds checks, checking), which is the behaviour a reader expects.
const termPattern = term => {
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // \b before a term starting with punctuation would anchor against the wrong
    // edge, so only word-ish terms get the boundary.
    return new RegExp(/^\w/.test(term) ? `\\b${escaped}` : escaped);
};

// Every whitespace-separated term has to appear somewhere in the article, in any
// order — so "gear check" and "check gear" find the same thing, and adding a word
// narrows the result rather than starting over.
export const matchesQuery = (entry, query) => {
    const terms = String(query || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return true;
    const corpus = entrySearchText(entry);
    return terms.every(term => termPattern(term).test(corpus));
};

// Is a tip currently snoozed? `snoozed[id]` holds the epoch-ms the snooze expires.
export const isSnoozed = (id, state, now = Date.now()) => {
    const until = state.snoozed && state.snoozed[id];
    return Boolean(until) && now < until;
};

// Is this catalog entry's predicate currently true for the tenant + admin?
export const isActive = (entry, signals, perms) => {
    if (!entry.signal) return false;
    if (!hasPerms(entry.signal.requires, perms)) return false;
    return Boolean(entry.signal.when(signals));
};

// Routes that only exist while their feature flag is on. Keyed by route rather
// than by category so a guide that points somewhere always reachable (a Settings
// page, say) keeps its own CTA even when its module's flag is off.
const FLAGGED_ROUTES = {
    '/fuel': 'fuel',
    '/matrix/qualification': 'qualificationMatrix',
    '/lessonplans': 'lessonPlans',
    '/lessonplans/add': 'lessonPlans',
    '/requirements': 'requirements',
    '/assignments': 'requirements',
    '/deployments': 'deployments',
    '/deployments/resources': 'deployments',
    '/calendar': 'calendar',
    '/dutyshifts': 'dutyShifts',
    '/settings/dutyshifts': 'dutyShifts',
    '/preplans': 'preplans',
    '/files': 'files',
    '/settings/files': 'files',
    '/payroll': 'payroll',
    '/settings/payroll': 'payroll',
    '/settings/fuel': 'fuel',
    '/ohs': 'ohs',
    '/settings/ohs': 'ohs'
};

// Resolve an entry's CTA against current signals. A department with the feature
// switched off gets pointed at the switch rather than at a route the router never
// registered.
export const resolveCta = (entry, signals) => {
    const flag = entry.cta && FLAGGED_ROUTES[entry.cta.to];
    if (flag && signals && signals.flags && !signals.flags[flag]) {
        return {label: 'Enable in Settings', to: '/settings/features'};
    }
    return entry.cta;
};

// `hidden: true` holds a written entry back without deleting it — the section-level
// equivalent is VISIBLE_MODULES in pages/Help.js. Applied at all three readers below,
// so a held-back entry can't reach a reader by some other route.
const isPublished = entry => !entry.hidden;

// Ranked dynamic suggestions for the "For you" view (dismissed/snoozed filtered out).
export const getSuggestions = (signals, perms, state, now = Date.now()) =>
    CATALOG
        .filter(isPublished)
        .filter(e => e.kind === 'tip' && isActive(e, signals, perms))
        .filter(e => !(state.dismissed && state.dismissed[e.id]) && !isSnoozed(e.id, state, now))
        .sort((a, b) => b.signal.priority - a.signal.priority)
        .map(e => ({...e, cta: resolveCta(e, signals)}));

// Announcements the admin can act on, still relevant (per `when`), and not dismissed.
export const getAnnouncements = (signals, perms, state) =>
    CATALOG
        .filter(isPublished)
        .filter(e =>
            e.kind === 'announcement' &&
            (!e.when || Boolean(e.when(signals))) &&
            hasPerms(e.cta && e.cta.permission ? [e.cta.permission] : [], perms) &&
            !(state.dismissed && state.dismissed[e.id]))
        .map(e => ({...e, cta: resolveCta(e, signals)}));

// The full browsable library (tips + guides, never announcements). Each tip is
// tagged active so the UI can mark currently-suggested rows.
export const getLibrary = (signals, perms) =>
    CATALOG
        .filter(isPublished)
        .filter(e => e.kind !== 'announcement')
        .map(e => ({...e, active: e.kind === 'tip' && isActive(e, signals, perms), cta: resolveCta(e, signals)}));

export const getPublishedLibrary = (signals, perms) => getLibrary(signals, perms)
    .filter(entry => VISIBLE_MODULES.some(id => inCategory(entry, id)));
